import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Sidebar from './Sidebar';
import Header from './Header';
import ScriptDrawer from './ScriptDrawer';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from './ui/dialog';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu.jsx';
import CustomDropdown from './ui/CustomDropdown';
import DarkDatePicker from './ui/DarkDatePicker';
import ViewToggle from './kanban/ViewToggle';
import KanbanBoard from './kanban/KanbanBoard';
import KanbanVideoModal from './kanban/KanbanVideoModal';
import DarkTooltip from './ui/DarkTooltip';
import FlagIcon from './voice/FlagIcon';
import { getCountryCode, getLanguageName } from './niche-finder/utils';
import PageHeader from './ui/PageHeader';
import FilterPills from './ui/FilterPills';
import ChannelPromptsModal from './ChannelPromptsModal';
import ChannelReferencesModal from './ChannelReferencesModal';
import ChannelNarratorModal from './ChannelNarratorModal';
import ConfirmDialog from './ui/ConfirmDialog';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';
import { usePrivacy } from '../contexts/PrivacyContext';
import { useSidebar } from '../contexts/SidebarContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { 
  Youtube, 
  Search, 
  Plus, 
  Globe,
  Mail,
  Clock,
  BarChart3,
  ExternalLink,
  Trash2,
  Edit3,
  FileText,
  Save,
  X,
  CheckCircle,
  Calendar,
  Settings,
  MoreVertical,
  Clapperboard,
  PlayCircle,
  Package,
  CalendarCheck,
  CheckSquare,
  Link,
  ArrowUpDown,
  ArrowLeft,
  Video,
  Tv2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  Info,
  Bookmark,
  ImageIcon,
  Download,
  Upload,
  RefreshCw,
  ZoomIn,
  StickyNote,
  Users,
  Eye,
  Archive,
  ChevronDown,
  AlertTriangle,
  Mic,
  ArrowRight
} from 'lucide-react';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ═══ CACHE DE NAVEGAÇÃO (stale-while-revalidate) ═══
// Escopo de módulo: sobrevive a trocas de rota dentro do SPA (zera no F5).
// Ao voltar para /canais, a grade renderiza INSTANTANEAMENTE com os dados da
// última visita e o fetchCanais() roda em segundo plano só para atualizar.
let _canaisNavCache = null; // array de canais da última carga

// Vídeos por canal/filtro/ordenação — kanban/lista abrem sem skeleton ao voltar.
// Entrada: { videos, offset, filteredTotal, counts }. Limitado a 12 chaves (LRU simples).
const _videosNavCache = new Map();
// Dedup de requests em voo: o StrictMode (dev) monta os effects 2× e disparava
// dois GET /videos/canal idênticos na abertura da tela. Chamadas simultâneas
// com a mesma chave compartilham a MESMA promise (padrão do useNicheFinderTeaser).
const _videosInFlight = new Map(); // requestKey -> promise do axios.get

// Idioma em texto livre do canal → ISO para a bandeira (fallback quando o
// canal ainda não tem language_detected gravado pelo vínculo)
const IDIOMA_TEXTO_ISO = {
  'português': 'pt-BR', 'portugues': 'pt-BR', 'português (brasil)': 'pt-BR', 'pt-br': 'pt-BR', 'pt': 'pt-BR',
  'inglês': 'en', 'ingles': 'en', 'english': 'en', 'en': 'en',
  'espanhol': 'es', 'español': 'es', 'spanish': 'es', 'es': 'es',
  'alemão': 'de', 'alemao': 'de', 'german': 'de', 'de': 'de',
  'francês': 'fr', 'frances': 'fr', 'french': 'fr', 'fr': 'fr',
  'italiano': 'it', 'it': 'it',
  'japonês': 'ja', 'japones': 'ja', 'ja': 'ja',
  'coreano': 'ko', 'ko': 'ko',
  'russo': 'ru', 'ru': 'ru',
  'hindi': 'hi', 'hi': 'hi',
  'árabe': 'ar', 'arabe': 'ar', 'ar': 'ar',
  'turco': 'tr', 'tr': 'tr',
  'polonês': 'pl', 'polones': 'pl', 'pl': 'pl',
  'holandês': 'nl', 'holandes': 'nl', 'nl': 'nl',
  'chinês': 'zh', 'chines': 'zh', 'zh': 'zh',
  'indonésio': 'id', 'indonesio': 'id',
};
const getCanalFlagIso = (canal) => {
  if (canal?.language_detected) return canal.language_detected;
  const idioma = (canal?.idioma || '').trim().toLowerCase();
  return IDIOMA_TEXTO_ISO[idioma] || null;
};
const _videosCacheSet = (key, entry) => {
  _videosNavCache.delete(key);
  _videosNavCache.set(key, entry);
  if (_videosNavCache.size > 12) {
    _videosNavCache.delete(_videosNavCache.keys().next().value);
  }
};

const CanalManagerNew = ({ onLogout }) => {
  usePageTitle('Canais');
  const { t } = useTranslation();
  const [canais, setCanais] = useState(() => _canaisNavCache || []);
  const [videos, setVideos] = useState([]);
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = último ao primeiro (padrão), 'asc' = primeiro ao último
  // Inicializar sem canal selecionado - será definido após carregar os canais
  const [selectedCanal, setSelectedCanal] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [channelSearchTerm, setChannelSearchTerm] = useState('');
  const [channelPromptCount, setChannelPromptCount] = useState(0);
  const [channelReferences, setChannelReferences] = useState([]);
  const [showPromptsModal, setShowPromptsModal] = useState(false);
  const [showReferencesModal, setShowReferencesModal] = useState(false);
  const [showNarratorModal, setShowNarratorModal] = useState(false);
  const [narratorInfo, setNarratorInfo] = useState(null);
  // Com cache de navegação quente, não mostra spinner — revalida em silêncio.
  const [loading, setLoading] = useState(() => !_canaisNavCache);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [videosOffset, setVideosOffset] = useState(0);
  const [videosFilteredTotal, setVideosFilteredTotal] = useState(0);
  const [videosCounts, setVideosCounts] = useState({ total: 0, planejamento: 0, producao: 0, finalizado: 0, postado: 0 });
  const VIDEOS_PAGE_SIZE = 50;
  const [showNewCanalDialog, setShowNewCanalDialog] = useState(false);
  const [showNewVideoDialog, setShowNewVideoDialog] = useState(false);
  const [showCustomButtonsDialog, setShowCustomButtonsDialog] = useState(false);
  const [editingFields, setEditingFields] = useState({});
  const [tempValues, setTempValues] = useState({});
  const [nextVideoInfo, setNextVideoInfo] = useState({});
  const [editingNotas, setEditingNotas] = useState({}); // Track which video's notas is being edited
  const [tempNotas, setTempNotas] = useState({}); // Store temporary notas values
  
  // Estados para formatação de texto
  const [textFormatStates, setTextFormatStates] = useState({}); // { 'videoId-titulo': 0, 'videoId-thumbnail': 1 }
  const [originalTexts, setOriginalTexts] = useState({}); // { 'videoId-titulo': 'Original text' }

  // Thumbnail upload + preview state
  const [uploadingThumbnail, setUploadingThumbnail] = useState({}); // { videoId: true }
  const [thumbnailPreview, setThumbnailPreview] = useState(null); // URL for lightbox preview
  const [thumbnailDeleteConfirm, setThumbnailDeleteConfirm] = useState(null); // videoId pending delete confirmation
  const thumbnailInputRef = useRef(null);
  const [thumbnailUploadVideoId, setThumbnailUploadVideoId] = useState(null);
  
  const [customButtons, setCustomButtons] = useState([
    { name: '', url: '', id: 1 },
    { name: '', url: '', id: 2 },
    { name: '', url: '', id: 3 },
    { name: '', url: '', id: 4 },
    { name: '', url: '', id: 5 },
    { name: '', url: '', id: 6 }
  ]);
  
  const [newCanal, setNewCanal] = useState({
    nome: '',
    idioma: 'Português',
    email: '',
    horario_postagem: ['19:00'],
    frequencia_postagem: '',
    videos_ja_postados: 0,
    anotacoes: '',
    custom_buttons: []
  });
  const [editingCanal, setEditingCanal] = useState(null);
  const [showEditCanalDialog, setShowEditCanalDialog] = useState(false);
  const [newVideo, setNewVideo] = useState({
    numero_video: '',
    titulo: '',
    thumbnail: '',
    notas: '',
    data_postagem: ''
  });

  const [scriptDrawerOpen, setScriptDrawerOpen] = useState(false);
  const [scriptDrawerVideo, setScriptDrawerVideo] = useState(null);

  // Extras/Anotações modal
  const [showExtrasModal, setShowExtrasModal] = useState(false);
  const [extrasText, setExtrasText] = useState('');
  const [savingExtras, setSavingExtras] = useState(false);

  const [refreshingStats, setRefreshingStats] = useState(false);
  const [showArchivedSection, setShowArchivedSection] = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  // Carregar filtro de vídeo do localStorage ou usar 'todos'
  const [videoFilter, setVideoFilter] = useState(() => {
    return localStorage.getItem('videoFilter') || 'todos';
  }); // 'todos', 'planejamento', 'producao', 'finalizado', 'postados'

  // View mode: 'list' or 'kanban'
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('videoViewMode') || 'list';
  });
  const [mobileTab, setMobileTab] = useState('planejamento');
  const [kanbanModalVideo, setKanbanModalVideo] = useState(null);

  // Derive fresh video data from videos array so modal stays in sync after checklist changes
  const currentModalVideo = kanbanModalVideo
    ? videos.find(v => v.id === kanbanModalVideo.id) || kanbanModalVideo
    : null;

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem('videoViewMode', mode);
  };

  // Estados para modal de link
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkModalData, setLinkModalData] = useState({ videoId: null, field: null, currentLink: '' });
  const [linkInputValue, setLinkInputValue] = useState('');

  // Dialog "Vincular ao YouTube" (vídeo publicado → card; 2026-06)
  // result = resposta do POST (titulo_youtube + aviso_canal_divergente)
  const [ytLinkModal, setYtLinkModal] = useState(null); // { videoId, url, loading, error, result, updateTitle }

  // YouTube Import states
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [youtubeSearching, setYoutubeSearching] = useState(false);
  const [youtubeChannel, setYoutubeChannel] = useState(null);
  const [youtubeImporting, setYoutubeImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0, videoName: '' });
  // (showApiKeyWarning removido — Fase 4 da unificação de chaves YouTube)
  const [showImportResult, setShowImportResult] = useState(null);
  const [showLinkYoutubeDialog, setShowLinkYoutubeDialog] = useState(false);
  const [youtubeUrlLink, setYoutubeUrlLink] = useState('');
  const [youtubeSearchingLink, setYoutubeSearchingLink] = useState(false);
  const [youtubeChannelLink, setYoutubeChannelLink] = useState(null);
  const [youtubeImportingLink, setYoutubeImportingLink] = useState(false);
  const [showDeleteImportedModal, setShowDeleteImportedModal] = useState(null);
  const [channelThumbErrors, setChannelThumbErrors] = useState({});

  // Canal sharing states
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [sharePermissions, setSharePermissions] = useState({
    pode_deletar_videos: false,
    pode_editar_config: false,
    pode_editar_referencias: false,
    pode_editar_prompts: false
  });
  const [shareMembros, setShareMembros] = useState([]);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareInviting, setShareInviting] = useState(false);

  const navigate = useNavigate();
  const { theme: themeColors, isDarkMode } = useTheme();
  const { isPrivate } = usePrivacy();
  const { sidebarWidth, isMobile } = useSidebar();
  // Processar query params vindos do sidebar (?canal=ID ou ?novo=true) — apenas no mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const canalIdFromUrl = params.get('canal');
    const novoFromUrl = params.get('novo');

    if (canalIdFromUrl) {
      setSelectedCanal(canalIdFromUrl);
      localStorage.setItem('selectedCanal', canalIdFromUrl);
    }

    if (novoFromUrl === 'true') {
      setShowNewCanalDialog(true);
    }

    // Limpar os params da URL sem causar re-render do React Router
    if (canalIdFromUrl || novoFromUrl) {
      window.history.replaceState({}, '', '/canais');
    }
  }, []);

  // Listen for sidebar custom events (when already on /canais and sidebar dispatches navigation)
  useEffect(() => {
    const handleSidebarNav = (e) => {
      const { action, canalId } = e.detail || {};
      if (action === 'select' && canalId) {
        setSelectedCanal(canalId);
        localStorage.setItem('selectedCanal', canalId);
      } else if (action === 'grid') {
        setSelectedCanal('');
        localStorage.removeItem('selectedCanal');
      } else if (action === 'novo') {
        setShowNewCanalDialog(true);
      }
    };
    window.addEventListener('sidebar-navigate-canal', handleSidebarNav);
    return () => window.removeEventListener('sidebar-navigate-canal', handleSidebarNav);
  }, []);

  // Salvar selectedCanal no localStorage sempre que mudar
  useEffect(() => {
    if (selectedCanal) {
      localStorage.setItem('selectedCanal', selectedCanal);
    }
  }, [selectedCanal]);

  // Salvar videoFilter no localStorage sempre que mudar
  useEffect(() => {
    localStorage.setItem('videoFilter', videoFilter);
  }, [videoFilter]);

  useEffect(() => {
    fetchCanais();
    checkIfAdmin();
  }, []);

  const checkIfAdmin = () => {
    axios.get(`${API}/auth/me`)
      .then((response) => {
        setIsAdmin(response.data.is_super_admin || false);
      })
      .catch(() => setIsAdmin(false));
  };

  useEffect(() => {
    // Só executa se há um canal selecionado
    if (!selectedCanal) return;

    // Verificar se canal existe em próprios OU compartilhados
    const canalExisteOwn = canais.some(c => c.id === selectedCanal);
    let canalExisteShared = false;
    try {
      const shared = JSON.parse(sessionStorage.getItem('sidebar-shared-channels') || '[]');
      canalExisteShared = shared.some(c => c.id === selectedCanal && c.comp_status === 'aceito');
    } catch { /* ignore */ }

    if (canalExisteOwn || canalExisteShared) {
      fetchNextVideoInfo();
      loadCustomButtonsFromCanal();
      fetchChannelPromptCount();
      fetchChannelReferences();
      fetchNarratorStatus(selectedCanal);
    } else if (canais.length > 0) {
      // Só reseta se os canais já carregaram e o canal realmente não existe
      console.warn('⚠️ Canal selecionado não existe mais, voltando para grid');
      setSelectedCanal('');
      localStorage.removeItem('selectedCanal');
    }
  }, [selectedCanal, canais]);

  // Fetch videos when channel, filter, or sort changes (resets to page 1)
  useEffect(() => {
    if (selectedCanal) {
      fetchVideos();
    }
  }, [selectedCanal, videoFilter, sortOrder]);

  // Reload custom buttons when canais are updated
  useEffect(() => {
    loadCustomButtonsFromCanal();
  }, [canais, selectedCanal]);

  const loadCustomButtonsFromCanal = () => {
    if (!selectedCanal) return;

    let canal = canais.find(c => c.id === selectedCanal);
    // Also check shared channels
    if (!canal) {
      try {
        const shared = JSON.parse(sessionStorage.getItem('sidebar-shared-channels') || '[]');
        canal = shared.find(c => c.id === selectedCanal);
      } catch { /* ignore */ }
    }
    if (canal && canal.custom_buttons && canal.custom_buttons.length > 0) {
      const loadedButtons = canal.custom_buttons.map((btn, index) => ({
        ...btn,
        id: index + 1
      }));
      while (loadedButtons.length < 6) {
        loadedButtons.push({ name: '', url: '', id: loadedButtons.length + 1 });
      }
      setCustomButtons(loadedButtons);
    } else {
      setCustomButtons([
        { name: '', url: '', id: 1 },
        { name: '', url: '', id: 2 },
        { name: '', url: '', id: 3 },
        { name: '', url: '', id: 4 },
        { name: '', url: '', id: 5 },
        { name: '', url: '', id: 6 }
      ]);
    }
  };

  const saveCustomButtons = async () => {
    if (!selectedCanal) {
      toast.error(t('channels.select_channel'));
      return;
    }

    try {
      const buttonsToSave = customButtons
        .filter(btn => btn.name && btn.url)
        .map(({ name, url }) => ({ name, url }));

      const response = await axios.put(`${API}/canais/${selectedCanal}`, {
        custom_buttons: buttonsToSave
      });

      setCanais(canais.map(c => c.id === selectedCanal ? response.data : c));
      
      setShowCustomButtonsDialog(false);
      toast.success(t('channels.buttons_saved'));
    } catch (error) {
      console.error('Erro ao salvar botões:', error);
      toast.error(t('channels.error_save_buttons'));
    }
  };

  const fetchCanais = async () => {
    try {
      const response = await axios.get(`${API}/canais`);
      setCanais(response.data);
      _canaisNavCache = response.data; // alimenta o cache de navegação (SWR)
      // Cache for sidebar dynamic channel list
      try {
        sessionStorage.setItem('sidebar-channels', JSON.stringify(response.data));
        window.dispatchEvent(new Event('sidebar-channels-updated'));
      } catch {};

      // Fetch shared channels
      try {
        const sharedResponse = await axios.get(`${API}/canais/compartilhados`);
        sessionStorage.setItem('sidebar-shared-channels', JSON.stringify(sharedResponse.data));
        window.dispatchEvent(new Event('sidebar-channels-updated'));
      } catch (e) {
        // Silently fail — shared channels are optional
      }

      // Se há canais disponíveis (own + shared)
      const sharedCanais = JSON.parse(sessionStorage.getItem('sidebar-shared-channels') || '[]');
      const allCanais = [...response.data, ...sharedCanais];
      if (allCanais.length > 0) {
        // Verificar se o canal salvo no localStorage ainda existe
        const savedCanal = localStorage.getItem('selectedCanal');
        const canalExists = savedCanal && allCanais.some(c => c.id === savedCanal);

        if (canalExists) {
          // Canal salvo existe, manter seleção
          setSelectedCanal(savedCanal);
        } else {
          // Nenhum canal salvo — mostrar grid de cards (não auto-selecionar)
          setSelectedCanal('');
          localStorage.removeItem('selectedCanal');
        }
      } else {
        // Não há canais, limpar seleção
        setSelectedCanal('');
        localStorage.removeItem('selectedCanal');
      }
    } catch (error) {
      toast.error(t('channels.error_load_channels'));
    } finally {
      setLoading(false);
    }
  };

  const sortVideosByNumber = (videoList, order = 'asc') => {
    return videoList.sort((a, b) => {
      const getVideoNumber = (video) => {
        const match = video.numero_video?.match(/\d+/);
        return match ? parseInt(match[0]) : 0;
      };
      const diff = getVideoNumber(a) - getVideoNumber(b);
      return order === 'asc' ? diff : -diff; // Se 'desc', inverte a ordem
    });
  };

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const fetchVideos = useCallback(async (loadMore = false) => {
    if (!selectedCanal) {
      setVideos([]);
      setVideosCounts({ total: 0, planejamento: 0, producao: 0, finalizado: 0, postado: 0 });
      setVideosFilteredTotal(0);
      setVideosOffset(0);
      return;
    }

    const cacheKey = `${selectedCanal}|${videoFilter}|${sortOrder}`;

    if (loadMore) {
      setLoadingMore(true);
    } else {
      // SWR: se há cache desta visão, renderiza na hora (sem skeleton) e
      // revalida em segundo plano; o resultado fresco substitui em silêncio.
      const cached = _videosNavCache.get(cacheKey);
      if (cached) {
        setVideos(cached.videos);
        setVideosOffset(cached.offset);
        setVideosFilteredTotal(cached.filteredTotal);
        setVideosCounts(cached.counts);
      } else {
        setLoadingVideos(true);
      }
    }

    try {
      const currentOffset = loadMore ? videosOffset + VIDEOS_PAGE_SIZE : 0;
      const statusParam = videoFilter === 'postados' ? 'postado' : (videoFilter !== 'todos' ? videoFilter : '');

      const params = new URLSearchParams({
        limit: VIDEOS_PAGE_SIZE.toString(),
        offset: currentOffset.toString(),
        sort_order: sortOrder,
      });
      if (statusParam) params.append('status', statusParam);

      const requestKey = `${cacheKey}|${currentOffset}`;
      let inFlight = _videosInFlight.get(requestKey);
      if (!inFlight) {
        inFlight = axios.get(`${API}/videos/canal/${selectedCanal}?${params.toString()}`)
          .finally(() => _videosInFlight.delete(requestKey));
        _videosInFlight.set(requestKey, inFlight);
      }
      const response = await inFlight;
      const { videos: newVideos, filtered_total, counts } = response.data;

      if (loadMore) {
        setVideos(prev => {
          const merged = [...prev, ...newVideos];
          _videosCacheSet(cacheKey, { videos: merged, offset: currentOffset, filteredTotal: filtered_total, counts });
          return merged;
        });
      } else {
        setVideos(newVideos);
        _videosCacheSet(cacheKey, { videos: newVideos, offset: currentOffset, filteredTotal: filtered_total, counts });
      }
      setVideosOffset(currentOffset);
      setVideosFilteredTotal(filtered_total);
      setVideosCounts(counts);
    } catch (error) {
      console.error('Erro ao carregar vídeos:', error);
      toast.error(t('channels.error_load_videos'));
    } finally {
      setLoadingVideos(false);
      setLoadingMore(false);
    }
  }, [selectedCanal, sortOrder, videoFilter, videosOffset]);

  const hasMoreVideos = videos.length < videosFilteredTotal;

  // Write-through do cache de navegação: mudanças otimistas (drag do kanban,
  // checklist, exclusão) ficam espelhadas — ao sair e voltar, a tela reabre
  // exatamente como estava, sem flash de dados antigos.
  useEffect(() => {
    if (!selectedCanal || loadingVideos) return;
    const cacheKey = `${selectedCanal}|${videoFilter}|${sortOrder}`;
    const entry = _videosNavCache.get(cacheKey);
    if (entry) {
      _videosCacheSet(cacheKey, {
        ...entry,
        videos,
        filteredTotal: videosFilteredTotal,
        counts: videosCounts,
      });
    }
  }, [videos, videosCounts, videosFilteredTotal, selectedCanal, videoFilter, sortOrder, loadingVideos]);

  const fetchNextVideoInfo = async () => {
    if (!selectedCanal) return;
    try {
      const response = await axios.get(`${API}/videos/canal/${selectedCanal}/next-number`);
      setNextVideoInfo(response.data);
    } catch (error) {
      console.error('Erro ao buscar próximo vídeo:', error);
    }
  };

  const fetchChannelPromptCount = async () => {
    if (!selectedCanal) return;
    try {
      const response = await axios.get(`${API}/prompts/canal/${selectedCanal}`);
      setChannelPromptCount(Array.isArray(response.data) ? response.data.length : 0);
    } catch (error) {
      console.error('Erro ao buscar prompts do canal:', error);
      setChannelPromptCount(0);
    }
  };

  const fetchChannelReferences = async () => {
    if (!selectedCanal) return;
    try {
      const response = await axios.get(`${API}/referencias/canal/${selectedCanal}`);
      setChannelReferences(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao buscar referências do canal:', error);
      setChannelReferences([]);
    }
  };

  const fetchNarratorStatus = async (canalId) => {
    if (!canalId) { setNarratorInfo(null); return; }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BACKEND_URL}/api/canais/${canalId}/narrator`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNarratorInfo(res.data);
    } catch {
      setNarratorInfo(null);
    }
  };

  // Channel navigation (prev/next)
  // All navigable channels (own + shared accepted)
  const getAllNavigableChannels = () => {
    let shared = [];
    try {
      shared = JSON.parse(sessionStorage.getItem('sidebar-shared-channels') || '[]')
        .filter(c => c.comp_status === 'aceito');
    } catch { /* ignore */ }
    return [...canais, ...shared].sort((a, b) => {
      const nameA = (a.nome || a.nome_canal || '').toLowerCase();
      const nameB = (b.nome || b.nome_canal || '').toLowerCase();
      return nameA.localeCompare(nameB, 'pt-BR');
    });
  };

  const navigateChannel = (direction) => {
    const allChannels = getAllNavigableChannels();
    if (allChannels.length <= 1) return;
    const currentIndex = allChannels.findIndex(c => c.id === selectedCanal);
    if (currentIndex === -1) return;
    const newIndex = direction === 'prev'
      ? (currentIndex > 0 ? currentIndex - 1 : allChannels.length - 1)
      : (currentIndex < allChannels.length - 1 ? currentIndex + 1 : 0);
    setSelectedCanal(allChannels[newIndex].id);
    localStorage.setItem('selectedCanal', allChannels[newIndex].id);
  };

  const handleBackToChannels = () => {
    setSelectedCanal('');
    localStorage.removeItem('selectedCanal');
  };

  // CORREÇÃO: Listener para recarregar vídeos quando roteiro é salvo
  // IMPORTANTE: Colocado DEPOIS de fetchVideos para evitar erro de inicialização
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'ROTEIRO_SAVED') {
        // Recarregar vídeos usando a função fetchVideos que já existe
        if (selectedCanal) {
          fetchVideos();
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [selectedCanal, fetchVideos]);

  // =====================================================================
  // YouTube Import Functions
  // (Fase 4 da unificação: a checagem de API key do usuário foi removida —
  //  a busca usa o hub global de chaves do sistema.)
  // =====================================================================
  const searchYoutubeChannel = async (url, isLink = false) => {
    const setSearching = isLink ? setYoutubeSearchingLink : setYoutubeSearching;
    const setChannel = isLink ? setYoutubeChannelLink : setYoutubeChannel;

    setSearching(true);
    setChannel(null);

    try {
      const res = await axios.get(`${API}/youtube/channel`, { params: { url } });
      setChannel(res.data);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao buscar canal';
      toast.error(msg);
    } finally {
      setSearching(false);
    }
  };

  const importYoutubeChannel = async (channelData, canalId = null) => {
    const setImporting = canalId ? setYoutubeImportingLink : setYoutubeImporting;
    setImporting(true);
    setImportProgress({ current: 0, total: channelData.video_count || 0, videoName: 'Preparando...' });

    try {
      const payload = {
        youtube_channel_id: channelData.channel_id,
        youtube_channel_url: youtubeUrl || youtubeUrlLink,
        channel_name: channelData.name,
        channel_thumbnail: channelData.thumbnail,
        youtube_subscribers: channelData.subscribers,
        idioma: newCanal.idioma || 'Português',
        frequencia: newCanal.frequencia_postagem || null,
        horario: newCanal.horario_postagem || ['19:00'],
        email: newCanal.email || null,
        anotacoes: newCanal.anotacoes || null,
      };

      if (canalId) {
        payload.canal_id = canalId;
      }

      const res = await axios.post(`${API}/youtube/import`, payload);

      setShowImportResult({
        canal_id: res.data.canal_id,
        canal_nome: res.data.canal_nome,
        videos_imported: res.data.videos_imported,
      });

      // Refresh channel list
      await fetchCanais();
      if (res.data.canal_id) {
        setSelectedCanal(res.data.canal_id);
        localStorage.setItem('selectedCanal', res.data.canal_id);
      }

      toast.success(res.data.message || `Canal importado! ${res.data.videos_imported} vídeos.`);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao importar canal';
      toast.error(msg);
    } finally {
      setImporting(false);
    }
  };

  const resetYoutubeStates = () => {
    setYoutubeUrl('');
    setYoutubeChannel(null);
    setYoutubeSearching(false);
    setYoutubeImporting(false);
    setImportProgress({ current: 0, total: 0, videoName: '' });
    setShowImportResult(null);
  };

  const resetYoutubeLinkStates = () => {
    setYoutubeUrlLink('');
    setYoutubeChannelLink(null);
    setYoutubeSearchingLink(false);
    setYoutubeImportingLink(false);
  };

  const handleCreateCanal = async (e) => {
    e.preventDefault();
    try {
      // Convert field names for backend
      const canalData = {
        nome: newCanal.nome?.trim() || '',
        idioma: newCanal.idioma,
        email: newCanal.email?.trim() || '',
        horario: newCanal.horario_postagem,
        frequencia: newCanal.frequencia_postagem?.trim() || '',
        videos_ja_postados: newCanal.videos_ja_postados,
        anotacoes: newCanal.anotacoes?.trim() || '',
        custom_buttons: newCanal.custom_buttons
      };
      
      const response = await axios.post(`${API}/canais`, canalData);
      setCanais([...canais, response.data]);
      setNewCanal({
        nome: '',
        idioma: 'Português',
        email: '',
        horario_postagem: ['19:00'],
        frequencia_postagem: '',
        videos_ja_postados: 0,
        anotacoes: '',
        custom_buttons: []
      });
      setShowNewCanalDialog(false);
      toast.success(t('channels.channel_created'));
    } catch (error) {
      toast.error(t('channels.error_create_channel'));
    }
  };

  const handleEditCanal = (canal) => {
    setEditingCanal(canal);
    setNewCanal({
      nome: canal.nome,
      idioma: canal.idioma,
      email: canal.email,
      horario_postagem: Array.isArray(canal.horario || canal.horario_postagem) 
        ? (canal.horario || canal.horario_postagem) 
        : [(canal.horario || canal.horario_postagem || '19:00')],
      frequencia_postagem: canal.frequencia || canal.frequencia_postagem || '',
      videos_ja_postados: canal.videos_ja_postados || 0,
      anotacoes: canal.anotacoes || '',
      custom_buttons: canal.custom_buttons || []
    });
    setShowEditCanalDialog(true);
  };

  const handleUpdateCanal = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        nome: newCanal.nome?.trim() || '',
        idioma: newCanal.idioma,
        email: newCanal.email?.trim() || '',
        horario: newCanal.horario_postagem,
        frequencia: newCanal.frequencia_postagem?.trim() || '',
        videos_ja_postados: newCanal.videos_ja_postados,
        anotacoes: newCanal.anotacoes?.trim() || ''
      };
      
      const response = await axios.put(`${API}/canais/${editingCanal.id}`, updateData);
      
      setCanais(canais.map(c => c.id === editingCanal.id ? response.data : c));
      
      await fetchCanais();
      
      if (selectedCanal === editingCanal.id) {
        fetchNextVideoInfo();
      }
      
      setNewCanal({
        nome: '',
        idioma: 'Português',
        email: '',
        horario_postagem: ['19:00'],
        frequencia_postagem: '',
        videos_ja_postados: 0,
        anotacoes: '',
        custom_buttons: []
      });
      setShowEditCanalDialog(false);
      setEditingCanal(null);
      toast.success(t('channels.channel_updated'));
    } catch (error) {
      console.error('Erro ao atualizar canal:', error);
      toast.error(t('channels.error_update_channel'));
    }
  };

  // --- Extras/Anotações modal handlers ---
  const openExtrasModal = () => {
    setExtrasText(selectedCanalData?.anotacoes || '');
    setShowExtrasModal(true);
  };

  const handleSaveExtras = async () => {
    if (!selectedCanal) return;
    setSavingExtras(true);
    try {
      await axios.put(`${API}/canais/${selectedCanal}`, { anotacoes: extrasText.trim() });
      await fetchCanais();
      setShowExtrasModal(false);
      toast.success(t('channels.channel_updated'));
    } catch (error) {
      toast.error(t('channels.error_update_channel'));
    } finally {
      setSavingExtras(false);
    }
  };

  // --- Helper functions ---
  const formatNumber = (num) => {
    if (!num || num === 0) return '--';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    const diffH = Math.floor(diffMs / 3600000);
    const diffD = Math.floor(diffMs / 86400000);
    if (diffMin < 1) return 'Atualizado agora';
    if (diffMin < 60) return `Atualizado há ${diffMin}min`;
    if (diffH < 24) return `Atualizado há ${diffH}h`;
    return `Atualizado há ${diffD}d`;
  };

  const handleRefreshYoutubeStats = async () => {
    if (!selectedCanal || refreshingStats) return;
    setRefreshingStats(true);
    try {
      const res = await axios.post(`${API}/canais/${selectedCanal}/youtube-stats/refresh`);
      const stats = res.data;
      // Update the canal data in state
      setCanais(prev => prev.map(c => c.id === selectedCanal ? {
        ...c,
        youtube_subscribers: stats.youtube_subscribers,
        youtube_total_views: stats.youtube_total_views,
        youtube_video_count: stats.youtube_video_count,
        // Avatar volta junto do refresh (mesma chamada no backend)
        ...(stats.channel_thumbnail ? { channel_thumbnail: stats.channel_thumbnail } : {}),
        last_sync_at: stats.last_sync_at,
      } : c));
      toast.success('Estatísticas YouTube atualizadas!');
    } catch (error) {
      console.error('Erro ao atualizar stats:', error);
      toast.error('Erro ao atualizar estatísticas do YouTube');
    } finally {
      setRefreshingStats(false);
    }
  };

  const handleCreateVideo = async (e) => {
    e.preventDefault();
    if (!selectedCanal) {
      toast.error(t('channels.select_channel'));
      return;
    }

    try {
      // Converter data do formato YYYY-MM-DD para DD/MM/YYYY se preenchida
      let dataPostagem = '';
      if (newVideo.data_postagem) {
        const [year, month, day] = newVideo.data_postagem.split('-');
        dataPostagem = `${day}/${month}/${year}`;
      }

      const videoData = {
        ...newVideo,
        titulo: newVideo.titulo?.trim() || '',
        thumbnail: newVideo.thumbnail?.trim() || '',
        numero_video: nextVideoInfo.numero_video || '',
        canal_id: selectedCanal,
        checklist_titulo: newVideo.titulo?.trim() ? "Publicado" : "",
        data_postagem: dataPostagem
      };
      const response = await axios.post(`${API}/videos`, videoData);
      setNewVideo({ numero_video: '', titulo: '', thumbnail: '', notas: '', data_postagem: '' });
      setShowNewVideoDialog(false);
      fetchVideos(); // Re-fetch to get correct sort position and updated counts
      
      fetchNextVideoInfo();
      
      toast.success(t('channels.video_created'));
    } catch (error) {
      toast.error(t('channels.error_create_video'));
    }
  };

  // useCallback (identidade estável): essas funções descem até KanbanCard/KanbanColumn
  // memoizados — se fossem recriadas a cada render do pai, o memo seria anulado e
  // todos os cards re-renderizariam a cada tecla/hover da página.
  const handleCompleteAllChecklist = useCallback(async (videoId) => {
    try {
      const video = videos.find(v => v.id === videoId);
      const oldStatus = video?.status || 'planejamento';

      const updateData = {
        checklist_titulo: "Pronto",
        checklist_roteiro: "Pronto",
        checklist_audio: "Pronto",
        checklist_thumbnail: "Pronto",
        checklist_pronto: "Pronto",
        checklist_postagem: "Publicado"  // Apenas este usa "Publicado"
      };

      const response = await axios.put(`${API}/videos/${videoId}`, updateData);
      const newStatus = response.data.status || 'planejamento';

      setVideos(prev => prev.map(video =>
        video.id === videoId ? { ...video, ...response.data } : video
      ));

      // Atualizar contadores localmente (sem requisição extra)
      if (oldStatus !== newStatus) {
        setVideosCounts(prev => ({
          ...prev,
          [oldStatus]: Math.max(0, (prev[oldStatus] || 0) - 1),
          [newStatus]: (prev[newStatus] || 0) + 1,
        }));
      }

      toast.success(t('channels.all_steps_completed'));
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail);
      } else {
        toast.error(t('channels.error_update_status'));
      }
    }
  }, [videos, t]);

  const handleChecklistChange = useCallback(async (videoId, field, checked) => {
    try {
      if (field === 'checklist_postagem' && checked) {
        const video = videos.find(v => v.id === videoId);
        if (!video.checklist_titulo || !video.checklist_roteiro || !video.checklist_audio || !video.checklist_thumbnail || !video.checklist_pronto) {
          setConfirmState({
            icon: <CheckSquare />,
            title: 'Completar checklist',
            message: t('channels.confirm_complete_all'),
            confirmLabel: 'Sim, completar',
            onConfirm: () => { setConfirmState(null); handleCompleteAllChecklist(videoId); },
          });
          return;
        }
      }

      if (field === 'checklist_postagem' && !checked) {
        const video = videos.find(v => v.id === videoId);
        if (video.checklist_postagem) {
          setConfirmState({
            icon: <AlertTriangle />,
            title: 'Desmarcar postagem',
            message: t('channels.confirm_unpost'),
            confirmLabel: 'Sim, desmarcar',
            danger: true,
            onConfirm: async () => {
              setConfirmState(null);
              try {
                const oldStatus = video.status || 'planejamento';
                const updateData = { [field]: '' };
                const response = await axios.put(`${API}/videos/${videoId}`, updateData);
                const newStatus = response.data.status || 'planejamento';
                setVideos(prev => prev.map(v => v.id === videoId ? { ...v, ...response.data } : v));
                // Atualizar contadores localmente (sem requisição extra)
                if (oldStatus !== newStatus) {
                  setVideosCounts(prev => ({
                    ...prev,
                    [oldStatus]: Math.max(0, (prev[oldStatus] || 0) - 1),
                    [newStatus]: (prev[newStatus] || 0) + 1,
                  }));
                }
                toast.success(t('channels.video_unmarked'));
              } catch (error) {
                toast.error(t('channels.error_save'));
              }
            },
          });
          return;
        }
      }

      // Convert boolean to string format expected by backend
      // CORREÇÃO: Usar valor correto para cada tipo de checkbox
      let value;
      if (field === 'checklist_postagem') {
        value = checked ? "Publicado" : "";
      } else {
        value = checked ? "Pronto" : "";
      }

      const video = videos.find(v => v.id === videoId);
      const oldStatus = video?.status || 'planejamento';

      const updateData = { [field]: value };
      const response = await axios.put(`${API}/videos/${videoId}`, updateData);
      const newStatus = response.data.status || 'planejamento';

      setVideos(prev => prev.map(video =>
        video.id === videoId ? { ...video, ...response.data } : video
      ));

      // Atualizar contadores localmente quando o status mudou (sem requisição extra)
      if (oldStatus !== newStatus) {
        setVideosCounts(prev => ({
          ...prev,
          [oldStatus]: Math.max(0, (prev[oldStatus] || 0) - 1),
          [newStatus]: (prev[newStatus] || 0) + 1,
        }));
      }

      if (field === 'checklist_postagem' && checked) {
        toast.success(t('channels.video_marked_posted'));
      } else if (field === 'checklist_postagem' && !checked) {
        toast.success(t('channels.video_unmarked'));
      } else {
        toast.success(t('channels.status_updated'));
      }
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail);
      } else {
        toast.error(t('channels.error_update_status'));
      }
    }
  }, [videos, t, handleCompleteAllChecklist]);

  // ═══ KANBAN: callbacks estáveis p/ cards memoizados (abrir roteiro / modal) ═══
  const handleKanbanOpenScript = useCallback((video) => {
    setScriptDrawerVideo({
      id: video.id,
      titulo: video.titulo,
      canalId: video.canal_id || selectedCanal,
      status: video.status,
    });
    setScriptDrawerOpen(true);
  }, [selectedCanal]);

  const handleKanbanCardClick = useCallback((video) => {
    setKanbanModalVideo(video);
  }, []);

  // ═══ KANBAN: Update video via drag & drop ═══
  const handleKanbanVideoUpdate = async (videoId, updateData) => {
    try {
      await axios.put(`${API}/videos/${videoId}`, updateData);
      // Não precisa de setVideos aqui — o optimistic update já fez
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail);
      } else {
        toast.error(t('channels.error_update_status'));
      }
      throw error; // Re-throw so KanbanBoard can handle rollback
    }
  };

  // ═══ KANBAN: Optimistic update — atualiza UI imediatamente ═══
  const handleKanbanOptimisticUpdate = (videoId, updateData) => {
    const video = videos.find(v => v.id === videoId);
    const oldStatus = video?.status || 'planejamento';
    const newStatus = updateData.status || oldStatus;

    setVideos(prev => prev.map(video =>
      video.id === videoId ? { ...video, ...updateData } : video
    ));

    // Atualizar contadores localmente
    if (oldStatus !== newStatus) {
      setVideosCounts(prev => ({
        ...prev,
        [oldStatus]: Math.max(0, (prev[oldStatus] || 0) - 1),
        [newStatus]: (prev[newStatus] || 0) + 1,
      }));
    }
  };

  // ═══ KANBAN: Optimistic rollback — reverte se API falhar ═══
  const handleKanbanOptimisticRollback = (previousVideos) => {
    setVideos(previousVideos);
    // Recalcular contadores a partir do estado anterior
    const counts = { total: previousVideos.length, planejamento: 0, producao: 0, finalizado: 0, postado: 0 };
    previousVideos.forEach(v => {
      const st = v.status || 'planejamento';
      if (counts[st] !== undefined) counts[st]++;
    });
    setVideosCounts(counts);
  };

  // ═══ KANBAN: Delete video (no confirm — KanbanBoard handles confirmation) ═══
  const handleKanbanVideoDelete = async (videoId) => {
    try {
      const deletedVideo = videos.find(v => v.id === videoId);
      await axios.delete(`${API}/videos/${videoId}`);
      setVideos(prev => prev.filter(video => video.id !== videoId));
      if (deletedVideo) {
        const st = deletedVideo.status || 'planejamento';
        setVideosCounts(prev => ({
          ...prev,
          total: Math.max(0, prev.total - 1),
          [st]: Math.max(0, (prev[st] || 0) - 1),
        }));
        setVideosFilteredTotal(prev => Math.max(0, prev - 1));
      }
      fetchNextVideoInfo();
      fetchVideos();  // Recarrega lista com numeração recalculada
      fetchCanais();
    } catch (error) {
      toast.error(t('channels.error_delete_video'));
      throw error;
    }
  };

  // ═══ THUMBNAIL UPLOAD/DELETE HANDLERS ═══
  const handleThumbnailUpload = async (videoId, file) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error(t('channels.thumbnail_invalid_type') || 'Apenas JPEG, PNG ou WebP são permitidos');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('channels.thumbnail_too_large') || 'Arquivo excede 5MB');
      return;
    }
    setUploadingThumbnail(prev => ({ ...prev, [videoId]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      // NOTE: Do NOT set Content-Type header manually for FormData!
      // The browser must set it automatically with the correct multipart boundary.
      // Setting it manually causes the server to fail parsing the multipart body.
      const response = await axios.post(`${API}/videos/${videoId}/thumbnail`, formData);
      if (response.data.success) {
        setVideos(prev => prev.map(v =>
          v.id === videoId ? { ...v, thumbnail_url: response.data.url, checklist_thumbnail: 'Publicado' } : v
        ));
        toast.success(t('channels.thumbnail_saved') || 'Thumbnail salva!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('channels.thumbnail_upload_error') || 'Erro ao salvar thumbnail');
    } finally {
      setUploadingThumbnail(prev => ({ ...prev, [videoId]: false }));
    }
  };

  const handleThumbnailDelete = async (videoId) => {
    // Importado OU vinculado ao YouTube: thumb é sincronizada, não se mexe
    const vDel = videos.find(v => v.id === videoId);
    if (vDel?.is_imported || vDel?.youtube_video_id) return;
    setUploadingThumbnail(prev => ({ ...prev, [videoId]: true }));
    try {
      await axios.delete(`${API}/videos/${videoId}/thumbnail`);
      setVideos(prev => prev.map(v =>
        v.id === videoId ? { ...v, thumbnail_url: null, checklist_thumbnail: '' } : v
      ));
      toast.success(t('channels.thumbnail_removed') || 'Thumbnail removida');
    } catch (error) {
      toast.error(t('channels.thumbnail_delete_error') || 'Erro ao remover thumbnail');
    } finally {
      setUploadingThumbnail(prev => ({ ...prev, [videoId]: false }));
    }
  };

  const triggerThumbnailUpload = (videoId) => {
    // Importado OU vinculado ao YouTube: thumb é sincronizada, sem upload
    const vUp = videos.find(v => v.id === videoId);
    if (vUp?.is_imported || vUp?.youtube_video_id) return;
    setThumbnailUploadVideoId(videoId);
    if (thumbnailInputRef.current) {
      thumbnailInputRef.current.value = '';
      thumbnailInputRef.current.click();
    }
  };

  const handleFieldEdit = (videoId) => {
    const video = videos.find(v => v.id === videoId);
    
    // Converter data DD/MM/YYYY para YYYY-MM-DD para o input type="date"
    let dataForInput = '';
    if (video.data_postagem) {
      const [day, month, year] = video.data_postagem.split('/');
      dataForInput = `${year}-${month}-${day}`;
    }
    
    setEditingFields(prev => ({
      ...prev, 
      [`${videoId}-titulo`]: true,
      [`${videoId}-thumbnail`]: true,
      [`${videoId}-notas`]: true,
      [`${videoId}-data_postagem`]: true
    }));
    setTempValues(prev => ({
      ...prev,
      [`${videoId}-titulo`]: video.titulo || '',
      [`${videoId}-titulo-link`]: video.titulo_link || '',
      [`${videoId}-thumbnail`]: video.thumbnail || '',
      [`${videoId}-thumbnail-link`]: video.thumbnail_link || '',
      [`${videoId}-notas`]: video.notas || '',
      [`${videoId}-data_postagem`]: dataForInput
    }));
  };
  
  // Função para abrir modal de link
  const handleOpenLinkModal = (videoId, field) => {
    const video = videos.find(v => v.id === videoId);
    const currentLink = field === 'titulo' ? video.titulo_link : video.thumbnail_link;
    setLinkModalData({ videoId, field, currentLink: currentLink || '' });
    setLinkInputValue(currentLink || '');
    setShowLinkModal(true);
  };
  
  // Função para salvar link do modal
  const handleSaveLinkFromModal = () => {
    const { videoId, field } = linkModalData;
    const linkField = `${field}-link`;
    handleTempValueChange(videoId, linkField, linkInputValue.trim());
    setShowLinkModal(false);
    setLinkInputValue('');
  };

  // ── Vincular vídeo publicado no YouTube ao card (2026-06) ──
  const openYtLinkModal = (video) => {
    setYtLinkModal({ videoId: video.id, url: '', loading: false, error: '', result: null, updateTitle: false });
  };

  // Merge da resposta no estado + contadores de coluna (padrão handleChecklistChange)
  const applyYtVideoResponse = (videoId, data) => {
    const video = videos.find(v => v.id === videoId);
    const oldStatus = video?.status || 'planejamento';
    const newStatus = data.status || oldStatus;
    setVideos(prev => prev.map(v => v.id === videoId ? { ...v, ...data } : v));
    if (oldStatus !== newStatus) {
      setVideosCounts(prev => ({
        ...prev,
        [oldStatus]: Math.max(0, (prev[oldStatus] || 0) - 1),
        [newStatus]: (prev[newStatus] || 0) + 1,
      }));
    }
  };

  const handleYtLink = async () => {
    if (!ytLinkModal || ytLinkModal.loading) return;
    const url = (ytLinkModal.url || '').trim();
    if (!url) return;
    setYtLinkModal(prev => ({ ...prev, loading: true, error: '' }));
    try {
      const res = await axios.post(`${API}/videos/${ytLinkModal.videoId}/link-youtube`, { url });
      const { titulo_youtube, aviso_canal_divergente, ...videoData } = res.data;
      applyYtVideoResponse(ytLinkModal.videoId, videoData);
      setYtLinkModal(prev => ({
        ...prev, loading: false,
        result: { titulo_youtube, aviso_canal_divergente },
      }));
      toast.success(t('channels.yt_link_success') || 'Vídeo vinculado e marcado como Publicado');
    } catch (error) {
      const detail = error.response?.data?.detail || t('channels.error_save') || 'Erro ao vincular';
      setYtLinkModal(prev => ({ ...prev, loading: false, error: detail }));
    }
  };

  // Concluir: aplica o título do YouTube se o opt-in foi marcado (PUT, zero quota)
  const handleYtLinkFinish = async () => {
    if (!ytLinkModal) return;
    const { videoId, result, updateTitle } = ytLinkModal;
    if (updateTitle && result?.titulo_youtube) {
      try {
        const res = await axios.put(`${API}/videos/${videoId}`, { titulo: result.titulo_youtube });
        setVideos(prev => prev.map(v => v.id === videoId ? { ...v, ...res.data } : v));
      } catch {
        toast.error(t('channels.error_save') || 'Erro ao atualizar título');
      }
    }
    setYtLinkModal(null);
  };

  const handleYtUnlink = async () => {
    if (!ytLinkModal || ytLinkModal.loading) return;
    setYtLinkModal(prev => ({ ...prev, loading: true, error: '' }));
    try {
      const res = await axios.delete(`${API}/videos/${ytLinkModal.videoId}/link-youtube`);
      applyYtVideoResponse(ytLinkModal.videoId, res.data);
      toast.success(t('channels.yt_unlink_success') || 'Vínculo removido (checkboxes mantidos)');
      setYtLinkModal(null);
    } catch (error) {
      const detail = error.response?.data?.detail || t('channels.error_save') || 'Erro ao desvincular';
      setYtLinkModal(prev => ({ ...prev, loading: false, error: detail }));
    }
  };

  const handleFieldSaveAll = async (videoId) => {
    const tituloValue = (tempValues[`${videoId}-titulo`] || '').trim();
    const tituloLinkValue = (tempValues[`${videoId}-titulo-link`] || '').trim();
    const thumbnailValue = (tempValues[`${videoId}-thumbnail`] || '').trim();
    const thumbnailLinkValue = (tempValues[`${videoId}-thumbnail-link`] || '').trim();
    const notasValue = (tempValues[`${videoId}-notas`] || '').trim();
    const dataPostagemInput = tempValues[`${videoId}-data_postagem`] || '';
    
    // Converter data do formato YYYY-MM-DD para DD/MM/YYYY
    let dataPostagem = '';
    if (dataPostagemInput) {
      const [year, month, day] = dataPostagemInput.split('-');
      dataPostagem = `${day}/${month}/${year}`;
    }
    
    try {
      const updateData = {
        titulo: tituloValue,
        titulo_link: tituloLinkValue,
        thumbnail: thumbnailValue,
        thumbnail_link: thumbnailLinkValue,
        notas: notasValue,
        data_postagem: dataPostagem
      };

      // Backend expects string, not boolean
      if (tituloValue) {
        updateData.checklist_titulo = "true";
      }
      
      // NÃO marcar automaticamente o checkbox da thumbnail ao salvar
      
      const response = await axios.put(`${API}/videos/${videoId}`, updateData);
      
      setVideos(prev => prev.map(video =>
        video.id === videoId ? { ...video, ...response.data } : video
      ));
      
      setEditingFields(prev => {
        const newState = {...prev};
        delete newState[`${videoId}-titulo`];
        delete newState[`${videoId}-thumbnail`];
        delete newState[`${videoId}-notas`];
        delete newState[`${videoId}-data_postagem`];
        return newState;
      });
      setTempValues(prev => {
        const newState = {...prev};
        delete newState[`${videoId}-titulo`];
        delete newState[`${videoId}-titulo-link`];
        delete newState[`${videoId}-thumbnail`];
        delete newState[`${videoId}-thumbnail-link`];
        delete newState[`${videoId}-notas`];
        delete newState[`${videoId}-data_postagem`];
        return newState;
      });
      
      toast.success(t('channels.saved'));
    } catch (error) {
      console.error('Erro ao atualizar vídeo:', error);
      const errorMsg = error.response?.data?.detail || t('channels.error_save');
      toast.error(errorMsg);
    }
  };

  const handleFieldCancel = (videoId) => {
    setEditingFields(prev => {
      const newState = {...prev};
      delete newState[`${videoId}-titulo`];
      delete newState[`${videoId}-thumbnail`];
      delete newState[`${videoId}-notas`];
      delete newState[`${videoId}-data_postagem`];
      return newState;
    });
    setTempValues(prev => {
      const newState = {...prev};
      delete newState[`${videoId}-titulo`];
      delete newState[`${videoId}-thumbnail`];
      delete newState[`${videoId}-notas`];
      delete newState[`${videoId}-data_postagem`];
      return newState;
    });
  };

  const handleTempValueChange = (videoId, field, value) => {
    const key = `${videoId}-${field}`;
    
    // Reset format state quando o usuário edita manualmente
    if (textFormatStates[key] !== undefined) {
      setTextFormatStates(prev => {
        const newState = {...prev};
        delete newState[key];
        return newState;
      });
      setOriginalTexts(prev => {
        const newState = {...prev};
        delete newState[key];
        return newState;
      });
    }
    
    setTempValues(prev => ({...prev, [key]: value}));
  };

  // Funções de formatação de texto
  const toTitleCase = (text) => {
    // Palavras que devem ficar em minúsculo (exceto primeira e última)
    const minusculas = [
      // Português/Espanhol
      'de', 'da', 'do', 'das', 'dos', 'del', 'la', 'las', 'lo', 'los', 
      'y', 'e', 'a', 'o', 'por', 'para', 'com', 'en', 'que', 'se', 'su', 'tu', 'mi',
      // Inglês
      'the', 'and', 'but', 'or', 'nor', 'for', 'so', 'yet', 'as', 'at', 
      'by', 'in', 'of', 'on', 'per', 'to', 'vs', 'via', 'an'
    ];
    
    const palavras = text.split(' ');
    
    return palavras.map((palavra, index) => {
      const palavraLower = palavra.toLowerCase();
      
      // Primeira e última palavra sempre capitalizadas
      if (index === 0 || index === palavras.length - 1) {
        return palavra.charAt(0).toUpperCase() + palavra.slice(1).toLowerCase();
      }
      
      // Verificar se é uma palavra minúscula
      if (minusculas.includes(palavraLower)) {
        return palavraLower;
      }
      
      // Capitalizar primeira letra
      return palavra.charAt(0).toUpperCase() + palavra.slice(1).toLowerCase();
    }).join(' ');
  };

  const handleTextFormat = (videoId, field) => {
    const key = `${videoId}-${field}`;
    const currentText = tempValues[key] || '';
    const currentState = textFormatStates[key] || 0;
    
    let newText = currentText;
    let newState = (currentState + 1) % 4;
    
    // Salvar texto original no primeiro clique
    if (currentState === 0 && !originalTexts[key]) {
      setOriginalTexts(prev => ({...prev, [key]: currentText}));
    }
    
    // Aplicar formatação baseada no estado
    switch (currentState) {
      case 0: // Próximo: MAIÚSCULO
        newText = currentText.toUpperCase();
        break;
      case 1: // Próximo: minúsculo
        newText = currentText.toLowerCase();
        break;
      case 2: // Próximo: Title Case
        newText = toTitleCase(currentText);
        break;
      case 3: // Próximo: Restaurar Original
        newText = originalTexts[key] || currentText;
        newState = 0; // Resetar para o início
        // Limpar original text após restaurar
        setOriginalTexts(prev => {
          const newState = {...prev};
          delete newState[key];
          return newState;
        });
        break;
      default:
        newText = currentText;
    }
    
    setTempValues(prev => ({...prev, [key]: newText}));
    setTextFormatStates(prev => ({...prev, [key]: newState}));
  };

  const getFormatButtonIcon = (videoId, field) => {
    const key = `${videoId}-${field}`;
    const state = textFormatStates[key] || 0;
    
    const icons = ['AA', 'aa', 'Aa', '↩'];
    return icons[state];
  };

  const getFormatButtonTooltip = (videoId, field) => {
    const key = `${videoId}-${field}`;
    const state = textFormatStates[key] || 0;
    
    const tooltips = [
      'Aplicar MAIÚSCULO',
      'Aplicar minúsculo',
      'Aplicar Primeira Letra de Cada Palavra',
      'Restaurar Original'
    ];
    return tooltips[state];
  };

  // Notas editing functions
  const handleNotasEdit = (videoId) => {
    const video = videos.find(v => v.id === videoId);
    setEditingNotas(prev => ({...prev, [videoId]: true}));
    setTempNotas(prev => ({...prev, [videoId]: video.notas || ''}));
  };

  const handleNotasSave = async (videoId) => {
    const notasValue = tempNotas[videoId] || '';
    
    try {
      const response = await axios.put(`${API}/videos/${videoId}`, {
        notas: notasValue
      });
      
      setVideos(prev => prev.map(video =>
        video.id === videoId ? { ...video, ...response.data } : video
      ));
      
      setEditingNotas(prev => {
        const newState = {...prev};
        delete newState[videoId];
        return newState;
      });
      setTempNotas(prev => {
        const newState = {...prev};
        delete newState[videoId];
        return newState;
      });
      
      toast.success(t('channels.saved'));
    } catch (error) {
      console.error('Erro ao atualizar nota:', error);
      const errorMsg = error.response?.data?.detail || t('channels.error_save');
      toast.error(errorMsg);
    }
  };

  const handleNotasCancel = (videoId) => {
    setEditingNotas(prev => {
      const newState = {...prev};
      delete newState[videoId];
      return newState;
    });
    setTempNotas(prev => {
      const newState = {...prev};
      delete newState[videoId];
      return newState;
    });
  };

  const handleCopyToClipboard = (text, label) => {
    if (!text) return;
    
    navigator.clipboard.writeText(text).then(() => {
      toast.success(t('channels.copied'));
    }).catch(() => {
      toast.error(t('channels.error_save'));
    });
  };

  const deleteVideo = (videoId) => {
    const video = videos.find(v => v.id === videoId);
    setConfirmState({
      icon: <Trash2 />,
      title: 'Excluir vídeo',
      message: t('channels.confirm_delete_video'),
      confirmLabel: 'Excluir',
      danger: true,
      onConfirm: async () => {
        setConfirmState(null);
        try {
          await axios.delete(`${API}/videos/${videoId}`);
          setVideos(prev => prev.filter(v => v.id !== videoId));
          if (video) {
            const st = video.status || 'planejamento';
            setVideosCounts(prev => ({
              ...prev,
              total: Math.max(0, prev.total - 1),
              [st]: Math.max(0, (prev[st] || 0) - 1),
            }));
            setVideosFilteredTotal(prev => Math.max(0, prev - 1));
          }
          fetchNextVideoInfo();
          fetchVideos();  // Recarrega lista com numeração recalculada
          fetchCanais();
          toast.success(t('channels.video_deleted'));
        } catch (error) {
          toast.error(t('channels.error_delete_video'));
        }
      },
    });
  };

  const deleteCanal = (canalId) => {
    const canal = canais.find(c => c.id === canalId);
    setConfirmState({
      icon: <Trash2 />,
      title: 'Excluir canal',
      message: t('channels.confirm_delete_channel'),
      confirmLabel: 'Excluir',
      danger: true,
      onConfirm: async () => {
        setConfirmState(null);
        try {
          await axios.delete(`${API}/canais/${canalId}`);
          setCanais(canais.filter(c => c.id !== canalId));
          if (selectedCanal === canalId) {
            setSelectedCanal(canais.length > 1 ? canais.find(c => c.id !== canalId)?.id || '' : '');
          }
          toast.success(t('channels.channel_deleted'));
        } catch (error) {
          toast.error(t('channels.error_delete_channel'));
        }
      },
    });
  };

  // ==================== CANAL SHARING FUNCTIONS ====================
  const loadShareMembros = async (canalId) => {
    setShareLoading(true);
    try {
      const response = await axios.get(`${API}/canais/${canalId}/membros`);
      setShareMembros(response.data.membros || []);
    } catch (error) {
      toast.error('Erro ao carregar membros');
    } finally {
      setShareLoading(false);
    }
  };

  const handleShareInvite = async () => {
    if (!shareEmail.trim()) return;
    setShareInviting(true);
    try {
      await axios.post(`${API}/canais/${selectedCanal}/compartilhar`, {
        email: shareEmail.trim(),
        ...sharePermissions
      });
      toast.success(t('sharing.invite_success', { email: shareEmail }));
      setShareEmail('');
      setSharePermissions({ pode_deletar_videos: false, pode_editar_config: false, pode_editar_referencias: false, pode_editar_prompts: false });
      loadShareMembros(selectedCanal);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('sharing.invite_error'));
    } finally {
      setShareInviting(false);
    }
  };

  const handleUpdateMembro = async (userId, permissions) => {
    try {
      await axios.put(`${API}/canais/${selectedCanal}/membros/${userId}`, permissions);
    } catch (error) {
      toast.error(t('sharing.update_permissions_error'));
    }
  };

  const handleRemoveMembro = async (userId, email) => {
    // Close share modal first so ConfirmDialog is clickable (z-index overlap)
    setShowShareModal(false);
    setConfirmState({
      icon: <Users />,
      title: t('sharing.remove_title'),
      message: t('sharing.remove_confirm_message', { email }),
      confirmLabel: t('sharing.remove_confirm_label'),
      danger: true,
      onConfirm: async () => {
        setConfirmState(null);
        try {
          await axios.delete(`${API}/canais/${selectedCanal}/membros/${userId}`);
          toast.success(t('sharing.remove_success'));
          // Reopen share modal and refresh members
          setShowShareModal(true);
          loadShareMembros(selectedCanal);
        } catch (error) {
          toast.error(t('sharing.remove_error'));
          setShowShareModal(true);
        }
      },
      onCancel: () => {
        setConfirmState(null);
        // Reopen share modal on cancel
        setShowShareModal(true);
      },
    });
  };

  const handleAceitarConvite = async (compId) => {
    try {
      await axios.post(`${API}/canais/compartilhamentos/${compId}/aceitar`);
      toast.success(t('sharing.accept_success'));
      fetchCanais();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('sharing.accept_error'));
    }
  };

  const handleRecusarConvite = async (compId) => {
    try {
      await axios.post(`${API}/canais/compartilhamentos/${compId}/recusar`);
      toast.success(t('sharing.decline_success'));
      fetchCanais();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('sharing.decline_error'));
    }
  };
  // ==================== END CANAL SHARING FUNCTIONS ====================

  const archiveCanal = (canalId) => {
    const canal = canais.find(c => c.id === canalId);
    setConfirmState({
      icon: <Archive />,
      title: 'Arquivar canal',
      message: `Arquivar canal "${canal?.nome}"? Ele será removido da sidebar mas continuará disponível em Gestão de Canais.`,
      confirmLabel: 'Arquivar',
      onConfirm: async () => {
        setConfirmState(null);
        try {
          await axios.post(`${API}/canais/${canalId}/archive`);
          const updated = canais.map(c => c.id === canalId ? { ...c, is_archived: true, archived_at: new Date().toISOString() } : c);
          setCanais(updated);
          if (selectedCanal === canalId) { setSelectedCanal(''); localStorage.removeItem('selectedCanal'); }
          try { sessionStorage.setItem('sidebar-channels', JSON.stringify(updated)); window.dispatchEvent(new Event('sidebar-channels-updated')); } catch {}
          toast.success('Canal arquivado com sucesso');
        } catch { toast.error('Erro ao arquivar canal'); }
      },
    });
  };

  const unarchiveCanal = async (canalId) => {
    try {
      await axios.post(`${API}/canais/${canalId}/unarchive`);
      const updated = canais.map(c => c.id === canalId ? { ...c, is_archived: false, archived_at: null } : c);
      setCanais(updated);
      try { sessionStorage.setItem('sidebar-channels', JSON.stringify(updated)); window.dispatchEvent(new Event('sidebar-channels-updated')); } catch {}
      toast.success('Canal restaurado com sucesso');
    } catch { toast.error('Erro ao desarquivar canal'); }
  };

  const openRoteiro = (videoId, titulo, canalId) => {
    // Abrir em tela cheia
    const screenWidth = window.screen.availWidth;
    const screenHeight = window.screen.availHeight;
    const roteiroWindow = window.open('', '_blank', `width=${screenWidth},height=${screenHeight},left=0,top=0,scrollbars=yes,resizable=yes`);
    
    // Tentar colocar em fullscreen (alguns navegadores podem bloquear)
    if (roteiroWindow) {
      setTimeout(() => {
        try {
          roteiroWindow.moveTo(0, 0);
          roteiroWindow.resizeTo(screenWidth, screenHeight);
        } catch (e) {
        }
      }, 100);
    }
    
    // Monitorar quando a janela fechar e atualizar lista automaticamente
    const checkWindowClosed = setInterval(() => {
      if (roteiroWindow.closed) {
        clearInterval(checkWindowClosed);
        // Recarregar lista de vídeos para mostrar checkbox atualizado
        fetchVideos();
      }
    }, 500);
    
    // Detectar tema atual
    const currentTheme = isDarkMode ? 'dark' : 'light';
    
    roteiroWindow.document.write(`
      <!DOCTYPE html>
      <html class="${currentTheme}">
        <head>
          <title>Roteiro - ${titulo}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, sans-serif;
              margin: 0;
              padding: 20px;
              height: 100vh;
              overflow: hidden;
              transition: background 0.3s, color 0.3s;
              display: flex;
              flex-direction: column;
            }
            
            /* Dark theme */
            html.dark body {
              background: #252b3b;
              color: #ffffff;
            }
            
            /* Light theme */
            html.light body {
              background: #f9fafb;
              color: #111827;
            }
            
            .header {
              padding: 24px;
              border-radius: 16px;
              margin-bottom: 24px;
              transition: all 0.3s;
            }
            
            html.dark .header {
              background: linear-gradient(135deg, #252b3b 0%, #252b3b 100%);
              border: 1px solid #2a2a2a;
            }
            
            html.light .header {
              background: linear-gradient(135deg, #ffffff 0%, #f3f4f6 100%);
              border: 1px solid #e5e7eb;
            }
            
            .title {
              font-size: 28px;
              font-weight: 700;
              color: #06b6d4;
              margin-bottom: 8px;
              display: flex;
              align-items: center;
              gap: 12px;
            }
            
            .title::before {
              content: '📝';
              font-size: 32px;
            }
            
            .subtitle {
              font-size: 14px;
              font-weight: 500;
            }
            
            html.dark .subtitle {
              color: #9ca3af;
            }
            
            html.light .subtitle {
              color: #6b7280;
            }
            
            .editor-container {
              border-radius: 16px;
              overflow: hidden;
              transition: all 0.3s;
              flex: 1;
              display: flex;
              flex-direction: column;
              min-height: 0;
            }
            
            html.dark .editor-container {
              background: #252b3b;
              border: 1px solid #2a2a2a;
            }
            
            html.light .editor-container {
              background: #ffffff;
              border: 1px solid #e5e7eb;
            }
            
            .toolbar {
              padding: 16px 24px;
              display: flex;
              justify-content: space-between;
              align-items: center;
              transition: all 0.3s;
            }
            
            html.dark .toolbar {
              background: #1a1a1a;
              border-bottom: 1px solid #2a2a2a;
            }
            
            html.light .toolbar {
              background: #f9fafb;
              border-bottom: 1px solid #e5e7eb;
            }
            
            .save-btn {
              background: #06b6d4;
              color: white;
              border: none;
              padding: 12px 28px;
              border-radius: 10px;
              cursor: pointer;
              font-weight: 600;
              font-size: 14px;
              transition: all 0.2s ease;
              display: flex;
              align-items: center;
              gap: 8px;
            }
            
            .save-btn:hover {
              background: #0891b2;
              transform: translateY(-1px);
              box-shadow: 0 4px 12px rgba(6, 182, 212, 0.3);
            }
            
            .save-btn:active {
              transform: translateY(0);
            }
            
            #roteiro-editor {
              width: 100%;
              flex: 1;
              border: 1px solid;
              border-radius: 0;
              padding: 24px;
              font-size: 16px;
              line-height: 1.8;
              resize: none;
              outline: none;
              font-family: 'Segoe UI', system-ui, sans-serif;
              transition: all 0.3s;
              overflow-y: auto;
            }
            
            html.dark #roteiro-editor {
              background: #252b3b;
              color: #ffffff;
              border-color: #374151;
            }
            
            html.light #roteiro-editor {
              background: #ffffff;
              color: #111827;
              border-color: #d1d5db;
            }
            
            html.dark #roteiro-editor:focus {
              border-color: #4b5563;
            }
            
            html.light #roteiro-editor:focus {
              border-color: #9ca3af;
            }
            
            html.dark #roteiro-editor::placeholder {
              color: #4b5563;
            }
            
            html.light #roteiro-editor::placeholder {
              color: #9ca3af;
            }
            
            .status {
              padding: 8px 16px;
              border-radius: 20px;
              font-size: 13px;
              font-weight: 600;
              display: flex;
              align-items: center;
              gap: 6px;
            }
            
            .status.saved {
              background: rgba(6, 182, 212, 0.15);
              color: #06b6d4;
              border: 1px solid rgba(6, 182, 212, 0.3);
            }
            
            .status.saving {
              background: rgba(251, 191, 36, 0.15);
              color: #fbbf24;
              border: 1px solid rgba(251, 191, 36, 0.3);
            }
            
            .stats-container {
              display: flex;
              gap: 24px;
              align-items: center;
            }
            
            .stat-item {
              display: flex;
              flex-direction: column;
              gap: 4px;
            }
            
            .stat-label {
              color: #6b7280;
              font-size: 11px;
              text-transform: uppercase;
              font-weight: 600;
              letter-spacing: 0.5px;
            }
            
            .stat-value {
              color: #06b6d4;
              font-size: 20px;
              font-weight: 700;
            }
            
            .time-card {
              border-radius: 12px;
              padding: 16px;
              margin-left: 24px;
              transition: all 0.3s;
            }
            
            html.dark .time-card {
              background: linear-gradient(135deg, rgba(6, 182, 212, 0.1), rgba(16, 185, 129, 0.1));
              border: 1px solid rgba(6, 182, 212, 0.2);
            }
            
            html.light .time-card {
              background: linear-gradient(135deg, rgba(6, 182, 212, 0.08), rgba(16, 185, 129, 0.08));
              border: 1px solid rgba(6, 182, 212, 0.25);
            }
            
            .time-card-title {
              color: #06b6d4;
              font-size: 12px;
              font-weight: 700;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              margin-bottom: 12px;
            }
            
            .time-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 12px;
            }
            @media (max-width: 767px) {
              .time-grid {
                grid-template-columns: repeat(2, 1fr);
              }
            }
            
            .time-item {
              border-radius: 8px;
              padding: 10px;
              text-align: center;
              transition: all 0.3s;
            }
            
            html.dark .time-item {
              background: rgba(17, 17, 17, 0.6);
              border: 1px solid #2a2a2a;
            }
            
            html.light .time-item {
              background: rgba(255, 255, 255, 0.6);
              border: 1px solid #e5e7eb;
            }
            
            .time-label {
              font-size: 9px;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.3px;
              margin-bottom: 6px;
            }
            
            html.dark .time-label {
              color: #9ca3af;
            }
            
            html.light .time-label {
              color: #6b7280;
            }
            
            .time-value {
              color: #10b981;
              font-size: 16px;
              font-weight: 700;
              white-space: nowrap;
            }
            
            .time-value.normal {
              color: #06b6d4;
            }
            
            .time-value.slow {
              color: #fbbf24;
            }
            
            .time-value.fast {
              color: #10b981;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="title">${titulo}</div>
            <div class="subtitle">Editor de Roteiro - Dark Planner</div>
          </div>
          
          <div class="editor-container">
            <div class="toolbar">
              <div style="display: flex; align-items: center;">
                <div class="stats-container">
                  <div class="stat-item">
                    <span class="stat-label">Caracteres</span>
                    <span class="stat-value" id="char-count">0</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-label">Palavras</span>
                    <span class="stat-value" id="word-count">0</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-label">Parágrafos</span>
                    <span class="stat-value" id="paragraph-count">0</span>
                  </div>
                </div>
                <div class="time-card">
                  <div class="time-card-title">Tempo de narração (padrão)</div>
                  <div class="time-grid">
                    <div class="time-item">
                      <div class="time-label">Normal</div>
                      <div class="time-value normal" id="time-normal">0s</div>
                    </div>
                    <div class="time-item">
                      <div class="time-label">Lento/Pausado</div>
                      <div class="time-value slow" id="time-slow">0s</div>
                    </div>
                    <div class="time-item">
                      <div class="time-label">Rápido/Dinâmico</div>
                      <div class="time-value fast" id="time-fast">0s</div>
                    </div>
                  </div>
                </div>
              </div>
              <div style="display: flex; align-items: center; gap: 16px;">
                <span id="status" class="status"></span>
                <button class="save-btn" style="background: linear-gradient(135deg, #10B981, #06B6D4);" onclick="downloadSRT()">📥 Baixar SRT</button>
                <button class="save-btn" style="background: linear-gradient(135deg, #8B5CF6, #6366F1);" onclick="downloadTXT()">📄 Baixar TXT</button>
                <button class="save-btn" onclick="saveRoteiro()">💾 Salvar Roteiro</button>
              </div>
            </div>
            <textarea id="roteiro-editor" placeholder="Digite o roteiro do vídeo aqui...

Dicas:
• Use este espaço como um editor de texto completo
• O conteúdo é salvo automaticamente enquanto você digita
• Organize seu roteiro por seções: Introdução, Desenvolvimento, Conclusão
• Adicione notas de produção entre [colchetes]"></textarea>
          </div>

          <script>
            const videoId = '${videoId}';
            const canalId = '${canalId}';
            const videoTitulo = '${titulo.replace(/'/g, "\\'")}';
            const API = '${API}';
            let saveTimeout;
            
            const editor = document.getElementById('roteiro-editor');
            const charCount = document.getElementById('char-count');
            const wordCount = document.getElementById('word-count');
            const paragraphCount = document.getElementById('paragraph-count');
            const timeNormal = document.getElementById('time-normal');
            const timeSlow = document.getElementById('time-slow');
            const timeFast = document.getElementById('time-fast');
            
            function formatTime(seconds) {
              if (seconds === 0) return '0s';
              const mins = Math.floor(seconds / 60);
              const secs = Math.floor(seconds % 60);
              if (mins === 0) return secs + 's';
              return mins + 'min e ' + secs + 's';
            }
            
            function updateCounts() {
              const text = editor.value;
              const trimmedText = text.trim();
              
              // Caracteres (todos, incluindo espaços)
              const chars = text.length;
              charCount.textContent = chars.toLocaleString('pt-BR');
              
              // Palavras
              const words = trimmedText ? trimmedText.split(/\\s+/).length : 0;
              wordCount.textContent = words.toLocaleString('pt-BR');
              
              // Parágrafos (linhas não vazias)
              const paragraphs = trimmedText ? trimmedText.split(/\\n+/).filter(p => p.trim().length > 0).length : 0;
              paragraphCount.textContent = paragraphs.toLocaleString('pt-BR');
              
              // Tempo de narração (baseado em PALAVRAS)
              // Velocidade Normal: 155 palavras/minuto (padrão Dumela para português/vídeos)
              // Lento/Pausado: 130 palavras/minuto
              // Rápido/Dinâmico: 183 palavras/minuto (velocidade inglês adaptada)
              const secondsNormal = (words / 155) * 60;
              const secondsSlow = (words / 130) * 60;
              const secondsFast = (words / 183) * 60;
              
              timeNormal.textContent = formatTime(secondsNormal);
              timeSlow.textContent = formatTime(secondsSlow);
              timeFast.textContent = formatTime(secondsFast);
            }
            
            editor.addEventListener('input', updateCounts);
            
            fetch(API + '/roteiros/video/' + videoId, {
              headers: {
                'Authorization': 'Bearer ' + localStorage.getItem('token')
              }
            })
            .then(response => response.json())
            .then(data => {
              if (data && data.conteudo) {
                editor.value = data.conteudo;
                updateCounts();
              }
            })
            .catch(error => console.error('Erro ao carregar roteiro:', error));

            function saveRoteiro() {
              const conteudo = editor.value;
              const status = document.getElementById('status');
              
              status.textContent = 'Salvando...';
              status.className = 'status saving';
              
              fetch(API + '/roteiros/video/' + videoId, {
                headers: {
                  'Authorization': 'Bearer ' + localStorage.getItem('token')
                }
              })
              .then(response => response.json())
              .then(existingRoteiro => {
                if (existingRoteiro && existingRoteiro.id) {
                  return fetch(API + '/roteiros/' + existingRoteiro.id, {
                    method: 'PUT',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': 'Bearer ' + localStorage.getItem('token')
                    },
                    body: JSON.stringify({
                      conteudo: conteudo
                    })
                  });
                } else {
                  return fetch(API + '/roteiros', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': 'Bearer ' + localStorage.getItem('token')
                    },
                    body: JSON.stringify({
                      canal_id: canalId,
                      video_id: videoId,
                      conteudo: conteudo
                    })
                  });
                }
              })
              .then(response => {
                if (response.ok) {
                  status.textContent = '✅ Salvo!';
                  status.className = 'status saved';
                  
                  // CORREÇÃO: Notificar janela pai para recarregar vídeos
                  if (window.opener && !window.opener.closed) {
                    window.opener.postMessage({ type: 'ROTEIRO_SAVED' }, '*');
                  } else {
                    console.warn('⚠️ Janela pai não encontrada ou fechada');
                  }
                  
                  setTimeout(() => {
                    status.textContent = '';
                    status.className = 'status';
                  }, 3000);
                } else {
                  throw new Error('Erro ao salvar');
                }
              })
              .catch(error => {
                console.error('Erro ao salvar roteiro:', error);
                status.textContent = '❌ Erro ao salvar';
                status.className = 'status';
              });
            }

            function downloadSRT() {
              const text = editor.value.trim();
              
              if (!text) {
                alert('Por favor, escreva o roteiro antes de gerar o SRT');
                return;
              }
              
              // Preset Máximo CapCut (495 chars)
              const config = {
                maxDuration: 60,
                maxCharsPerLine: 495,
                sentencesPerSegment: 8,
                charsPerSecond: 11,
                pauseBetween: 1,
                safetyMargin: 1.2
              };
              
              // Função para formatar tempo SRT
              function formatTime(seconds) {
                const hours = Math.floor(seconds / 3600);
                const minutes = Math.floor((seconds % 3600) / 60);
                const secs = Math.floor(seconds % 60);
                const ms = Math.floor((seconds % 1) * 1000);
                return String(hours).padStart(2, '0') + ':' + 
                       String(minutes).padStart(2, '0') + ':' + 
                       String(secs).padStart(2, '0') + ',' + 
                       String(ms).padStart(3, '0');
              }
              
              // Helper function: Find safe break point in text (after punctuation, never before)
              function findSafeBreakPoint(text, maxLength) {
                if (text.length <= maxLength) {
                  return text.length;
                }

                // Define punctuation marks that are safe to break AFTER
                // Priority order: period > comma > semicolon > colon > dash
                const punctuationMarks = [
                  { char: '.', priority: 1 },
                  { char: '!', priority: 1 },
                  { char: '?', priority: 1 },
                  { char: ',', priority: 2 },
                  { char: ';', priority: 3 },
                  { char: ':', priority: 3 },
                  { char: '—', priority: 4 },
                  { char: '-', priority: 4 },
                  { char: ')', priority: 5 }
                ];

                // Search backwards from maxLength to find the best break point
                let bestBreakPoint = -1;
                let bestPriority = 999;

                for (let i = maxLength; i >= Math.floor(maxLength * 0.5); i--) {
                  const char = text[i];
                  
                  // Check if this character is a punctuation mark
                  const punctuation = punctuationMarks.find(p => p.char === char);
                  
                  if (punctuation) {
                    // Found a punctuation mark! Check if it's followed by a space (natural break)
                    const nextChar = text[i + 1];
                    
                    if (nextChar === ' ' || nextChar === undefined) {
                      // This is a valid break point (after punctuation + space)
                      if (punctuation.priority < bestPriority) {
                        bestBreakPoint = i + 1; // Break AFTER the punctuation
                        bestPriority = punctuation.priority;
                        
                        // If we found a period/question/exclamation, that's the best - stop searching
                        if (punctuation.priority === 1) {
                          break;
                        }
                      }
                    }
                  }
                }

                // If we found a good break point, use it
                if (bestBreakPoint > 0) {
                  return bestBreakPoint;
                }

                // Fallback: break at last space (but this means no punctuation was found)
                for (let i = maxLength; i >= Math.floor(maxLength * 0.5); i--) {
                  if (text[i] === ' ') {
                    return i;
                  }
                }

                // Last resort: break at maxLength (avoid cutting words)
                return maxLength;
              }
              
              // Split text into sentences
              let sentences = text
                .replace(/([.!?])\\s+/g, '$1\\n')
                .split('\\n')
                .map(s => s.trim())
                .filter(s => s.length > 0);
              
              let subtitles = [];
              let currentTime = 0;
              let subtitleIndex = 1;
              
              for (let i = 0; i < sentences.length; i += config.sentencesPerSegment) {
                const segmentSentences = sentences.slice(i, i + config.sentencesPerSegment);
                let segmentText = segmentSentences.join(' ');
                
                while (segmentText.length > config.maxCharsPerLine) {
                  // Use intelligent break point finder
                  const splitIndex = findSafeBreakPoint(segmentText, config.maxCharsPerLine);
                  
                  const firstPart = segmentText.substring(0, splitIndex).trim();
                  segmentText = segmentText.substring(splitIndex).trim();
                  
                  const duration = Math.min(
                    firstPart.length / config.charsPerSecond * config.safetyMargin,
                    config.maxDuration
                  );
                  
                  subtitles.push({
                    index: subtitleIndex++,
                    start: currentTime,
                    end: currentTime + duration,
                    text: firstPart
                  });
                  
                  currentTime += duration + config.pauseBetween;
                }
                
                if (segmentText.length > 0) {
                  const duration = Math.min(
                    segmentText.length / config.charsPerSecond * config.safetyMargin,
                    config.maxDuration
                  );
                  
                  subtitles.push({
                    index: subtitleIndex++,
                    start: currentTime,
                    end: currentTime + duration,
                    text: segmentText
                  });
                  
                  currentTime += duration + config.pauseBetween;
                }
              }
              
              // Generate SRT content
              const srtText = subtitles.map(sub => 
                sub.index + '\\n' + formatTime(sub.start) + ' --> ' + formatTime(sub.end) + '\\n' + sub.text + '\\n'
              ).join('\\n');
              
              // Download file
              const blob = new Blob([srtText], { type: 'text/plain;charset=utf-8' });
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              
              // Sanitizar título para nome de arquivo (remover caracteres inválidos)
              const sanitizedTitle = videoTitulo
                .replace(/[<>:"\\/\\|?*]/g, '')  // Remover caracteres inválidos
                .replace(/\\s+/g, '_')          // Substituir espaços por underscore
                .substring(0, 100);            // Limitar tamanho
              
              link.download = sanitizedTitle ? (sanitizedTitle + '.srt') : 'darkplanner.srt';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
              
              const status = document.getElementById('status');
              status.textContent = '✅ SRT baixado!';
              status.className = 'status saved';
              setTimeout(() => {
                status.textContent = '';
                status.className = 'status';
              }, 3000);
            }

            function downloadTXT() {
              const text = editor.value;
              
              if (!text.trim()) {
                alert('Por favor, escreva o roteiro antes de baixar o TXT');
                return;
              }
              
              // Download file
              const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              
              // Sanitizar título para nome de arquivo (remover caracteres inválidos)
              const sanitizedTitle = videoTitulo
                .replace(/[<>:"\\/\\|?*]/g, '')  // Remover caracteres inválidos
                .replace(/\\s+/g, '_')          // Substituir espaços por underscore
                .substring(0, 100);            // Limitar tamanho
              
              link.download = sanitizedTitle ? (sanitizedTitle + '.txt') : 'darkplanner.txt';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
              
              const status = document.getElementById('status');
              status.textContent = '✅ TXT baixado!';
              status.className = 'status saved';
              setTimeout(() => {
                status.textContent = '';
                status.className = 'status';
              }, 3000);
            }

            editor.addEventListener('input', function() {
              clearTimeout(saveTimeout);
              saveTimeout = setTimeout(saveRoteiro, 3000);
            });
            
            editor.addEventListener('keydown', function(e) {
              if (e.ctrlKey && e.key === 's') {
                e.preventDefault();
                saveRoteiro();
              }
            });
          </script>
        </body>
      </html>
    `);
    roteiroWindow.document.close();
  };

  const getStatusBadge = (status) => {
    // Mapear status do backend para nomes com acentuação e cores (Design System)
    const statusMap = {
      'planejamento': {
        label: t('channels.planning'),
        bg: 'rgba(59, 130, 246, 0.10)',
        color: 'var(--dp-blue-500, #0DAEFF)',
        border: 'rgba(59, 130, 246, 0.20)'
      },
      'producao': {
        label: t('channels.production'),
        bg: 'var(--dp-orange-bg, rgba(251, 146, 60, 0.10))',
        color: 'var(--dp-orange-500, #FB923C)',
        border: 'rgba(251, 146, 60, 0.20)'
      },
      'finalizado': {
        label: t('channels.finished'),
        bg: 'var(--dp-green-bg, rgba(52, 211, 153, 0.10))',
        color: 'var(--dp-green-500, #34D399)',
        border: 'rgba(52, 211, 153, 0.20)'
      },
      'postado': {
        label: t('channels.scheduled_published'),
        bg: 'var(--dp-purple-bg, rgba(167, 139, 250, 0.10))',
        color: 'var(--dp-purple-500, #A78BFA)',
        border: 'rgba(167, 139, 250, 0.20)'
      }
    };

    const config = statusMap[status] || {
      label: status || 'Sem Status',
      bg: 'var(--dp-bg-card-hover)',
      color: 'var(--dp-text-tertiary)',
      border: 'var(--dp-border-subtle)'
    };
    
    return (
      <span style={{
        background: config.bg,
        color: config.color,
        border: `1px solid ${config.border}`,
        padding: '2px 8px',
        borderRadius: '6px',
        fontSize: '10px',
        fontWeight: '600',
        display: 'inline-block'
      }}>
        {config.label}
      </span>
    );
  };

  // Check in own canals first, then shared canals
  const selectedCanalData = (() => {
    if (!selectedCanal) return null;
    const ownCanal = Array.isArray(canais) ? canais.find(canal => canal.id === selectedCanal) : null;
    if (ownCanal) return ownCanal;
    // Check shared canals from sessionStorage
    try {
      const shared = JSON.parse(sessionStorage.getItem('sidebar-shared-channels') || '[]');
      return shared.find(c => c.id === selectedCanal) || null;
    } catch { return null; }
  })();
  
  // Aplicar busca textual (filtro de status já é feito pelo backend)
  const filteredVideos = searchTerm
    ? videos.filter(v => v.titulo?.toLowerCase().includes(searchTerm.toLowerCase()))
    : videos;

  // Contadores via backend (contam TODOS os vídeos do canal, não só os carregados)
  const totalVideos = videosCounts.total;
  const totalVideosDisplay = totalVideos + (selectedCanalData?.videos_ja_postados || 0);
  const videosPlanejamento = videosCounts.planejamento;
  const videosEmProducao = videosCounts.producao;
  const videosProntos = videosCounts.finalizado;
  const videosPostadosCount = videosCounts.postado;

  // Conta vídeos marcados como "Postado" no sistema + vídeos que já existiam antes
  const videosPostadosNoSistema = videosCounts.postado;
  const videosJaPostadosAntes = selectedCanalData?.videos_ja_postados || 0;
  const videosPostados = videosPostadosNoSistema + videosJaPostadosAntes;
  
  // Contadores para os cards de estatísticas
  // BACKLOG: Vídeos prontos (pronto filter) - não postados
  const videosBacklog = videosProntos;
  
  // AGENDADOS: Vídeos marcados como postado com data futura
  const videosAgendados = videos.filter(video => {
    if (video.checklist_postagem !== 'Publicado' || !video.data_postagem) return false;
    try {
      const [day, month, year] = video.data_postagem.split('/');
      const dataVideo = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      dataVideo.setHours(0, 0, 0, 0);
      return dataVideo.getTime() > hoje.getTime();
    } catch {
      return false;
    }
  }).length;

  // Channel avatar colors
  const channelColors = [
    ['#60A5FA', '#2563EB'],
    ['#F472B6', '#DB2777'],
    ['#34D399', '#059669'],
    ['#FBBF24', '#D97706'],
    ['#A78BFA', '#7C3AED'],
    ['#FB923C', '#EA580C'],
    ['#2DD4BF', '#0D9488'],
    ['#FF6B6B', '#EE5A24'],
    ['#C084FC', '#9333EA'],
    ['#4ADE80', '#16A34A'],
  ];
  const channelIndex = Array.isArray(canais) ? canais.findIndex(c => c.id === selectedCanal) : -1;
  const [avatarColor1, avatarColor2] = channelColors[channelIndex >= 0 ? channelIndex % channelColors.length : 0];

  // Glassmorphism dialog style (Design System)
  const dialogStyle = {
    background: 'var(--dp-bg-glass, rgba(28,28,28,0.85))',
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    border: '1px solid var(--dp-border-medium)',
    borderRadius: '16px',
    boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
    color: 'var(--dp-text-primary)',
    padding: '24px',
    maxHeight: '85vh',
    overflow: 'auto',
  };

  // Consistent input style for modals (Design System)
  const inputStyle = {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid var(--dp-border-subtle)',
    background: 'var(--dp-bg-input, #2E2E2E)',
    color: 'var(--dp-text-primary)',
    fontSize: '13px',
    fontFamily: 'var(--dp-font)',
    outline: 'none',
    transition: 'border-color 0.3s, box-shadow 0.3s',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--dp-text-secondary)',
    fontFamily: 'var(--dp-font)',
    marginBottom: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
        <Sidebar onLogout={onLogout} isAdmin={isAdmin} />
        <Header />
        <main className="dp-main-content" style={{
          marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
          marginTop: '60px', flex: 1, minWidth: 0, transition: 'margin-left 0.3s ease'
        }}>
          {/* Skeleton PageHeader */}
          <div style={{ marginBottom: '24px' }}>
            <div className="dp-skeleton" style={{ width: '200px', height: '24px', marginBottom: '8px' }} />
            <div className="dp-skeleton" style={{ width: '320px', height: '14px' }} />
          </div>
          {/* Skeleton search */}
          <div className="dp-skeleton" style={{ width: '360px', height: '42px', marginBottom: '20px', borderRadius: '10px' }} />
          {/* Skeleton grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: '16px',
          }}>
            {[1,2,3,4,5,6].map(i => (
              <div key={i} style={{
                background: 'var(--dp-bg-card)',
                borderRadius: '14px',
                border: '1px solid var(--dp-border-subtle)',
                padding: '18px',
                overflow: 'hidden',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '14px' }}>
                  <div className="dp-skeleton" style={{ width: '44px', height: '44px', borderRadius: '12px' }} />
                  <div className="dp-skeleton" style={{ width: '52px', height: '20px', borderRadius: '6px' }} />
                </div>
                <div className="dp-skeleton" style={{ width: '70%', height: '16px', marginBottom: '8px' }} />
                <div className="dp-skeleton" style={{ width: '50%', height: '12px', marginBottom: '14px' }} />
                <div style={{ height: '1px', background: 'var(--dp-border-subtle)', margin: '14px 0 12px' }} />
                <div className="dp-skeleton" style={{ width: '100px', height: '14px' }} />
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
      {/* Hidden file input for thumbnail upload */}
      <input
        ref={thumbnailInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
        onChange={(e) => {
          if (e.target.files[0] && thumbnailUploadVideoId) {
            handleThumbnailUpload(thumbnailUploadVideoId, e.target.files[0]);
          }
        }}
      />
      <Sidebar onLogout={onLogout} isAdmin={isAdmin} />
      <Header />

      {/* Main Content */}
      <main className="dp-main-content" style={{
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
        marginTop: '60px',
        flex: 1,
        minWidth: 0,
        transition: 'margin-left 0.3s ease',
      }}>
        {/* Page Header - only show full header on grid view (no channel selected) */}
        {!selectedCanalData ? (
          <PageHeader
            icon={<Clapperboard size={20} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />}
            title={t('channels.title')}
            description={t('channels.subtitle')}
          >
            <button
              className="dp-btn-cta dp-btn-cta--sm"
              onClick={() => setShowNewCanalDialog(true)}
            >
              <Plus size={14} />
              {t('channels.new_channel')}
              <ArrowRight size={14} />
            </button>
          </PageHeader>
        ) : null}

        {/* ======= CHANNEL GRID (No channel selected) ======= */}
        {(canais.length > 0 || JSON.parse(sessionStorage.getItem('sidebar-shared-channels') || '[]').length > 0) && !selectedCanalData && (
          <div style={{ animation: 'dp-fadeInUp 0.3s ease-out both' }}>
            {/* Search bar */}
            <div style={{ position: 'relative', maxWidth: '360px', marginBottom: '20px' }}>
              <Search size={16} style={{
                position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
                color: 'var(--dp-text-tertiary)', pointerEvents: 'none'
              }} />
              <input
                type="text"
                value={channelSearchTerm}
                onChange={(e) => setChannelSearchTerm(e.target.value)}
                placeholder={t('channels.search_channels') || 'Buscar canais...'}
                style={{
                  width: '100%',
                  padding: '10px 14px 10px 38px',
                  borderRadius: '10px',
                  border: '1px solid var(--dp-border-subtle)',
                  background: 'var(--dp-bg-card)',
                  color: 'var(--dp-text-primary)',
                  fontSize: '13px',
                  fontFamily: 'var(--dp-font)',
                  outline: 'none',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--dp-blue-500)';
                  e.target.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.12)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--dp-border-subtle)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Pending Invitations Banner */}
            {(() => {
              try {
                const pendentes = JSON.parse(sessionStorage.getItem('sidebar-shared-channels') || '[]')
                  .filter(c => c.comp_status === 'pendente');
                if (pendentes.length === 0) return null;
                return (
                  <div style={{
                    marginBottom: '20px', padding: '16px 20px', borderRadius: '14px',
                    background: 'rgba(139,92,246,0.06)',
                    border: '1px solid rgba(139,92,246,0.2)',
                  }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px',
                      fontSize: '14px', fontWeight: 600, color: '#8B5CF6',
                    }}>
                      <Users size={16} />
                      {t('sharing.pending_invites', { count: pendentes.length })}
                    </div>
                    {pendentes.map(canal => (
                      <div key={canal.id} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '10px 14px', borderRadius: '10px', marginBottom: '8px',
                        background: 'var(--dp-bg-card)',
                        border: '1px solid var(--dp-border-subtle)',
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--dp-text-primary)' }}>
                            {canal.nome}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)', marginTop: '2px' }}>
                            {t('sharing.shared_by', { name: canal.shared_by_name || canal.shared_by })}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                          <button
                            onClick={() => handleAceitarConvite(canal.comp_id)}
                            style={{
                              padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
                              background: 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)', color: '#fff',
                              border: 'none', cursor: 'pointer', transition: 'opacity 0.15s',
                            }}
                            onMouseEnter={(e) => e.target.style.opacity = '0.85'}
                            onMouseLeave={(e) => e.target.style.opacity = '1'}
                          >
                            {t('sharing.accept')}
                          </button>
                          <button
                            onClick={() => handleRecusarConvite(canal.comp_id)}
                            style={{
                              padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 500,
                              background: 'transparent', color: 'var(--dp-text-secondary)',
                              border: '1px solid var(--dp-border-medium, rgba(255,255,255,0.1))',
                              cursor: 'pointer', transition: 'all 0.15s',
                            }}
                            onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.05)'}
                            onMouseLeave={(e) => e.target.style.background = 'transparent'}
                          >
                            {t('sharing.decline')}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              } catch { return null; }
            })()}

            {/* Channel Cards Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: '16px',
            }}>
              {(() => {
                const sharedCanais = (() => { try { return JSON.parse(sessionStorage.getItem('sidebar-shared-channels') || '[]'); } catch { return []; } })();
                return [...canais, ...sharedCanais.filter(sc => sc.comp_status !== 'pendente')];
              })().filter(c => !c.is_archived)
                .filter(canal => {
                  if (!channelSearchTerm.trim()) return true;
                  const search = channelSearchTerm.toLowerCase();
                  return (canal.nome || '').toLowerCase().includes(search) ||
                         (canal.nome_canal || '').toLowerCase().includes(search) ||
                         (canal.idioma || '').toLowerCase().includes(search);
                })
                .sort((a, b) => {
                  const nameA = a.nome.toLowerCase();
                  const nameB = b.nome.toLowerCase();
                  const numA = nameA.match(/^\d+/);
                  const numB = nameB.match(/^\d+/);
                  if (numA && numB) {
                    const diff = parseInt(numA[0]) - parseInt(numB[0]);
                    if (diff !== 0) return diff;
                  }
                  return nameA.localeCompare(nameB, 'pt-BR');
                })
                .map((canal, index) => {
                  // Avatar gradient colors based on channel name
                  const hash = (canal.nome || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
                  const hue1 = hash % 360;
                  const hue2 = (hash * 7) % 360;
                  const videoCount = (canal.video_count || 0) + (canal.videos_ja_postados || 0);
                  // Bandeira: idioma detectado no vínculo, ou campo do usuário mapeado
                  const canalFlagIso = getCanalFlagIso(canal);

                  return (
                    <div
                      key={canal.id}
                      onClick={() => {
                        setSelectedCanal(canal.id);
                        localStorage.setItem('selectedCanal', canal.id);
                        setChannelSearchTerm('');
                      }}
                      style={{
                        background: 'var(--dp-bg-card)',
                        borderRadius: '14px',
                        border: '1px solid var(--dp-border-subtle)',
                        cursor: 'pointer',
                        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                        overflow: 'hidden',
                        animation: `dp-card-stagger 0.4s ease ${index * 50}ms both`,
                        position: 'relative',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = 'var(--dp-blue-500)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 24px rgba(13,174,255,0.12)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'var(--dp-border-subtle)';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      {/* Barra superior PADRÃO (= Text to Speech) — canais próprios e
                          compartilhados iguais; o badge inline já distingue compartilhado. */}
                      <div style={{
                        height: '2px',
                        background: 'linear-gradient(90deg, #0DAEFF, #3B82F6)',
                        opacity: 0.6,
                      }} />

                      {/* Shared badge moved inline into status badges row */}

                      <div style={{ padding: '18px' }}>
                        {/* Header compacto (2026-06): avatar+bandeira | nome+nicho | badges */}
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' }}>
                          {/* Avatar com bandeira do idioma sobreposta (padrão Niche Finder) */}
                          <div style={{ position: 'relative', flexShrink: 0 }}>
                            {canal.channel_thumbnail && !channelThumbErrors[canal.id] && !isPrivate ? (
                              <img
                                src={canal.channel_thumbnail}
                                alt={canal.nome}
                                style={{ width: '44px', height: '44px', borderRadius: '12px', objectFit: 'cover', display: 'block' }}
                                onError={() => setChannelThumbErrors(prev => ({ ...prev, [canal.id]: true }))}
                              />
                            ) : (
                              <div style={{
                                width: '44px', height: '44px', borderRadius: '12px',
                                background: `linear-gradient(135deg, hsl(${hue1}, 60%, 50%), hsl(${hue2}, 60%, 45%))`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '18px', fontWeight: 800, color: '#fff',
                                fontFamily: 'var(--dp-font)',
                              }}>
                                {isPrivate ? '?' : (canal.nome?.charAt(0)?.toUpperCase() || '?')}
                              </div>
                            )}
                            {!isPrivate && canalFlagIso && (
                              <div style={{ position: 'absolute', top: '-4px', right: '-4px' }} onClick={(e) => e.stopPropagation()}>
                                <DarkTooltip content={canal.language_detected ? getLanguageName(canal.language_detected, t) : canal.idioma}>
                                  <span style={{
                                    width: '18px', height: '18px', borderRadius: '50%',
                                    background: 'var(--dp-bg-card)', border: '2px solid var(--dp-bg-card)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    boxShadow: '0 0 0 0.5px var(--dp-border-medium)', cursor: 'default',
                                  }}>
                                    <FlagIcon code={getCountryCode(canalFlagIso)} size="12px" rounded />
                                  </span>
                                </DarkTooltip>
                              </div>
                            )}
                          </div>

                          {/* Nome + nicho detectado */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <h3 style={{
                              fontSize: '14px', fontWeight: 700, color: 'var(--dp-text-primary)',
                              margin: '0', fontFamily: 'var(--dp-font)',
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>
                              {isPrivate ? '••••••••••' : canal.nome}
                            </h3>
                            {!isPrivate && canal.nicho_detectado && (
                              <p style={{
                                fontSize: '11px', color: 'var(--dp-text-tertiary)', margin: '3px 0 0 0',
                                fontFamily: 'var(--dp-font)',
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                              }}>
                                {canal.nicho_detectado}
                              </p>
                            )}
                            {canal._is_shared && canal.shared_by && (
                              <p style={{
                                fontSize: '11px', color: '#8B5CF6', margin: '3px 0 0 0',
                                fontFamily: 'var(--dp-font)', display: 'flex', alignItems: 'center', gap: '4px',
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                              }}>
                                <Users size={10} />
                                {canal.shared_by_name || canal.shared_by}
                              </p>
                            )}
                          </div>

                          {/* Badges: Editor | YouTube (só ícone) | Ativo (sem bolinha) */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                            {canal._is_shared && (
                              <div style={{
                                display: 'inline-flex', alignItems: 'center', gap: '4px',
                                padding: '3px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: 600,
                                background: 'rgba(139,92,246,0.12)', color: '#8B5CF6',
                                border: '1px solid rgba(139,92,246,0.2)',
                              }}>
                                <Users size={10} /> Editor
                              </div>
                            )}
                            {!isPrivate && canal.is_youtube_linked && (
                              canal.youtube_channel_url ? (
                                <a
                                  href={canal.youtube_channel_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  title="Abrir canal no YouTube"
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    padding: '4px 6px', borderRadius: '6px',
                                    background: 'linear-gradient(135deg, #FF0000, #CC0000)', color: '#fff',
                                    textDecoration: 'none', cursor: 'pointer',
                                    transition: 'transform 0.2s, opacity 0.2s',
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.opacity = '0.9'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.opacity = '1'; }}
                                >
                                  <Youtube size={11} />
                                </a>
                              ) : (
                                <div style={{
                                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                  padding: '4px 6px', borderRadius: '6px',
                                  background: 'linear-gradient(135deg, #FF0000, #CC0000)', color: '#fff',
                                }}>
                                  <Youtube size={11} />
                                </div>
                              )
                            )}
                            {canal.is_archived ? (
                              <div style={{
                                display: 'inline-flex', alignItems: 'center', gap: '5px',
                                padding: '3px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: 600,
                                background: 'rgba(156,163,175,0.12)', color: 'var(--dp-text-tertiary)',
                                border: '1px solid rgba(156,163,175,0.2)',
                              }}>
                                <Archive size={10} />
                                Arquivado
                              </div>
                            ) : (
                              <div style={{
                                display: 'inline-flex', alignItems: 'center',
                                padding: '3px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: 600,
                                background: 'rgba(52,211,153,0.12)', color: 'var(--dp-green-500, #34D399)',
                                border: '1px solid rgba(52,211,153,0.2)',
                              }}>
                                {t('channels.status_active') || 'Ativo'}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Slug / identifier */}
                        {canal.nome_canal && (
                          <p style={{
                            fontSize: '12px', color: 'var(--dp-text-tertiary)',
                            margin: '0 0 2px 0', fontFamily: 'var(--dp-font)',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          }}>
                            {isPrivate ? '•••••' : `@${canal.nome_canal}`}
                          </p>
                        )}

                        {/* Separator */}
                        <div style={{
                          height: '1px', background: 'var(--dp-border-subtle)',
                          margin: '14px 0 12px 0',
                        }} />

                        {/* YouTube stats do banco (sem chamada ao YouTube ao abrir;
                            atualiza só pelo botão de refresh na página do canal) */}
                        {!isPrivate && canal.is_youtube_linked && (
                          <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
                            {[
                              { icon: <Users size={13} />, value: canal.youtube_subscribers, label: 'Inscritos' },
                              { icon: <Eye size={13} />, value: canal.youtube_total_views, label: 'Views' },
                              { icon: <Video size={13} />, value: canal.youtube_video_count, label: 'Vídeos' },
                            ].map((item, idx) => (
                              <div key={idx} style={{
                                flex: 1, textAlign: 'center',
                                background: 'rgba(0, 0, 0, 0.25)',
                                borderRadius: '8px', padding: '8px 4px',
                              }}>
                                <div style={{ color: '#06B6D4', marginBottom: '3px', display: 'flex', justifyContent: 'center' }}>
                                  {item.icon}
                                </div>
                                <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)', lineHeight: 1.1 }}>
                                  {formatNumber(item.value)}
                                </div>
                                <div style={{ fontSize: '9px', color: 'var(--dp-text-tertiary)', marginTop: '2px', fontFamily: 'var(--dp-font)' }}>
                                  {item.label}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Status dos videos internos (sugestao de usuario): quantos
                            em producao e finalizados, direto no card — evita abrir
                            cada canal so pra ver o andamento. Cores dos status. */}
                        <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
                          <div style={{
                            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                            background: 'rgba(251, 146, 60, 0.08)', border: '1px solid rgba(251, 146, 60, 0.18)',
                            borderRadius: '8px', padding: '6px 4px',
                          }}>
                            <PlayCircle size={13} style={{ color: 'var(--dp-orange-500, #FB923C)' }} />
                            <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>
                              {isPrivate ? '•' : (canal.videos_producao || 0)}
                            </span>
                            <span style={{ fontSize: '10px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)' }}>
                              {t('channels.in_production_short') || 'em produção'}
                            </span>
                          </div>
                          <div style={{
                            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                            background: 'rgba(52, 211, 153, 0.08)', border: '1px solid rgba(52, 211, 153, 0.18)',
                            borderRadius: '8px', padding: '6px 4px',
                          }}>
                            <CheckCircle size={13} style={{ color: 'var(--dp-green-500, #34D399)' }} />
                            <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>
                              {isPrivate ? '•' : (canal.videos_finalizado || 0)}
                            </span>
                            <span style={{ fontSize: '10px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)' }}>
                              {t('channels.finished_short') || 'finalizados'}
                            </span>
                          </div>
                        </div>

                        {/* Footer: video count */}
                        <div style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          fontSize: '12px', color: 'var(--dp-text-tertiary)',
                        }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <Clapperboard size={13} style={{ color: 'var(--dp-blue-500)' }} />
                            {isPrivate ? '•' : videoCount} {t('channels.titles_label') || 'títulos'}
                          </span>
                          {canal.frequencia_postagem && (
                            <span style={{
                              fontSize: '10px', padding: '2px 6px', borderRadius: '4px',
                              background: 'rgba(13,174,255,0.08)', color: 'var(--dp-blue-400)',
                              border: '1px solid rgba(13,174,255,0.15)',
                            }}>
                              {isPrivate ? '•••' : canal.frequencia_postagem}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              }
            </div>

            {/* ======= ARCHIVED CHANNELS SECTION ======= */}
            {canais.filter(c => c.is_archived).length > 0 && (
              <div style={{ marginTop: '32px' }}>
                <button
                  onClick={() => setShowArchivedSection(!showArchivedSection)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    background: 'none', border: 'none', cursor: 'pointer',
                    padding: '8px 0', width: '100%',
                    fontSize: '13px', fontWeight: 600,
                    color: 'var(--dp-text-tertiary)',
                    fontFamily: 'var(--dp-font)',
                  }}
                >
                  <Archive size={16} />
                  Canais Arquivados ({canais.filter(c => c.is_archived).length})
                  <ChevronDown size={14} style={{
                    transition: 'transform 0.2s',
                    transform: showArchivedSection ? 'rotate(180deg)' : 'rotate(0deg)',
                  }} />
                </button>

                {showArchivedSection && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                    gap: '16px', marginTop: '12px', opacity: 0.7,
                  }}>
                    {canais.filter(c => c.is_archived)
                      .sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'))
                      .map((canal, index) => {
                        const hash = (canal.nome || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
                        const hue1 = hash % 360;
                        const hue2 = (hash * 7) % 360;
                        const videoCount = (canal.video_count || 0) + (canal.videos_ja_postados || 0);
                        return (
                          <div
                            key={canal.id}
                            onClick={() => { setSelectedCanal(canal.id); localStorage.setItem('selectedCanal', canal.id); }}
                            style={{
                              background: 'var(--dp-bg-card)', borderRadius: '14px',
                              border: '1px solid var(--dp-border-subtle)', cursor: 'pointer',
                              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                              overflow: 'hidden', position: 'relative',
                              animation: `dp-card-stagger 0.4s ease ${index * 50}ms both`,
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--dp-text-tertiary)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                          >
                            <div style={{ height: '3px', background: 'var(--dp-border-subtle)' }} />
                            <div style={{ padding: '18px' }}>
                              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '14px' }}>
                                <div style={{
                                  width: '44px', height: '44px', borderRadius: '12px',
                                  background: `linear-gradient(135deg, hsl(${hue1}, 40%, 45%), hsl(${hue2}, 40%, 40%))`,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: '18px', fontWeight: 800, color: '#fff', flexShrink: 0,
                                  fontFamily: 'var(--dp-font)', filter: 'grayscale(0.3)',
                                }}>
                                  {isPrivate ? '?' : (canal.nome?.charAt(0)?.toUpperCase() || '?')}
                                </div>
                                <div style={{
                                  display: 'inline-flex', alignItems: 'center', gap: '5px',
                                  padding: '3px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: 600,
                                  background: 'rgba(156,163,175,0.12)', color: 'var(--dp-text-tertiary)',
                                  border: '1px solid rgba(156,163,175,0.2)',
                                }}>
                                  <Archive size={10} />
                                  Arquivado
                                </div>
                              </div>
                              <h3 style={{
                                fontSize: '15px', fontWeight: 700, color: 'var(--dp-text-primary)',
                                margin: '0 0 4px 0', fontFamily: 'var(--dp-font)',
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                              }}>
                                {isPrivate ? '••••••••••' : canal.nome}
                              </h3>
                              {canal.nome_canal && (
                                <p style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)', margin: '0 0 2px 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {isPrivate ? '•••••' : `@${canal.nome_canal}`}
                                </p>
                              )}
                              <div style={{ height: '1px', background: 'var(--dp-border-subtle)', margin: '14px 0 12px 0' }} />
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px', color: 'var(--dp-text-tertiary)' }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                  <Clapperboard size={13} style={{ color: 'var(--dp-text-tertiary)' }} />
                                  {isPrivate ? '•' : videoCount} {t('channels.titles_label') || 'títulos'}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    }
                  </div>
                )}
              </div>
            )}

            {/* Filtered empty state */}
            {channelSearchTerm.trim() && canais.filter(c => {
              const s = channelSearchTerm.toLowerCase();
              return (c.nome || '').toLowerCase().includes(s) || (c.nome_canal || '').toLowerCase().includes(s);
            }).length === 0 && (
              <div style={{
                textAlign: 'center', padding: '48px 24px',
                color: 'var(--dp-text-tertiary)', fontSize: '14px',
              }}>
                <Search size={32} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
                <p style={{ margin: 0 }}>{t('channels.no_search_results') || 'Nenhum canal encontrado para esta busca'}</p>
              </div>
            )}
          </div>
        )}

        {/* ======= EMPTY STATE (No channels) ======= */}
        {canais.length === 0 && !selectedCanal && JSON.parse(sessionStorage.getItem('sidebar-shared-channels') || '[]').filter(c => c.comp_status === 'aceito').length === 0 && !loading && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: '80px 24px', textAlign: 'center',
          }}>
            <div style={{
              width: '72px', height: '72px', borderRadius: '20px',
              background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: '20px',
            }}>
              <Tv2 size={32} style={{ color: 'var(--dp-text-tertiary)', opacity: 0.5 }} />
            </div>
            <h3 style={{
              fontSize: '18px', fontWeight: 700, color: 'var(--dp-text-primary)',
              margin: '0 0 8px 0', fontFamily: 'var(--dp-font)',
            }}>
              {t('channels.no_channels_title') || 'Você ainda não tem canais cadastrados'}
            </h3>
            <p style={{
              fontSize: '14px', color: 'var(--dp-text-tertiary)',
              margin: '0 0 24px 0', maxWidth: '400px',
            }}>
              {t('channels.no_channels_desc') || 'Crie seu primeiro canal para começar a gerenciar seus vídeos do YouTube'}
            </p>
            <button
              onClick={() => setShowNewCanalDialog(true)}
              style={{
                background: 'linear-gradient(135deg, var(--dp-blue-500, #0DAEFF), #3B82F6)',
                color: 'white', border: 'none', padding: '12px 24px',
                borderRadius: '10px', fontSize: '14px', fontWeight: 600,
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
                fontFamily: 'var(--dp-font)', transition: 'all 0.2s ease',
              }}
            >
              <Plus size={16} />
              {t('channels.create_first_channel') || 'Criar Primeiro Canal'}
            </button>
          </div>
        )}

            {/* Custom Buttons Config Dialog */}
            <Dialog open={showCustomButtonsDialog} onOpenChange={setShowCustomButtonsDialog}>
              <DialogContent style={{ ...dialogStyle, maxWidth: '640px' }}
                onPointerDownOutside={(e) => {
                  const target = e.target;
                  if (target?.closest?.('[role="listbox"]') || target?.closest?.('[role="option"]')) {
                    e.preventDefault();
                  }
                }}
                onInteractOutside={(e) => {
                  const target = e.target;
                  if (target?.closest?.('[role="listbox"]') || target?.closest?.('[role="option"]')) {
                    e.preventDefault();
                  }
                }}
              >
                <DialogHeader>
                  <DialogTitle style={{
                    fontSize: '18px', fontWeight: 700,
                    color: 'var(--dp-text-primary)',
                    fontFamily: 'var(--dp-font)', margin: 0,
                    display: 'flex', alignItems: 'center', gap: '10px',
                  }}>
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '10px',
                      background: 'rgba(167, 139, 250, 0.12)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Settings size={16} style={{ color: '#A78BFA' }} />
                    </div>
                    {t('channels.configure_shortcuts')}
                  </DialogTitle>
                  <p style={{ fontSize: '13px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)', margin: '8px 0 0 0' }}>
                    {t('channels.shortcuts_subtitle')}
                  </p>
                </DialogHeader>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '12px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
                    {customButtons.map((button, index) => (
                      <div key={button.id} style={{
                        padding: '14px',
                        borderRadius: '10px',
                        border: '1px solid var(--dp-border-subtle)',
                        background: 'var(--dp-bg-elevated, rgba(255,255,255,0.03))',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      }}>
                        <span style={labelStyle}>
                          {t('channels.button')} {index + 1}
                        </span>
                        <input
                          placeholder={t('channels.button_name_placeholder')}
                          value={button.name}
                          onChange={(e) => {
                            const newButtons = [...customButtons];
                            newButtons[index].name = e.target.value;
                            setCustomButtons(newButtons);
                          }}
                          style={inputStyle}
                          onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.1)'; }}
                          onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.boxShadow = 'none'; }}
                        />
                        <input
                          placeholder={t('channels.button_url_placeholder')}
                          value={button.url}
                          onChange={(e) => {
                            const newButtons = [...customButtons];
                            newButtons[index].url = e.target.value;
                            setCustomButtons(newButtons);
                          }}
                          style={inputStyle}
                          onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.1)'; }}
                          onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.boxShadow = 'none'; }}
                        />
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '10px', paddingTop: '12px', borderTop: '1px solid var(--dp-border-subtle)' }}>
                    <button
                      type="button"
                      onClick={() => setShowCustomButtonsDialog(false)}
                      style={{
                        flex: 1, padding: '10px 16px', background: 'transparent',
                        border: '1px solid var(--dp-border-subtle)', borderRadius: '10px',
                        color: 'var(--dp-text-secondary)', fontSize: '13px', fontWeight: 500,
                        fontFamily: 'var(--dp-font)', cursor: 'pointer', transition: 'all 0.2s',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dp-bg-elevated)'; e.currentTarget.style.borderColor = 'var(--dp-border-medium)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; }}
                    >
                      <X size={14} />
                      {t('channels.cancel')}
                    </button>
                    <button
                      type="button"
                      onClick={saveCustomButtons}
                      style={{
                        flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                        padding: '10px 16px', background: 'linear-gradient(135deg, var(--dp-blue-500), #3B82F6)',
                        border: 'none', borderRadius: '10px', color: '#fff', fontSize: '13px', fontWeight: 600,
                        fontFamily: 'var(--dp-font)', cursor: 'pointer', transition: 'all 0.3s',
                      }}
                    >
                      <Save size={14} />
                      {t('channels.save_buttons')}
                    </button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* New Canal Dialog */}
            <Dialog open={showNewCanalDialog} onOpenChange={(open) => { setShowNewCanalDialog(open); if (!open) resetYoutubeStates(); }}>
              <DialogContent style={{ ...dialogStyle, maxWidth: '560px', maxHeight: '85vh', overflowY: 'auto' }}
                onPointerDownOutside={(e) => {
                  const target = e.target;
                  if (target?.closest?.('[role="listbox"]') || target?.closest?.('[role="option"]')) {
                    e.preventDefault();
                  }
                }}
                onInteractOutside={(e) => {
                  const target = e.target;
                  if (target?.closest?.('[role="listbox"]') || target?.closest?.('[role="option"]')) {
                    e.preventDefault();
                  }
                }}
              >
                {/* === IMPORT RESULT STATE === */}
                {showImportResult ? (
                  <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <div style={{
                      width: '56px', height: '56px', borderRadius: '16px',
                      background: 'rgba(52,211,153,0.12)', margin: '0 auto 16px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <CheckCircle size={28} style={{ color: 'var(--dp-green-500, #34D399)' }} />
                    </div>
                    <h3 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--dp-text-primary)', margin: '0 0 8px 0', fontFamily: 'var(--dp-font)' }}>
                      Canal importado com sucesso!
                    </h3>
                    <p style={{ fontSize: '14px', color: 'var(--dp-text-secondary)', margin: '0 0 4px 0' }}>
                      {showImportResult.canal_nome}
                    </p>
                    <p style={{ fontSize: '13px', color: 'var(--dp-text-tertiary)', margin: '0 0 24px 0' }}>
                      {showImportResult.videos_imported} vídeos importados
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCanal(showImportResult.canal_id);
                        localStorage.setItem('selectedCanal', showImportResult.canal_id);
                        setShowNewCanalDialog(false);
                        resetYoutubeStates();
                        fetchVideos();
                      }}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '8px',
                        padding: '10px 24px', background: 'linear-gradient(135deg, var(--dp-blue-500), #3B82F6)',
                        border: 'none', borderRadius: '10px', color: '#fff', fontSize: '14px', fontWeight: 600,
                        fontFamily: 'var(--dp-font)', cursor: 'pointer',
                      }}
                    >
                      Ir para o Canal
                    </button>
                  </div>
                ) : youtubeImporting ? (
                  /* === IMPORTING STATE === */
                  <div style={{ textAlign: 'center', padding: '30px 0' }}>
                    <div style={{
                      width: '56px', height: '56px', borderRadius: '16px',
                      background: 'rgba(13,174,255,0.12)', margin: '0 auto 16px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <RefreshCw size={24} style={{ color: 'var(--dp-blue-500)', animation: 'spin 1s linear infinite' }} />
                    </div>
                    <h3 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--dp-text-primary)', margin: '0 0 8px 0', fontFamily: 'var(--dp-font)' }}>
                      Importando canal...
                    </h3>
                    <p style={{ fontSize: '13px', color: 'var(--dp-text-tertiary)', margin: '0 0 16px 0' }}>
                      Buscando e importando vídeos do YouTube. Isso pode levar alguns instantes.
                    </p>
                    {/* Progress bar */}
                    <div style={{
                      width: '100%', height: '6px', borderRadius: '3px',
                      background: 'var(--dp-bg-elevated)', overflow: 'hidden',
                    }}>
                      <div style={{
                        width: '60%', height: '100%', borderRadius: '3px',
                        background: 'linear-gradient(90deg, var(--dp-blue-500), #3B82F6)',
                        animation: 'dp-progress-pulse 1.5s ease-in-out infinite',
                      }} />
                    </div>
                  </div>
                ) : (
                  /* === NORMAL FORM STATE === */
                  <>
                <DialogHeader>
                  <DialogTitle style={{
                    fontSize: '18px', fontWeight: 700,
                    color: 'var(--dp-text-primary)',
                    fontFamily: 'var(--dp-font)', margin: 0,
                    display: 'flex', alignItems: 'center', gap: '10px',
                  }}>
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '10px',
                      background: 'rgba(13, 174, 255, 0.12)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Plus size={16} style={{ color: 'var(--dp-blue-500)' }} />
                    </div>
                    {t('channels.create_new_channel')}
                  </DialogTitle>
                  <p style={{ fontSize: '13px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)', margin: '8px 0 0 0' }}>
                    {t('channels.fill_info')}
                  </p>
                </DialogHeader>

                {/* ===== YOUTUBE IMPORT SECTION ===== */}
                <div style={{
                  margin: '12px 0 4px 0', padding: '14px',
                  borderRadius: '12px', border: '1px solid rgba(255,0,0,0.15)',
                  background: 'rgba(255,0,0,0.03)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <Youtube size={16} style={{ color: '#FF0000' }} />
                    <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>
                      Importar do YouTube
                    </span>
                    <span style={{ fontSize: '10px', color: 'var(--dp-text-tertiary)', fontStyle: 'italic' }}>(opcional)</span>
                  </div>

                  {/* Aviso de API Key removido (Fase 4): import usa o hub global */}
                  {youtubeChannel ? (
                    /* Channel Found Result */
                    <div>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: '12px', padding: '12px',
                        borderRadius: '10px', background: 'var(--dp-bg-card)',
                        border: '1px solid var(--dp-border-subtle)', marginBottom: '10px',
                      }}>
                        <img
                          src={youtubeChannel.thumbnail}
                          alt={youtubeChannel.name}
                          style={{ width: '48px', height: '48px', borderRadius: '12px', objectFit: 'cover' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>
                            {youtubeChannel.name}
                          </div>
                          {youtubeChannel.custom_url && (
                            <div style={{ fontSize: '11px', color: 'var(--dp-text-tertiary)' }}>{youtubeChannel.custom_url}</div>
                          )}
                          <div style={{ display: 'flex', gap: '12px', marginTop: '4px', fontSize: '11px', color: 'var(--dp-text-secondary)' }}>
                            <span>📺 {youtubeChannel.video_count} vídeos</span>
                            <span>👥 {youtubeChannel.subscribers >= 1000 ? `${(youtubeChannel.subscribers / 1000).toFixed(1)}K` : youtubeChannel.subscribers} inscritos</span>
                          </div>
                        </div>
                        <div style={{
                          padding: '3px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: 600,
                          background: 'rgba(52,211,153,0.12)', color: 'var(--dp-green-500, #34D399)',
                        }}>
                          ✓ Encontrado
                        </div>
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--dp-text-tertiary)', marginBottom: '10px' }}>
                        Serão importados: nome, thumbnail e {youtubeChannel.video_count} vídeos.
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button type="button"
                          onClick={() => {
                            setNewCanal({ ...newCanal, nome: youtubeChannel.name });
                            importYoutubeChannel(youtubeChannel);
                          }}
                          style={{
                            flex: 1, padding: '8px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
                            background: 'linear-gradient(135deg, #FF0000, #CC0000)',
                            color: '#fff', border: 'none', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                          }}>
                          <Youtube size={14} /> Confirmar e Importar
                        </button>
                        <button type="button" onClick={() => { setYoutubeChannel(null); setYoutubeUrl(''); }}
                          style={{
                            padding: '8px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 500,
                            background: 'transparent', color: 'var(--dp-text-secondary)',
                            border: '1px solid var(--dp-border-subtle)', cursor: 'pointer',
                          }}>
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* URL Input + Search Button */
                    <div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                          value={youtubeUrl}
                          onChange={(e) => setYoutubeUrl(e.target.value)}
                          placeholder="https://youtube.com/@seucanal"
                          style={{ ...inputStyle, flex: 1 }}
                          onFocus={(e) => { e.currentTarget.style.borderColor = '#FF0000'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(255,0,0,0.08)'; }}
                          onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.boxShadow = 'none'; }}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (youtubeUrl.trim()) searchYoutubeChannel(youtubeUrl); } }}
                        />
                        <button type="button"
                          onClick={() => youtubeUrl.trim() && searchYoutubeChannel(youtubeUrl)}
                          disabled={youtubeSearching || !youtubeUrl.trim()}
                          style={{
                            padding: '0 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
                            background: youtubeUrl.trim() ? 'linear-gradient(135deg, #FF0000, #CC0000)' : 'var(--dp-bg-elevated)',
                            color: youtubeUrl.trim() ? '#fff' : 'var(--dp-text-tertiary)',
                            border: 'none', cursor: youtubeUrl.trim() ? 'pointer' : 'not-allowed',
                            display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap',
                          }}>
                          {youtubeSearching ? <RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Search size={13} />}
                          Buscar
                        </button>
                      </div>
                      <p style={{ fontSize: '11px', color: 'var(--dp-text-tertiary)', margin: '6px 0 0 0' }}>
                        Cole a URL do seu canal para importar automaticamente nome, thumbnail e todos os vídeos publicados.
                      </p>
                    </div>
                  )}
                </div>

                {/* Separator */}
                {!youtubeChannel && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '4px 0' }}>
                    <div style={{ flex: 1, height: '1px', background: 'var(--dp-border-subtle)' }} />
                    <span style={{ fontSize: '11px', color: 'var(--dp-text-tertiary)', whiteSpace: 'nowrap' }}>ou preencha manualmente</span>
                    <div style={{ flex: 1, height: '1px', background: 'var(--dp-border-subtle)' }} />
                  </div>
                )}

                {/* ===== MANUAL FORM (hidden when YouTube channel found) ===== */}
                {!youtubeChannel && (
                <form onSubmit={handleCreateCanal} style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '8px' }}>
                  <div>
                    <span style={labelStyle}>{t('channels.channel_name')}</span>
                    <input
                      id="nome"
                      value={newCanal.nome}
                      onChange={(e) => setNewCanal({...newCanal, nome: e.target.value})}
                      required
                      placeholder={t('channels.channel_name')}
                      style={inputStyle}
                      onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.1)'; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.boxShadow = 'none'; }}
                    />
                  </div>
                  {/* 2-column: Idioma + Email */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <span style={labelStyle}>{t('channels.language')}</span>
                      <CustomDropdown
                        value={newCanal.idioma}
                        onChange={(val) => setNewCanal({...newCanal, idioma: val})}
                        options={[
                          { value: 'Português', label: 'Português' },
                          { value: 'Inglês', label: 'Inglês' },
                          { value: 'Espanhol', label: 'Espanhol' },
                          { value: 'Francês', label: 'Francês' },
                          { value: 'Alemão', label: 'Alemão' },
                          { value: 'Italiano', label: 'Italiano' },
                          { value: 'Japonês', label: 'Japonês' },
                          { value: 'Coreano', label: 'Coreano' },
                          { value: 'Chinês', label: 'Chinês' },
                          { value: 'Russo', label: 'Russo' },
                          { value: 'Árabe', label: 'Árabe' },
                          { value: 'Hindi', label: 'Hindi' },
                          { value: 'Turco', label: 'Turco' },
                          { value: 'Holandês', label: 'Holandês' },
                          { value: 'Sueco', label: 'Sueco' },
                          { value: 'Polonês', label: 'Polonês' },
                        ]}
                        placeholder="Selecionar idioma"
                      />
                    </div>
                    <div>
                      <span style={labelStyle}>{t('channels.email')}</span>
                      <input
                        id="email"
                        type="email"
                        value={newCanal.email}
                        onChange={(e) => setNewCanal({...newCanal, email: e.target.value})}
                        required
                        placeholder="email@exemplo.com"
                        style={inputStyle}
                        onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.1)'; }}
                        onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.boxShadow = 'none'; }}
                      />
                    </div>
                  </div>
                  <div>
                    <span style={labelStyle}>{t('channels.posting_time')}</span>
                    <div style={{ maxHeight: '120px', overflowY: 'auto', marginTop: '4px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: '8px' }}>
                        {newCanal.horario_postagem.map((horario, index) => (
                          <div key={index} style={{ position: 'relative' }}>
                            <input
                              type="time"
                              value={horario}
                              onChange={(e) => {
                                const newHorarios = [...newCanal.horario_postagem];
                                newHorarios[index] = e.target.value;
                                setNewCanal({...newCanal, horario_postagem: newHorarios});
                              }}
                              style={{ ...inputStyle, paddingRight: '28px', height: '38px' }}
                              onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.1)'; }}
                              onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.boxShadow = 'none'; }}
                            />
                            {newCanal.horario_postagem.length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const newHorarios = newCanal.horario_postagem.filter((_, i) => i !== index);
                                  setNewCanal({...newCanal, horario_postagem: newHorarios});
                                }}
                                style={{
                                  position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)',
                                  width: '22px', height: '22px', borderRadius: '4px', border: 'none',
                                  background: 'transparent', color: '#F87171', cursor: 'pointer',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}
                              >
                                <X size={12} />
                              </button>
                            )}
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => {
                            setNewCanal({...newCanal, horario_postagem: [...newCanal.horario_postagem, '19:00']});
                          }}
                          style={{
                            height: '38px', borderRadius: '8px',
                            background: 'rgba(13, 174, 255, 0.08)', border: '1px solid rgba(13, 174, 255, 0.20)',
                            color: 'var(--dp-blue-500)', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'all 0.2s',
                          }}
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                  {/* 2-column: Frequência + Vídeos Já Postados */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <span style={labelStyle}>{t('channels.posting_frequency')}</span>
                      <input
                        id="frequencia"
                        value={newCanal.frequencia_postagem}
                        onChange={(e) => setNewCanal({...newCanal, frequencia_postagem: e.target.value})}
                        placeholder="3x por semana..."
                        style={inputStyle}
                        onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.1)'; }}
                        onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.boxShadow = 'none'; }}
                      />
                    </div>
                    <div>
                      <span style={labelStyle}>{t('channels.videos_posted')}</span>
                      <input
                        id="videos_ja_postados"
                        type="number"
                        min="0"
                        value={newCanal.videos_ja_postados}
                        onChange={(e) => setNewCanal({...newCanal, videos_ja_postados: parseInt(e.target.value) || 0})}
                        placeholder="0"
                        style={inputStyle}
                        onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.1)'; }}
                        onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.boxShadow = 'none'; }}
                      />
                    </div>
                  </div>
                  <div>
                    <span style={labelStyle}>{t('channels.notes')}</span>
                    <textarea
                      id="anotacoes"
                      value={newCanal.anotacoes}
                      onChange={(e) => setNewCanal({...newCanal, anotacoes: e.target.value})}
                      rows="2"
                      style={{ ...inputStyle, resize: 'vertical', lineHeight: '1.5' }}
                      onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.1)'; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.boxShadow = 'none'; }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '10px', paddingTop: '8px' }}>
                    <button
                      type="button"
                      onClick={() => setShowNewCanalDialog(false)}
                      style={{
                        flex: 1, padding: '10px 16px', background: 'transparent',
                        border: '1px solid var(--dp-border-subtle)', borderRadius: '10px',
                        color: 'var(--dp-text-secondary)', fontSize: '13px', fontWeight: 500,
                        fontFamily: 'var(--dp-font)', cursor: 'pointer', transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dp-bg-elevated)'; e.currentTarget.style.borderColor = 'var(--dp-border-medium)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; }}
                    >
                      {t('channels.cancel')}
                    </button>
                    <button
                      type="submit"
                      style={{
                        flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                        padding: '10px 16px', background: 'linear-gradient(135deg, var(--dp-blue-500), #3B82F6)',
                        border: 'none', borderRadius: '10px', color: '#fff', fontSize: '13px', fontWeight: 600,
                        fontFamily: 'var(--dp-font)', cursor: 'pointer', transition: 'all 0.3s',
                      }}
                    >
                      <Save size={14} />
                      {t('channels.create_channel')}
                    </button>
                  </div>
                </form>
                )}
                  </>
                )}
              </DialogContent>
            </Dialog>

        {/* ======= FASE 2: CHANNEL INDIVIDUAL PAGE ======= */}
        {selectedCanalData && (
          <div key={selectedCanal} style={{ animation: 'dp-card-stagger 0.4s ease-out both' }}>
            {/* Back to Channels + Channel Navigation */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <button
                onClick={handleBackToChannels}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '6px',
                  color: 'var(--dp-blue-500, #0DAEFF)', fontSize: '13px', fontWeight: 600,
                  fontFamily: 'var(--dp-font)', padding: '4px 0',
                  transition: 'opacity 0.2s ease',
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              >
                <ArrowLeft size={16} />
                {t('channels.back_to_channels')}
              </button>

              {/* Channel Navigation Arrows */}
              {getAllNavigableChannels().length > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <button
                    onClick={() => navigateChannel('prev')}
                    title={t('channels.nav_previous')}
                    style={{
                      background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)',
                      borderRadius: '8px', padding: '6px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; }}
                  >
                    <ChevronLeft size={16} style={{ color: 'var(--dp-text-secondary)' }} />
                  </button>
                  <button
                    onClick={() => navigateChannel('next')}
                    title={t('channels.nav_next')}
                    style={{
                      background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)',
                      borderRadius: '8px', padding: '6px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; }}
                  >
                    <ChevronRight size={16} style={{ color: 'var(--dp-text-secondary)' }} />
                  </button>
                </div>
              )}
            </div>

            {/* ======= CHANNEL HEADER CARD ======= */}
            <div style={{
              background: 'var(--dp-bg-card)',
              borderRadius: '16px',
              border: '1px solid var(--dp-border-subtle)',
              overflow: 'hidden',
              marginBottom: '16px',
            }}>
              {/* Barra superior padrão */}
              <div style={{ height: '2px', background: 'linear-gradient(90deg, #0DAEFF, #3B82F6)', opacity: 0.6 }} />

              <div style={{ padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1, minWidth: 0 }}>
                  {/* Large Avatar (YouTube thumbnail or letter fallback) */}
                  {selectedCanalData.channel_thumbnail && !channelThumbErrors[selectedCanalData.id] && !isPrivate ? (
                    <div style={{ position: 'relative', width: '56px', height: '56px', flexShrink: 0 }}>
                      <img
                        src={selectedCanalData.channel_thumbnail}
                        alt={selectedCanalData.nome}
                        style={{
                          width: '56px', height: '56px', borderRadius: '16px', objectFit: 'cover',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        }}
                        onError={() => setChannelThumbErrors(prev => ({ ...prev, [selectedCanalData.id]: true }))}
                      />
                      {selectedCanalData.is_youtube_linked && (
                        selectedCanalData.youtube_channel_url ? (
                          <a
                            href={selectedCanalData.youtube_channel_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Abrir canal no YouTube"
                            style={{
                              position: 'absolute', bottom: '-3px', right: '-3px',
                              background: '#FF0000', borderRadius: '6px', padding: '2px 4px',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              cursor: 'pointer', transition: 'transform 0.2s, opacity 0.2s',
                              textDecoration: 'none',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.15)'; e.currentTarget.style.opacity = '0.9'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.opacity = '1'; }}
                          >
                            <Youtube size={12} style={{ color: '#fff' }} />
                          </a>
                        ) : (
                          <div style={{
                            position: 'absolute', bottom: '-3px', right: '-3px',
                            background: '#FF0000', borderRadius: '6px', padding: '2px 4px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <Youtube size={12} style={{ color: '#fff' }} />
                          </div>
                        )
                      )}
                    </div>
                  ) : (
                    <div style={{
                      width: '56px', height: '56px', borderRadius: '16px',
                      background: `linear-gradient(135deg, ${avatarColor1}, ${avatarColor2})`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '24px', fontWeight: 800, color: '#fff', flexShrink: 0,
                      fontFamily: 'var(--dp-font)',
                      boxShadow: `0 4px 12px ${avatarColor1}40`,
                    }}>
                      {isPrivate ? '?' : (selectedCanalData.nome?.charAt(0)?.toUpperCase() || '?')}
                    </div>
                  )}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <h1 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--dp-text-primary)', margin: 0, fontFamily: 'var(--dp-font)', lineHeight: 1.3 }}>
                      {isPrivate ? '••••••••••' : selectedCanalData.nome}
                    </h1>
                    {!isPrivate && selectedCanalData.is_youtube_linked && (
                      selectedCanalData.youtube_channel_url ? (
                        <a
                          href={selectedCanalData.youtube_channel_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Abrir canal no YouTube"
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '3px',
                            padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 600,
                            background: 'linear-gradient(135deg, #FF0000, #CC0000)', color: '#fff',
                            textDecoration: 'none', cursor: 'pointer',
                            transition: 'transform 0.2s, opacity 0.2s',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.opacity = '0.9'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.opacity = '1'; }}
                        >
                          ▶ YouTube
                        </a>
                      ) : (
                        <div style={{
                          display: 'inline-flex', alignItems: 'center', gap: '3px',
                          padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 600,
                          background: 'linear-gradient(135deg, #FF0000, #CC0000)', color: '#fff',
                        }}>
                          ▶ YouTube
                        </div>
                      )
                    )}
                    {/* Editor badge for shared channels */}
                    {selectedCanalData._is_shared && (
                      <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: '3px',
                        padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 600,
                        background: 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)', color: '#fff',
                      }}>
                        <Users size={10} /> Editor
                      </div>
                    )}
                    </div>
                    {/* Meta info */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', marginTop: '6px', fontSize: '12px', color: 'var(--dp-text-tertiary)' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Globe size={12} style={{ color: 'var(--dp-blue-500)' }} /> {selectedCanalData.idioma}</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Clock size={12} style={{ color: 'var(--dp-blue-500)' }} /> {Array.isArray(selectedCanalData.horario_postagem) ? selectedCanalData.horario_postagem.join(' | ') : selectedCanalData.horario_postagem}</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Mail size={12} style={{ color: 'var(--dp-blue-500)' }} /> {isPrivate ? '••••@••••' : selectedCanalData.email}</span>
                      {selectedCanalData.frequencia_postagem && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Calendar size={12} style={{ color: 'var(--dp-green-500, #34D399)' }} /> {selectedCanalData.frequencia_postagem}</span>
                      )}
                    </div>
                    {/* Anotações preview */}
                    {selectedCanalData.anotacoes && !isPrivate && (
                      <div
                        onClick={openExtrasModal}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '6px',
                          marginTop: '6px', fontSize: '12px', color: 'var(--dp-text-tertiary)',
                          cursor: 'pointer', maxWidth: '340px',
                        }}
                        title="Clique para editar anotações"
                      >
                        <StickyNote size={12} style={{ color: 'var(--dp-text-tertiary)', flexShrink: 0 }} />
                        <span style={{
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {selectedCanalData.anotacoes}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', alignItems: 'start', gap: '8px', flexShrink: 0 }}>
                  {/* Custom Buttons (compact grid, max 3 per row) */}
                  {customButtons.filter(btn => btn.url && btn.name).length > 0 && (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${Math.min(customButtons.filter(btn => btn.url && btn.name).length, 3)}, auto)`,
                      gap: '5px',
                    }}>
                      {customButtons.filter(btn => btn.url && btn.name).map((button) => (
                        <button
                          key={button.id}
                          onClick={() => window.open(button.url, '_blank')}
                          style={{
                            padding: '4px 10px', borderRadius: '7px', fontSize: '11px', fontWeight: 500,
                            border: '1px solid var(--dp-border-subtle)',
                            background: 'transparent', color: 'var(--dp-text-secondary)', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: '4px', transition: 'all 0.2s ease',
                            fontFamily: 'var(--dp-font)', whiteSpace: 'nowrap', lineHeight: 1.3,
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.color = 'var(--dp-text-primary)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
                        >
                          {button.icon_url ? (
                            <img src={button.icon_url} alt={button.name} style={{ width: '12px', height: '12px' }} onError={(e) => { e.target.style.display = 'none'; }} />
                          ) : (
                            <ExternalLink size={10} />
                          )}
                          {button.name}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* More Menu */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button style={{
                        background: 'transparent', border: '1px solid var(--dp-border-subtle)',
                        borderRadius: '8px', padding: '7px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.2s ease',
                      }}>
                        <MoreVertical size={16} style={{ color: 'var(--dp-text-tertiary)' }} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent style={{ background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-medium)' }}>
                      {/* Edit Channel — only owner or editor with pode_editar_config */}
                      {(!selectedCanalData._is_shared || selectedCanalData.permissions?.pode_editar_config) && (
                        <DropdownMenuItem onClick={() => handleEditCanal(selectedCanalData)} style={{ color: 'var(--dp-blue-500)', cursor: 'pointer' }}>
                          <Edit3 size={14} style={{ marginRight: '8px' }} />
                          {t('channels.edit_channel')}
                        </DropdownMenuItem>
                      )}

                      {!selectedCanalData._is_shared && <DropdownMenuSeparator />}

                      {/* Create Shortcuts — owner only */}
                      {!selectedCanalData._is_shared && (
                        <DropdownMenuItem onClick={() => setShowCustomButtonsDialog(true)} style={{ color: 'var(--dp-text-secondary)', cursor: 'pointer' }}>
                          <Settings size={14} style={{ marginRight: '8px' }} />
                          {t('channels.create_shortcuts')}
                        </DropdownMenuItem>
                      )}

                      {/* Share Channel — owner only */}
                      {!selectedCanalData._is_shared && (
                        <DropdownMenuItem onClick={() => {
                          setShowShareModal(true);
                          // Load members
                          loadShareMembros(selectedCanalData.id);
                        }} style={{ color: 'var(--dp-text-secondary)', cursor: 'pointer' }}>
                          <Users size={14} style={{ marginRight: '8px' }} />
                          {t('sharing.share_button')}
                        </DropdownMenuItem>
                      )}

                      {/* Link/Unlink YouTube — owner only */}
                      {!selectedCanalData._is_shared && (
                        <>
                          {!selectedCanalData.is_youtube_linked ? (
                            <DropdownMenuItem onClick={() => setShowLinkYoutubeDialog(true)} style={{ color: '#FF0000', cursor: 'pointer' }}>
                              <Youtube size={14} style={{ marginRight: '8px' }} />
                              Vincular YouTube
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => {
                              setConfirmState({
                                icon: <Youtube />,
                                title: 'Desvincular YouTube',
                                message: 'Deseja desvincular o YouTube deste canal? Os vídeos importados permanecerão.',
                                confirmLabel: 'Desvincular',
                                danger: true,
                                onConfirm: async () => {
                                  setConfirmState(null);
                                  try {
                                    await axios.put(`${API}/canais/${selectedCanalData.id}`, {
                                      is_youtube_linked: false, youtube_channel_id: null, youtube_channel_url: null,
                                      channel_thumbnail: null, youtube_subscribers: 0,
                                    });
                                    await fetchCanais();
                                    toast.success('YouTube desvinculado');
                                  } catch { toast.error('Erro ao desvincular'); }
                                },
                              });
                            }} style={{ color: 'var(--dp-text-secondary)', cursor: 'pointer' }}>
                              <Youtube size={14} style={{ marginRight: '8px' }} />
                              Desvincular YouTube
                            </DropdownMenuItem>
                          )}
                        </>
                      )}

                      {/* Owner-only actions: Archive, Delete */}
                      {!selectedCanalData._is_shared && (
                        <>
                          <DropdownMenuSeparator />
                          {!selectedCanalData.is_archived ? (
                            <DropdownMenuItem onClick={() => archiveCanal(selectedCanalData.id)} style={{ color: 'var(--dp-text-secondary)', cursor: 'pointer' }}>
                              <Archive size={14} style={{ marginRight: '8px' }} />
                              Arquivar Canal
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => unarchiveCanal(selectedCanalData.id)} style={{ color: 'var(--dp-text-secondary)', cursor: 'pointer' }}>
                              <Archive size={14} style={{ marginRight: '8px' }} />
                              Desarquivar Canal
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => deleteCanal(selectedCanalData.id)} style={{ color: 'var(--dp-red-500, #F87171)', cursor: 'pointer' }}>
                            <Trash2 size={14} style={{ marginRight: '8px' }} />
                            {t('channels.delete')}
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>

                </div>
              </div>
            </div>

            {/* ======= STATS CARDS (4 cards) ======= */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '16px' }}>
              {[
                { label: t('channels.total_videos'), value: totalVideosDisplay, color: 'var(--dp-blue-500, #0DAEFF)', icon: <Video size={14} />, borderColor: 'rgba(13, 174, 255, 0.20)', topGradient: 'linear-gradient(90deg, #0DAEFF, #3B82F6)' },
                { label: t('channels.posted_videos'), value: videosPostados, color: 'var(--dp-green-500, #34D399)', icon: <CheckCircle size={14} />, borderColor: 'rgba(52, 211, 153, 0.20)', topGradient: 'linear-gradient(90deg, #0DAEFF, #3B82F6)' },
                { label: t('channels.pending_videos'), value: totalVideos - videosPostadosNoSistema, color: 'var(--dp-orange-500, #FB923C)', icon: <Clapperboard size={14} />, borderColor: 'rgba(251, 146, 60, 0.20)', topGradient: 'linear-gradient(90deg, #0DAEFF, #3B82F6)' },
              ].map((stat, i) => (
                <div key={i} style={{
                  padding: '18px 20px', borderRadius: '14px',
                  background: 'var(--dp-bg-card)',
                  border: '1px solid var(--dp-border-subtle)',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  animation: `dp-card-stagger 0.4s ease-out ${0.1 + i * 0.05}s both`,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = stat.borderColor; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '2px', background: stat.topGradient, opacity: 0.6 }} />
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 600, color: stat.color, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', fontFamily: 'var(--dp-font)' }}>
                        {stat.icon}
                        {stat.label}
                      </div>
                      <div style={{ fontSize: '32px', fontWeight: 700, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)', lineHeight: 1 }}>
                        {stat.value}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* YouTube Stats Card - Premium Design */}
              <div style={{
                padding: '18px 20px', borderRadius: '14px',
                background: 'var(--dp-bg-card)',
                border: '1px solid var(--dp-border-subtle)',
                transition: 'all 0.2s ease',
                position: 'relative',
                overflow: 'hidden',
                animation: 'dp-card-stagger 0.4s ease-out 0.25s both',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(13, 174, 255, 0.30)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '2px', background: 'linear-gradient(90deg, #0DAEFF, #3B82F6)', opacity: 0.6 }} />
                {selectedCanalData.is_youtube_linked ? (
                  <>
                    {/* Header: Title + Timestamp + Refresh */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 600, color: 'var(--dp-blue-500, #0DAEFF)', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--dp-font)' }}>
                        <BarChart3 size={14} />
                        YouTube Stats
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {timeAgo(selectedCanalData.last_sync_at) && (
                          <span style={{ fontSize: '10px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)' }}>
                            {timeAgo(selectedCanalData.last_sync_at)}
                          </span>
                        )}
                        <button
                          onClick={handleRefreshYoutubeStats}
                          disabled={refreshingStats}
                          style={{
                            background: 'none', border: 'none', cursor: refreshingStats ? 'default' : 'pointer',
                            padding: '4px', borderRadius: '6px', display: 'flex', alignItems: 'center',
                            color: '#06B6D4', opacity: refreshingStats ? 0.5 : 0.7,
                            transition: 'all 0.2s ease',
                          }}
                          onMouseEnter={(e) => { if (!refreshingStats) e.currentTarget.style.opacity = '1'; }}
                          onMouseLeave={(e) => { if (!refreshingStats) e.currentTarget.style.opacity = '0.7'; }}
                          title="Atualizar estatísticas"
                        >
                          <RefreshCw size={13} style={{
                            animation: refreshingStats ? 'spin 1s linear infinite' : 'none',
                          }} />
                        </button>
                      </div>
                    </div>
                    {/* 3 Mini Cards */}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {[
                        { icon: <Users size={16} />, value: selectedCanalData.youtube_subscribers, label: 'Inscritos' },
                        { icon: <Eye size={16} />, value: selectedCanalData.youtube_total_views, label: 'Views' },
                        { icon: <Video size={16} />, value: selectedCanalData.youtube_video_count, label: 'Videos' },
                      ].map((item, idx) => (
                        <div key={idx} style={{
                          flex: 1, textAlign: 'center',
                          background: 'rgba(0, 0, 0, 0.25)',
                          borderRadius: '10px',
                          padding: '12px 8px',
                          transition: 'background 0.2s ease',
                        }}>
                          <div style={{ color: '#06B6D4', marginBottom: '6px', display: 'flex', justifyContent: 'center' }}>
                            {item.icon}
                          </div>
                          <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)', lineHeight: 1.1 }}>
                            {formatNumber(item.value)}
                          </div>
                          <div style={{ fontSize: '10px', color: 'var(--dp-text-tertiary)', marginTop: '4px', fontFamily: 'var(--dp-font)' }}>
                            {item.label}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 600, color: '#06B6D4', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--dp-font)' }}>
                      <BarChart3 size={14} />
                      YouTube Stats
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)', textAlign: 'center', fontFamily: 'var(--dp-font)' }}>
                      Vincule ao YouTube para ver
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ======= CHANNEL INFO SECTION (Prompts + References side by side) ======= */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px', marginBottom: '16px' }}>
              {/* Prompts Card */}
              <div style={{
                background: 'var(--dp-bg-card)', borderRadius: '14px',
                border: '1px solid var(--dp-border-subtle)', padding: '18px 20px',
                transition: 'all 0.2s ease', cursor: 'default',
                animation: 'dp-card-stagger 0.4s ease-out 0.25s both',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(167, 139, 250, 0.30)'; e.currentTarget.style.boxShadow = '0 0 16px rgba(167, 139, 250, 0.08)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '40px', height: '40px', borderRadius: '12px',
                      background: 'rgba(167, 139, 250, 0.10)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <MessageSquare size={18} style={{ color: 'var(--dp-purple-500, #A78BFA)' }} />
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>
                        {t('channels.channel_prompts')}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)', marginTop: '2px' }}>
                        {t('channels.prompts_saved', { count: channelPromptCount })}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowPromptsModal(true)}
                    style={{
                      background: 'rgba(167, 139, 250, 0.08)', border: '1px solid rgba(167, 139, 250, 0.20)',
                      borderRadius: '8px', padding: '6px 12px', cursor: 'pointer',
                      fontSize: '12px', fontWeight: 600, color: 'var(--dp-purple-500, #A78BFA)',
                      fontFamily: 'var(--dp-font)', display: 'flex', alignItems: 'center', gap: '4px',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(167, 139, 250, 0.15)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(167, 139, 250, 0.08)'; }}
                  >
                    <Edit3 size={12} />
                    {t('channels.manage')}
                  </button>
                </div>
              </div>

              {/* Reference Channels Card */}
              <div style={{
                background: 'var(--dp-bg-card)', borderRadius: '14px',
                border: '1px solid var(--dp-border-subtle)', padding: '18px 20px',
                transition: 'all 0.2s ease',
                animation: 'dp-card-stagger 0.4s ease-out 0.3s both',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.20)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: channelReferences.length > 0 ? '12px' : '0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '40px', height: '40px', borderRadius: '12px',
                      background: 'rgba(239, 68, 68, 0.10)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Bookmark size={18} style={{ color: 'var(--dp-red-500, #F87171)' }} />
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>
                        {t('channels.reference_channels')}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)', marginTop: '2px' }}>
                        {channelReferences.length > 0 ? t('channels.references_count', { count: channelReferences.length }) : t('channels.no_references')}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowReferencesModal(true)}
                    style={{
                      background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.20)',
                      borderRadius: '8px', padding: '6px 12px', cursor: 'pointer',
                      fontSize: '12px', fontWeight: 600, color: 'var(--dp-red-500, #F87171)',
                      fontFamily: 'var(--dp-font)', display: 'flex', alignItems: 'center', gap: '4px',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.08)'; }}
                  >
                    <Edit3 size={12} />
                    {t('channels.manage')}
                  </button>
                </div>

                {/* Reference Badges */}
                {channelReferences.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
                    {channelReferences.map((ref) => (
                      <a
                        key={ref.id}
                        href={ref.url_canal || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '5px',
                          padding: '4px 10px', borderRadius: '7px',
                          background: 'transparent',
                          border: '1px solid var(--dp-border-subtle)',
                          textDecoration: 'none', transition: 'all 0.2s ease',
                          cursor: ref.url_canal ? 'pointer' : 'default',
                          lineHeight: 1.3,
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.30)'; e.currentTarget.style.color = 'var(--dp-text-primary)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
                        onClick={(e) => { if (isPrivate || !ref.url_canal) e.preventDefault(); }}
                      >
                        {ref.foto_url && !isPrivate ? (
                          <img src={ref.foto_url} alt={ref.nome_canal} style={{ width: '16px', height: '16px', borderRadius: '50%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; }} />
                        ) : (
                          <Youtube size={12} style={{ color: 'var(--dp-red-500, #F87171)' }} />
                        )}
                        <span style={{ fontSize: '11px', fontWeight: 500, color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)' }}>
                          {isPrivate ? '••••••' : ref.nome_canal}
                        </span>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Narrator Card */}
              <div style={{
                background: 'var(--dp-bg-card)', borderRadius: '14px',
                border: '1px solid var(--dp-border-subtle)', padding: '18px 20px',
                transition: 'all 0.2s ease',
                animation: 'dp-card-stagger 0.4s ease-out 0.35s both',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(13, 174, 255, 0.30)'; e.currentTarget.style.boxShadow = '0 0 16px rgba(13, 174, 255, 0.08)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '40px', height: '40px', borderRadius: '12px',
                      background: 'rgba(13, 174, 255, 0.10)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Mic size={18} style={{ color: 'var(--dp-blue-500)' }} />
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>
                        Narrador
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)', marginTop: '2px' }}>
                        {narratorInfo?.configured ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--dp-blue-500)', display: 'inline-block' }} />
                            {narratorInfo.voice_name || 'Configurado'}{narratorInfo.speed && narratorInfo.speed !== 1.0 ? ` · ${narratorInfo.speed}x` : ''}
                          </span>
                        ) : 'Não configurado'}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowNarratorModal(true)}
                    style={{
                      background: 'rgba(13, 174, 255, 0.08)', border: '1px solid rgba(13, 174, 255, 0.20)',
                      borderRadius: '8px', padding: '6px 12px', cursor: 'pointer',
                      fontSize: '12px', fontWeight: 600, color: 'var(--dp-blue-500)',
                      fontFamily: 'var(--dp-font)', display: 'flex', alignItems: 'center', gap: '4px',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(13, 174, 255, 0.15)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(13, 174, 255, 0.08)'; }}
                  >
                    <Settings size={12} />
                    Configurar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* No Canais - hide when any channel is selected or shared channels exist */}
        {canais.length === 0 && !selectedCanal && JSON.parse(sessionStorage.getItem('sidebar-shared-channels') || '[]').length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 24px', borderRadius: '16px', background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)' }}>
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--dp-blue-500, #0DAEFF), #3B82F6)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Youtube size={32} style={{ color: '#fff' }} />
            </div>
            <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--dp-text-primary)', marginBottom: '8px' }}>{t('channels.no_channels')}</h3>
            <p style={{ fontSize: '13px', color: 'var(--dp-text-tertiary)', marginBottom: '24px' }}>{t('channels.create_first_channel')}</p>
            <button
              onClick={() => setShowNewCanalDialog(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '9px 18px', borderRadius: '10px',
                background: 'linear-gradient(135deg, var(--dp-blue-500, #0DAEFF), #3B82F6)',
                color: '#fff', border: 'none', fontSize: '13px', fontWeight: 600,
                fontFamily: 'var(--dp-font)', cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              <Plus size={14} />
              {t('channels.create_first_title')}
            </button>
          </div>
        )}

        {/* Videos Section */}
        {selectedCanal && (
          <div>
            {/* Row 1: Title + Filters */}
            <div style={{
              position: 'sticky', top: '64px', zIndex: 30, backgroundColor: 'var(--dp-bg-primary)',
              paddingTop: '8px', paddingBottom: '8px', marginTop: '-8px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--dp-text-primary)', margin: 0, fontFamily: 'var(--dp-font)' }}>
                    {t('channels.titles_videos')}
                  </h3>
                  <ViewToggle mode={viewMode} onChange={handleViewModeChange} />
                </div>
                <FilterPills
                  options={[
                    { value: 'todos', label: `${t('channels.all')} (${totalVideos})` },
                    { value: 'planejamento', label: `${t('channels.planning')} (${videosPlanejamento})` },
                    { value: 'producao', label: `${t('channels.production')} (${videosEmProducao})` },
                    { value: 'finalizado', label: `${t('channels.finished')} (${videosProntos})` },
                    { value: 'postados', label: `${t('channels.scheduled_published')} (${videosPostadosCount})` },
                  ]}
                  value={videoFilter}
                  onChange={setVideoFilter}
                />
              </div>

              {/* Row 2: Search + Sort + New Video */}
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '8px' }}>
                {/* Full-width Search */}
                <div style={{ flex: 1, position: 'relative' }}>
                  <Search size={14} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--dp-text-tertiary)' }} />
                  <input
                    type="text"
                    placeholder={t('channels.search_titles')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      ...inputStyle,
                      width: '100%',
                      paddingLeft: '34px',
                      fontSize: '13px',
                      padding: '8px 14px 8px 34px',
                    }}
                  />
                </div>

                {/* Sort button */}
                <button onClick={toggleSortOrder} style={{
                  background: 'transparent', border: '1px solid var(--dp-border-subtle)', borderRadius: '8px',
                  padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
                  color: 'var(--dp-text-tertiary)', fontSize: '12px', fontWeight: 500, flexShrink: 0,
                }}>
                  <ArrowUpDown size={13} />
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </button>

                {/* New Video Button + Dialog */}
                <Dialog open={showNewVideoDialog} onOpenChange={setShowNewVideoDialog}>
                  <DialogTrigger asChild>
                    <button
                      onClick={() => fetchNextVideoInfo()}
                      style={{
                        background: 'linear-gradient(135deg, var(--dp-blue-500, #0DAEFF), #3B82F6)', color: 'white',
                        border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px',
                        fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
                        fontFamily: 'var(--dp-font)', flexShrink: 0, whiteSpace: 'nowrap',
                      }}
                    >
                      <Plus size={14} />
                      {t('channels.new_title')}
                    </button>
                  </DialogTrigger>
                  <DialogContent style={{ ...dialogStyle, maxWidth: '480px' }}
                    onPointerDownOutside={(e) => {
                      const target = e.target;
                      if (target?.closest?.('[role="listbox"]') || target?.closest?.('[role="option"]') || target?.closest?.('[data-custom-dropdown]') || target?.closest?.('[data-dark-datepicker]')) {
                        e.preventDefault();
                      }
                    }}
                    onInteractOutside={(e) => {
                      const target = e.target;
                      if (target?.closest?.('[role="listbox"]') || target?.closest?.('[role="option"]') || target?.closest?.('[data-custom-dropdown]') || target?.closest?.('[data-dark-datepicker]')) {
                        e.preventDefault();
                      }
                    }}
                  >
                    <DialogHeader>
                      <DialogTitle style={{
                        fontSize: '18px', fontWeight: 700,
                        color: 'var(--dp-text-primary)',
                        fontFamily: 'var(--dp-font)', margin: 0,
                        display: 'flex', alignItems: 'center', gap: '10px',
                      }}>
                        <div style={{
                          width: '32px', height: '32px', borderRadius: '10px',
                          background: 'rgba(13, 174, 255, 0.12)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Plus size={16} style={{ color: 'var(--dp-blue-500)' }} />
                        </div>
                        {t('channels.new_title_video')}
                      </DialogTitle>
                      <p style={{ fontSize: '13px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)', margin: '8px 0 0 0' }}>
                        {t('channels.new_title_subtitle')}
                      </p>
                    </DialogHeader>
                    <form onSubmit={handleCreateVideo} style={{ display: 'flex', flexDirection: 'column', gap: '18px', marginTop: '12px' }}>
                      <div>
                        <span style={labelStyle}>{t('channels.title_label')}</span>
                        <div style={{ position: 'relative' }}>
                          <input
                            id="titulo"
                            value={newVideo.titulo}
                            onChange={(e) => setNewVideo({...newVideo, titulo: e.target.value})}
                            required
                            placeholder="Digite o título do vídeo"
                            style={{ ...inputStyle, paddingRight: '48px' }}
                            onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.1)'; }}
                            onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.boxShadow = 'none'; }}
                          />
                          <span style={{
                            position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                            fontSize: '9px', pointerEvents: 'none', fontFamily: 'var(--dp-font)',
                            color: newVideo.titulo.length > 100 ? '#F87171' : 'var(--dp-text-tertiary)',
                          }}>
                            {newVideo.titulo.length}/100
                          </span>
                        </div>
                      </div>
                      <div>
                        <span style={labelStyle}>{t('channels.thumbnail')}</span>
                        <div style={{ position: 'relative' }}>
                          <input
                            id="thumbnail"
                            value={newVideo.thumbnail}
                            onChange={(e) => setNewVideo({...newVideo, thumbnail: e.target.value})}
                            placeholder="Descrição ou conceito da thumbnail"
                            style={{ ...inputStyle, paddingRight: '48px' }}
                            onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.1)'; }}
                            onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.boxShadow = 'none'; }}
                          />
                          <span style={{
                            position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                            fontSize: '9px', pointerEvents: 'none', fontFamily: 'var(--dp-font)',
                            color: 'var(--dp-text-tertiary)',
                          }}>
                            {newVideo.thumbnail.length}
                          </span>
                        </div>
                      </div>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                          <span style={labelStyle}>{t('channels.video_notes')}</span>
                          <span style={{
                            fontSize: '11px', fontFamily: 'var(--dp-font)',
                            color: newVideo.notas.length > 5000 ? '#F87171' : 'var(--dp-text-tertiary)',
                            fontWeight: newVideo.notas.length > 5000 ? 600 : 400,
                          }}>
                            {newVideo.notas.length}/5000
                          </span>
                        </div>
                        <textarea
                          id="notas"
                          value={newVideo.notas}
                          onChange={(e) => setNewVideo({...newVideo, notas: e.target.value})}
                          rows="3"
                          placeholder="Descrição do vídeo..."
                          style={{ ...inputStyle, resize: 'vertical', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}
                          onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.1)'; }}
                          onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.boxShadow = 'none'; }}
                        />
                      </div>
                      <div>
                        <span style={labelStyle}>{t('channels.posting_date')}</span>
                        <DarkDatePicker
                          value={newVideo.data_postagem}
                          onChange={(val) => setNewVideo({...newVideo, data_postagem: val})}
                          placeholder={t('channels.select_date')}
                        />
                        <p style={{ fontSize: '11px', marginTop: '4px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)' }}>
                          {t('channels.posting_date_hint')}
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: '10px', paddingTop: '8px' }}>
                        <button
                          type="button"
                          onClick={() => setShowNewVideoDialog(false)}
                          style={{
                            flex: 1, padding: '10px 16px', background: 'transparent',
                            border: '1px solid var(--dp-border-subtle)', borderRadius: '10px',
                            color: 'var(--dp-text-secondary)', fontSize: '13px', fontWeight: 500,
                            fontFamily: 'var(--dp-font)', cursor: 'pointer', transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dp-bg-elevated)'; e.currentTarget.style.borderColor = 'var(--dp-border-medium)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; }}
                        >
                          {t('channels.cancel')}
                        </button>
                        <button
                          type="submit"
                          style={{
                            flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                            padding: '10px 16px', background: 'linear-gradient(135deg, var(--dp-blue-500), #3B82F6)',
                            border: 'none', borderRadius: '10px', color: '#fff', fontSize: '13px', fontWeight: 600,
                            fontFamily: 'var(--dp-font)', cursor: 'pointer', transition: 'all 0.3s',
                          }}
                        >
                          <Save size={14} />
                          {t('channels.create')}
                        </button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Videos: Kanban or List view */}
            {viewMode === 'kanban' ? (
              <>
                <KanbanBoard
                  videos={videos}
                  totalCounts={videosCounts}
                  onVideoUpdate={handleKanbanVideoUpdate}
                  onVideoDelete={handleKanbanVideoDelete}
                  onChecklistChange={handleChecklistChange}
                  onOptimisticUpdate={handleKanbanOptimisticUpdate}
                  onOptimisticRollback={handleKanbanOptimisticRollback}
                  onConfirmAction={(config) => setConfirmState({ ...config, onConfirm: () => { setConfirmState(null); config.onConfirm(); } })}
                  onOpenScript={handleKanbanOpenScript}
                  onCardClick={handleKanbanCardClick}
                  isLoading={loadingVideos}
                  isMobile={isMobile}
                  mobileTab={mobileTab}
                  onMobileTabChange={setMobileTab}
                />

                {/* Kanban Video Edit Modal */}
                <KanbanVideoModal
                  video={currentModalVideo}
                  onClose={() => setKanbanModalVideo(null)}
                  onChecklistChange={handleChecklistChange}
                  onVideoUpdate={handleKanbanVideoUpdate}
                  onOpenScript={(video) => {
                    setKanbanModalVideo(null);
                    setScriptDrawerVideo({
                      id: video.id,
                      titulo: video.titulo,
                      canalId: video.canal_id || selectedCanal,
                      status: video.status,
                    });
                    setScriptDrawerOpen(true);
                  }}
                  onThumbnailUpload={triggerThumbnailUpload}
                  onThumbnailDelete={handleThumbnailDelete}
                  onDeleteVideo={(videoId) => {
                    deleteVideo(videoId);
                    setKanbanModalVideo(null);
                  }}
                  onLinkYoutube={(video) => {
                    setKanbanModalVideo(null);
                    openYtLinkModal(video);
                  }}
                  canalId={selectedCanal}
                />
              </>
            ) : (
            <>
            {/* Videos List */}
            {loadingVideos ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[1, 2, 3, 4].map(i => (
                  <div key={i} style={{
                    background: 'var(--dp-bg-card)', borderRadius: '14px',
                    border: '1px solid var(--dp-border-subtle)', padding: '14px 20px',
                    display: 'flex', alignItems: 'center', gap: '16px',
                  }}>
                    <div className="dp-skeleton" style={{ width: '40px', height: '40px', borderRadius: '10px', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div className="dp-skeleton" style={{ width: '65%', height: '14px', marginBottom: '8px' }} />
                      <div className="dp-skeleton" style={{ width: '40%', height: '10px' }} />
                    </div>
                    <div className="dp-skeleton" style={{ width: '70px', height: '24px', borderRadius: '12px', flexShrink: 0 }} />
                  </div>
                ))}
              </div>
            ) : filteredVideos.length === 0 ? (
              <div className="text-center py-12 rounded-2xl" style={{ background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)' }}>
                <FileText className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--dp-text-tertiary)' }} />
                <h4 className="text-lg font-medium mb-2" style={{ color: 'var(--dp-text-primary)' }}>
                  {searchTerm ? t('channels.no_titles') : t('channels.no_titles')}
                </h4>
                <p className="mb-6" style={{ color: 'var(--dp-text-tertiary)' }}>
                  {searchTerm ? t('channels.search_titles') : t('channels.create_first')}
                </p>
                {!searchTerm && (
                  <Button
                    onClick={() => {
                      fetchNextVideoInfo(); // Buscar número atualizado ao abrir diálogo
                      setShowNewVideoDialog(true);
                    }}
                    style={{
                      background: 'linear-gradient(135deg, var(--dp-blue-500, #0DAEFF), #3B82F6)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 16px rgba(6, 182, 212, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('channels.create_first_title')}
                  </Button>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {filteredVideos.map((video, index) => {
                  // Usar o número real do vídeo ao invés do índice da lista filtrada
                  const numeroFormatado = video.numero_video || `Vídeo ${(index + 1).toString().padStart(2, '0')}`;
                  
                  return (
                  <div key={video.id}
                    style={{
                      overflow: 'hidden', minWidth: 0, background: 'var(--dp-bg-card)',
                      border: '1px solid var(--dp-border-subtle)', borderRadius: '14px',
                      padding: '14px 20px', transition: 'all 0.25s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-medium)'; e.currentTarget.style.background = 'var(--dp-bg-card-hover)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.background = 'var(--dp-bg-card)'; }}
                  >
                    {/* Edit Mode (expandable) - only for non-imported videos */}
                    {!video.is_imported && editingFields[`${video.id}-titulo`] ? (
                      <div className="space-y-2 mb-2 p-3 rounded-lg" style={{ background: 'rgba(13, 174, 255, 0.06)', border: '1px solid rgba(13, 174, 255, 0.15)' }}>
                        <div className="flex items-center gap-2 font-medium text-xs" style={{ color: 'var(--dp-blue-500)' }}>
                          <Edit3 className="w-2.5 h-2.5" />
                          {t('channels.edit')}
                        </div>

                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <Label className="text-[10px] font-medium" style={{ color: 'var(--dp-text-secondary)' }}>
                              {t('channels.title_label')}
                            </Label>
                            <button
                              type="button"
                              onClick={() => handleOpenLinkModal(video.id, 'titulo')}
                              title={tempValues[`${video.id}-titulo-link`] ? 'Editar link' : 'Adicionar link'}
                              className={`flex items-center justify-center w-5 h-5 rounded transition-colors ${
                                tempValues[`${video.id}-titulo-link`]
                                  ? isDarkMode
                                    ? ''
                                    : ''
                                  : isDarkMode
                                  ? 'text-gray-500 hover:bg-gray-700'
                                  : 'text-gray-400 hover:bg-gray-100'
                              }`}
                            >
                              <Link className="w-3 h-3" />
                            </button>
                          </div>
                          <div className="relative flex items-center gap-1">
                            <input
                              type="text"
                              value={tempValues[`${video.id}-titulo`] || ''}
                              onChange={(e) => handleTempValueChange(video.id, 'titulo', e.target.value)}
                              className={`flex-1 p-1.5 pr-12 text-xs border rounded-md ${
                                isDarkMode
                                  ? 'border-gray-700 bg-gray-800 text-white'
                                  : 'border-gray-300 bg-white'
                              }`}
                              placeholder="Título do vídeo"
                            />
                            <span className={`absolute right-10 top-1/2 -translate-y-1/2 text-[9px] pointer-events-none ${
                              ((tempValues[`${video.id}-titulo`] || '').length) > 100
                                ? 'text-red-400'
                                : 'text-gray-500'
                            }`}>
                              {(tempValues[`${video.id}-titulo`] || '').length}/100
                            </span>
                            <button
                              type="button"
                              onClick={() => handleTextFormat(video.id, 'titulo')}
                              title={getFormatButtonTooltip(video.id, 'titulo')}
                              className={`flex-shrink-0 w-7 h-7 flex items-center justify-center text-[10px] font-bold rounded border transition-colors ${
                                isDarkMode
                                  ? ''
                                  : ''
                              }`}
                            >
                              {getFormatButtonIcon(video.id, 'titulo')}
                            </button>
                          </div>
                        </div>

                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <Label className="text-[10px] font-medium" style={{ color: 'var(--dp-text-secondary)' }}>
                              {t('channels.thumbnail')}
                            </Label>
                            <button
                              type="button"
                              onClick={() => handleOpenLinkModal(video.id, 'thumbnail')}
                              title={tempValues[`${video.id}-thumbnail-link`] ? 'Editar link' : 'Adicionar link'}
                              className={`flex items-center justify-center w-5 h-5 rounded transition-colors ${
                                tempValues[`${video.id}-thumbnail-link`]
                                  ? isDarkMode
                                    ? ''
                                    : ''
                                  : isDarkMode
                                  ? 'text-gray-500 hover:bg-gray-700'
                                  : 'text-gray-400 hover:bg-gray-100'
                              }`}
                            >
                              <Link className="w-3 h-3" />
                            </button>
                          </div>
                          <div className="relative flex items-center gap-1">
                            <input
                              type="text"
                              value={tempValues[`${video.id}-thumbnail`] || ''}
                              onChange={(e) => handleTempValueChange(video.id, 'thumbnail', e.target.value)}
                              className={`flex-1 p-1.5 pr-12 text-xs border rounded-md ${
                                isDarkMode
                                  ? 'border-gray-700 bg-gray-800 text-white'
                                  : 'border-gray-300 bg-white'
                              }`}
                              placeholder={t('channels.thumbnail_description_placeholder') || 'Descrição da thumbnail'}
                            />
                            <span className={`absolute right-10 top-1/2 -translate-y-1/2 text-[9px] pointer-events-none ${
                              'text-gray-500'
                            }`}>
                              {(tempValues[`${video.id}-thumbnail`] || '').length}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleTextFormat(video.id, 'thumbnail')}
                              title={getFormatButtonTooltip(video.id, 'thumbnail')}
                              className={`flex-shrink-0 w-7 h-7 flex items-center justify-center text-[10px] font-bold rounded border transition-colors ${
                                isDarkMode
                                  ? ''
                                  : ''
                              }`}
                            >
                              {getFormatButtonIcon(video.id, 'thumbnail')}
                            </button>
                          </div>
                        </div>

                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <Label className="text-[10px] font-medium" style={{ color: 'var(--dp-text-secondary)' }}>
                              {t('channels.video_notes')}
                            </Label>
                            <div className="flex items-center gap-2">
                              <span className={`text-[9px] ${
                                ((tempValues[`${video.id}-notas`] || '').length) > 5000
                                  ? 'text-red-400 font-semibold'
                                  : 'text-gray-500'
                              }`}>
                                {(tempValues[`${video.id}-notas`] || '').length}/5000
                              </span>
                              {(tempValues[`${video.id}-notas`] || '').trim() && (
                                <button
                                  type="button"
                                  onClick={() => handleCopyToClipboard(tempValues[`${video.id}-notas`], 'Descrição')}
                                  title="Copiar descrição"
                                  className="flex items-center justify-center w-5 h-5 rounded transition-colors"
                                  style={{ color: 'var(--dp-blue-500)' }}
                                >
                                  <FileText className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </div>
                          <textarea
                            value={tempValues[`${video.id}-notas`] || ''}
                            onChange={(e) => handleTempValueChange(video.id, 'notas', e.target.value)}
                            className="w-full p-1.5 text-xs rounded-md resize-none"
                            placeholder="Descrição do vídeo..."
                            rows={3}
                            style={{ whiteSpace: 'pre-wrap', background: 'var(--dp-bg-input)', border: '1px solid var(--dp-border-subtle)', color: 'var(--dp-text-primary)' }}
                          />
                        </div>

                        <div>
                          <Label className="text-[10px] font-medium" style={{ color: 'var(--dp-text-secondary)', marginBottom: '4px', display: 'block' }}>
                            {t('channels.posting_date')}
                          </Label>
                          <DarkDatePicker
                            value={tempValues[`${video.id}-data_postagem`] || ''}
                            onChange={(val) => handleTempValueChange(video.id, 'data_postagem', val)}
                            placeholder={t('channels.select_date')}
                          />
                        </div>

                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleFieldSaveAll(video.id)}
                            className="px-2 py-1 text-[10px] h-6"
                            style={{ background: 'linear-gradient(135deg, var(--dp-green-500, #34D399), #059669)', color: '#fff', border: 'none' }}
                          >
                            <CheckCircle className="w-2.5 h-2.5 mr-1" />
                            {t('channels.save_link')}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleFieldCancel(video.id)}
                            style={{
                              color: 'var(--dp-text-secondary)',
                              borderColor: 'var(--dp-border-medium)'
                            }}
                            className="px-2 py-1 text-[10px] h-6"
                          >
                            <X className="w-2.5 h-2.5 mr-1" />
                            {t('channels.cancel')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* ===== COMPACT HORIZONTAL LAYOUT ===== */
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        {/* ZONE THUMBNAIL (with upload support / youtube thumbnail) */}
                        <div
                          style={{
                            width: '112px', height: '63px', borderRadius: '8px', flexShrink: 0,
                            overflow: 'hidden', background: 'var(--dp-bg-elevated)',
                            border: '1px solid var(--dp-border-subtle)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: video.is_imported ? 'default' : 'pointer', transition: 'all 0.2s ease', position: 'relative',
                          }}
                          title={video.is_imported ? '' : (video.youtube_video_id ? (t('channels.yt_thumb_synced') || 'Thumbnail sincronizada do YouTube') : (video.thumbnail_url ? '' : (t('channels.click_add_thumbnail') || 'Clique para adicionar thumbnail')))}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (video.is_imported || video.youtube_video_id) {
                              // Importado OU vinculado: thumb sincronizada do
                              // YouTube — clique abre preview, nunca upload
                              if (video.youtube_thumbnail) setThumbnailPreview(video.youtube_thumbnail);
                              return;
                            }
                            if (video.thumbnail_url) {
                              setThumbnailPreview(video.thumbnail_url);
                            } else {
                              triggerThumbnailUpload(video.id);
                            }
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; }}
                        >
                          {/* Loading spinner */}
                          {uploadingThumbnail[video.id] ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                              <RefreshCw size={18} style={{ color: 'var(--dp-blue-500)', animation: 'spin 1s linear infinite' }} />
                            </div>
                          ) : !isPrivate && (video.is_imported || video.youtube_video_id) && video.youtube_thumbnail ? (
                            /* Imported video: show YouTube thumbnail */
                            <>
                              <img
                                src={video.youtube_thumbnail}
                                alt={video.titulo || 'Thumbnail'}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                              {/* YouTube icon overlay */}
                              <div style={{
                                position: 'absolute', bottom: '2px', right: '2px',
                                background: 'rgba(255,0,0,0.85)', borderRadius: '3px', padding: '1px 3px',
                              }}>
                                <Youtube size={10} style={{ color: '#fff' }} />
                              </div>
                            </>
                          ) : !isPrivate && video.thumbnail_url ? (
                            <>
                              <img
                                src={video.thumbnail_url}
                                alt={video.titulo || 'Thumbnail'}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                              {/* Hover overlay with actions */}
                              <div
                                className="thumb-overlay"
                                style={{
                                  position: 'absolute', inset: 0,
                                  background: 'rgba(0,0,0,0.7)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                                  opacity: 0, transition: 'opacity 0.2s',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.opacity = '0'; }}
                              >
                                <button
                                  onClick={(e) => { e.stopPropagation(); if (!isPrivate) setThumbnailPreview(video.thumbnail_url); }}
                                  title="Visualizar"
                                  style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '4px', padding: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                  <ZoomIn size={12} style={{ color: 'white' }} />
                                </button>
                                {!video.youtube_video_id && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); triggerThumbnailUpload(video.id); }}
                                  title={t('channels.replace_thumbnail') || 'Substituir'}
                                  style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '4px', padding: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                  <Upload size={12} style={{ color: 'white' }} />
                                </button>
                                )}
                                <button
                                  onClick={(e) => { e.stopPropagation(); window.open(video.thumbnail_url, '_blank'); }}
                                  title={t('channels.download_thumbnail') || 'Baixar'}
                                  style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '4px', padding: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                  <Download size={12} style={{ color: 'white' }} />
                                </button>
                                {!video.youtube_video_id && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setThumbnailDeleteConfirm(video.id); }}
                                  title={t('channels.remove_thumbnail') || 'Remover'}
                                  style={{ background: 'rgba(239,68,68,0.3)', border: 'none', borderRadius: '4px', padding: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                  <Trash2 size={12} style={{ color: '#F87171' }} />
                                </button>
                                )}
                              </div>
                            </>
                          ) : (
                            <div style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '2px',
                              width: '100%', height: '100%',
                            }}>
                              <ImageIcon size={20} style={{ color: 'var(--dp-text-tertiary)', opacity: 0.5 }} />
                            </div>
                          )}
                        </div>

                        {/* ZONE LEFT: Video number + Status + Date */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '180px', minWidth: '180px', flexShrink: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '11px', fontFamily: 'monospace',
                              padding: '2px 8px', borderRadius: '6px', border: '1px solid var(--dp-border-medium)',
                              color: 'var(--dp-text-tertiary)', fontWeight: 500,
                              minWidth: '70px', flexShrink: 0, textAlign: 'center',
                            }}>
                              {numeroFormatado}
                            </span>
                            {getStatusBadge(video.status)}
                          </div>
                          {/* Date display + YouTube imported tag */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '2px' }}>
                          {video.data_postagem && (() => {
                            try {
                              const [day, month, year] = video.data_postagem.split('/');
                              const dataVideo = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                              const hoje = new Date();
                              hoje.setHours(0, 0, 0, 0);
                              dataVideo.setHours(0, 0, 0, 0);
                              const isPostado = video.checklist_postagem === 'Publicado';
                              const isDataFutura = dataVideo.getTime() > hoje.getTime();
                              const isDataPassada = dataVideo.getTime() < hoje.getTime();
                              let cor, texto;
                              if (!isPostado && isDataPassada) {
                                cor = '#EF4444';
                                texto = `${t('channels.overdue_label')} ${video.data_postagem}`;
                              } else if (!isPostado) {
                                cor = 'var(--dp-blue-500)';
                                texto = `${t('channels.publish_on')} ${video.data_postagem}`;
                              } else if (isDataFutura) {
                                cor = '#F59E0B';
                                texto = `${t('channels.scheduled_for')} ${video.data_postagem}`;
                              } else {
                                cor = '#10B981';
                                texto = video.data_postagem;
                              }
                              return (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: cor }}>
                                  <Calendar size={10} />
                                  {texto}
                                </div>
                              );
                            } catch (error) {
                              return (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: 'var(--dp-blue-500)' }}>
                                  <Calendar size={10} />
                                  {video.data_postagem}
                                </div>
                              );
                            }
                          })()}
                          {video.is_imported && (
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: '3px',
                              padding: '1px 5px', borderRadius: '4px', fontSize: '9px', fontWeight: 500,
                              background: 'rgba(255,255,255,0.06)', border: '1px solid var(--dp-border-medium)',
                              color: 'var(--dp-text-tertiary)',
                            }}>
                              <Youtube size={9} /> Importado
                            </span>
                          )}
                          </div>
                        </div>

                        {/* ZONE CENTER: Title + Checklist */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: '14px', fontWeight: 600, color: 'var(--dp-text-primary)',
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                              fontFamily: 'var(--dp-font)', cursor: 'pointer',
                            }}
                            onClick={() => !isPrivate && handleCopyToClipboard(video.titulo, 'Título')}
                            title={isPrivate ? '' : video.titulo}
                          >
                            {isPrivate ? '••••••••••' : video.titulo || 'Sem título'}
                          </div>
                          {/* Thumbnail name display */}
                          {(video.thumbnail || video.thumbnail_url) && !isPrivate && (
                            <div
                              style={{
                                fontSize: '11px', color: 'var(--dp-text-tertiary)',
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                fontFamily: 'var(--dp-font)', marginTop: '2px', cursor: 'pointer',
                              }}
                              onClick={() => !isPrivate && handleCopyToClipboard(video.thumbnail || (video.thumbnail_url ? decodeURIComponent(video.thumbnail_url.split('/').pop().split('?')[0]) : ''), 'Thumbnail')}
                              title={video.thumbnail || video.thumbnail_url}
                            >
                              {video.thumbnail || (video.thumbnail_url ? decodeURIComponent(video.thumbnail_url.split('/').pop().split('?')[0]) : '')}
                            </div>
                          )}
                          {/* Inline checklist */}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '6px', alignItems: 'center' }}>
                            {[
                              { key: 'checklist_titulo', label: t('channels.title_label'), checked: !!video.checklist_titulo && video.checklist_titulo !== "" },
                              { key: 'checklist_roteiro', label: t('channels.script'), checked: !!video.checklist_roteiro && video.checklist_roteiro !== "" },
                              { key: 'checklist_audio', label: t('channels.audio_label'), checked: !!video.checklist_audio && video.checklist_audio !== "" },
                              { key: 'checklist_thumbnail', label: t('channels.thumbnail'), checked: !!video.checklist_thumbnail && video.checklist_thumbnail !== "" },
                              { key: 'checklist_pronto', label: t('channels.rendered'), checked: !!video.checklist_pronto && video.checklist_pronto !== "" },
                              { key: 'checklist_postagem', label: t('channels.posted'), checked: !!video.checklist_postagem && video.checklist_postagem !== "" },
                            ].map(item => (
                              <label key={item.key} style={{
                                display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px',
                                cursor: video.is_imported ? 'default' : 'pointer',
                                color: item.checked ? 'var(--dp-green-500, #34D399)' : 'var(--dp-text-tertiary)',
                                opacity: video.is_imported ? 0.7 : 1,
                              }}>
                                <Checkbox
                                  checked={item.checked}
                                  onCheckedChange={video.is_imported ? undefined : ((checked) => handleChecklistChange(video.id, item.key, checked))}
                                  disabled={video.is_imported}
                                  className="h-3.5 w-3.5"
                                />
                                {item.label}
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* ZONE RIGHT: Score + Actions */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                          {video.is_imported ? (
                            /* Imported video actions: View on YouTube + Delete */
                            <>
                              {video.youtube_url && (
                                <a href={video.youtube_url} target="_blank" rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    background: 'transparent', border: '1px solid var(--dp-border-subtle)',
                                    borderRadius: '8px', padding: '5px 12px', fontSize: '12px', fontWeight: 500,
                                    color: 'var(--dp-text-secondary)', cursor: 'pointer', display: 'flex',
                                    alignItems: 'center', gap: '5px', transition: 'all 0.2s ease',
                                    fontFamily: 'var(--dp-font)', whiteSpace: 'nowrap', textDecoration: 'none',
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-medium)'; e.currentTarget.style.background = 'var(--dp-bg-elevated)'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.background = 'transparent'; }}>
                                  <ExternalLink size={13} />
                                  YouTube
                                </a>
                              )}
                              <button
                                onClick={() => setShowDeleteImportedModal(video.id)}
                                style={{
                                  background: 'transparent', border: '1px solid var(--dp-border-subtle)',
                                  borderRadius: '8px', padding: '6px', cursor: 'pointer', display: 'flex',
                                  alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.3)'; e.currentTarget.style.background = 'rgba(239, 68, 68, 0.08)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.background = 'transparent'; }}
                              >
                                <Trash2 size={14} style={{ color: 'var(--dp-text-tertiary)' }} />
                              </button>
                            </>
                          ) : (
                            /* Normal video actions: Edit + Delete + Roteiro */
                            <>
                          {/* Title char count */}
                          {!isPrivate && (
                            <span style={{
                              fontSize: '12px', fontWeight: 600, fontFamily: 'var(--dp-font)',
                              color: (video.titulo?.length || 0) > 100 ? 'var(--dp-red-500, #F87171)' : 'var(--dp-blue-500, #0DAEFF)',
                            }}>
                              {video.titulo?.length || 0}/100
                            </span>
                          )}

                          {/* Edit button */}
                          <button
                            onClick={() => handleFieldEdit(video.id)}
                            title="Editar título, thumbnail e descrição"
                            style={{
                              background: 'rgba(13, 174, 255, 0.08)', border: '1px solid rgba(13, 174, 255, 0.20)',
                              borderRadius: '8px', padding: '6px', cursor: 'pointer', display: 'flex',
                              alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease',
                            }}
                          >
                            <Edit3 size={14} style={{ color: 'var(--dp-blue-500)' }} />
                          </button>

                          {/* Vincular ao YouTube (vídeo publicado) — 2026-06 */}
                          <button
                            onClick={() => openYtLinkModal(video)}
                            title={video.youtube_video_id ? (t('channels.yt_link_linked') || 'YouTube vinculado') : (t('channels.yt_link_action') || 'Vincular ao YouTube')}
                            style={{
                              background: video.youtube_video_id ? 'rgba(255, 0, 0, 0.08)' : 'transparent',
                              border: video.youtube_video_id ? '1px solid rgba(255, 0, 0, 0.20)' : '1px solid var(--dp-border-subtle)',
                              borderRadius: '8px', padding: '6px', cursor: 'pointer', display: 'flex',
                              alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(255, 0, 0, 0.35)'; e.currentTarget.style.background = 'rgba(255, 0, 0, 0.08)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.borderColor = video.youtube_video_id ? 'rgba(255, 0, 0, 0.20)' : 'var(--dp-border-subtle)'; e.currentTarget.style.background = video.youtube_video_id ? 'rgba(255, 0, 0, 0.08)' : 'transparent'; }}
                          >
                            <Youtube size={14} style={{ color: video.youtube_video_id ? '#FF0000' : 'var(--dp-text-tertiary)' }} />
                          </button>

                          {/* Delete button — hidden for editors without pode_deletar_videos */}
                          {(!selectedCanalData?._is_shared || selectedCanalData?.permissions?.pode_deletar_videos) && (
                          <button
                            onClick={() => deleteVideo(video.id)}
                            style={{
                              background: 'transparent', border: '1px solid var(--dp-border-subtle)',
                              borderRadius: '8px', padding: '6px', cursor: 'pointer', display: 'flex',
                              alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.3)'; e.currentTarget.style.background = 'rgba(239, 68, 68, 0.08)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.background = 'transparent'; }}
                          >
                            <Trash2 size={14} style={{ color: 'var(--dp-text-tertiary)' }} />
                          </button>
                          )}

                          {/* Roteiro button */}
                          <button
                            onClick={() => { setScriptDrawerVideo({ id: video.id, titulo: video.titulo, canalId: video.canal_id || selectedCanal, status: video.status }); setScriptDrawerOpen(true); }}
                            style={{
                              background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)',
                              borderRadius: '8px', padding: '5px 12px', fontSize: '12px', fontWeight: 500,
                              color: 'var(--dp-text-primary)', cursor: 'pointer', display: 'flex',
                              alignItems: 'center', gap: '5px', transition: 'all 0.2s ease',
                              fontFamily: 'var(--dp-font)', whiteSpace: 'nowrap',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.color = 'var(--dp-blue-500)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.color = 'var(--dp-text-primary)'; }}
                          >
                            <FileText size={13} style={{ color: 'var(--dp-blue-500)' }} />
                            {t('channels.script')}
                          </button>
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Notas - only shown when NOT editing (compact hint below the card content) */}
                    {!editingFields[`${video.id}-titulo`] && !isPrivate && video.notas && (
                      <div style={{
                        marginTop: '8px', padding: '4px 10px', borderRadius: '6px',
                        background: 'var(--dp-bg-input)', border: '1px solid var(--dp-border-subtle)',
                        fontSize: '10px', color: 'var(--dp-text-tertiary)',
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                        overflow: 'hidden', whiteSpace: 'normal', lineHeight: '1.5',
                        cursor: 'pointer',
                      }}
                        onClick={() => handleCopyToClipboard(video.notas, 'Descrição')}
                        title={video.notas}
                      >
                        📝 {video.notas}
                      </div>
                    )}
                  </div>
                  );
                })}
                {/* Ver mais button */}
                {hasMoreVideos && (
                  <button
                    onClick={() => fetchVideos(true)}
                    disabled={loadingMore}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      marginTop: '4px',
                      background: 'var(--dp-bg-card)',
                      border: '1px solid var(--dp-border-subtle)',
                      borderRadius: '12px',
                      color: 'var(--dp-blue-500, #0DAEFF)',
                      fontSize: '13px',
                      fontWeight: 500,
                      fontFamily: 'var(--dp-font)',
                      cursor: loadingMore ? 'wait' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'all 0.2s ease',
                      opacity: loadingMore ? 0.7 : 1,
                    }}
                    onMouseEnter={(e) => { if (!loadingMore) { e.currentTarget.style.borderColor = 'var(--dp-blue-500, #0DAEFF)'; e.currentTarget.style.background = 'rgba(13, 174, 255, 0.06)'; } }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.background = 'var(--dp-bg-card)'; }}
                  >
                    {loadingMore ? (
                      <>
                        <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />
                        Carregando...
                      </>
                    ) : (
                      <>
                        Mostrando {videos.length} de {videosFilteredTotal} — {t('kanban.see_more', { count: videosFilteredTotal - videos.length })}
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
            </>
            )}
          </div>
        )}

        {/* Edit Canal Dialog */}
        <Dialog open={showEditCanalDialog} onOpenChange={setShowEditCanalDialog}>
          <DialogContent style={{ ...dialogStyle, maxWidth: '520px' }}
            onPointerDownOutside={(e) => {
              const target = e.target;
              if (target?.closest?.('[role="listbox"]') || target?.closest?.('[role="option"]')) {
                e.preventDefault();
              }
            }}
            onInteractOutside={(e) => {
              const target = e.target;
              if (target?.closest?.('[role="listbox"]') || target?.closest?.('[role="option"]')) {
                e.preventDefault();
              }
            }}
          >
            <DialogHeader>
              <DialogTitle style={{
                fontSize: '18px', fontWeight: 700,
                color: 'var(--dp-text-primary)',
                fontFamily: 'var(--dp-font)', margin: 0,
                display: 'flex', alignItems: 'center', gap: '10px',
              }}>
                <div style={{
                  width: '32px', height: '32px', borderRadius: '10px',
                  background: 'rgba(13, 174, 255, 0.12)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Edit3 size={16} style={{ color: 'var(--dp-blue-500)' }} />
                </div>
                {t('channels.edit_channel')}
              </DialogTitle>
              <p style={{ fontSize: '13px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)', margin: '8px 0 0 0' }}>
                {t('channels.update_info')}
              </p>
            </DialogHeader>
            <form onSubmit={handleUpdateCanal} style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '8px' }}>
              <div>
                <span style={labelStyle}>{t('channels.channel_name')}</span>
                <input
                  id="edit_nome"
                  value={newCanal.nome}
                  onChange={(e) => setNewCanal({...newCanal, nome: e.target.value})}
                  required
                  style={inputStyle}
                  onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.1)'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.boxShadow = 'none'; }}
                />
              </div>
              {/* 2-column: Idioma + Email */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <span style={labelStyle}>{t('channels.language')}</span>
                  <CustomDropdown
                    value={newCanal.idioma}
                    onChange={(val) => setNewCanal({...newCanal, idioma: val})}
                    options={[
                      { value: 'Português', label: 'Português' },
                      { value: 'Inglês', label: 'Inglês' },
                      { value: 'Espanhol', label: 'Espanhol' },
                      { value: 'Francês', label: 'Francês' },
                      { value: 'Alemão', label: 'Alemão' },
                      { value: 'Italiano', label: 'Italiano' },
                      { value: 'Japonês', label: 'Japonês' },
                      { value: 'Coreano', label: 'Coreano' },
                      { value: 'Chinês', label: 'Chinês' },
                      { value: 'Russo', label: 'Russo' },
                      { value: 'Árabe', label: 'Árabe' },
                      { value: 'Hindi', label: 'Hindi' },
                      { value: 'Turco', label: 'Turco' },
                      { value: 'Holandês', label: 'Holandês' },
                      { value: 'Sueco', label: 'Sueco' },
                      { value: 'Polonês', label: 'Polonês' },
                    ]}
                    placeholder="Selecionar idioma"
                  />
                </div>
                <div>
                  <span style={labelStyle}>{t('channels.email')}</span>
                  <input
                    id="edit_email"
                    type="email"
                    value={newCanal.email}
                    onChange={(e) => setNewCanal({...newCanal, email: e.target.value})}
                    required
                    placeholder="email@exemplo.com"
                    style={inputStyle}
                    onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.1)'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.boxShadow = 'none'; }}
                  />
                </div>
              </div>
              <div>
                <span style={labelStyle}>{t('channels.posting_time')}</span>
                <div style={{ maxHeight: '160px', overflowY: 'auto', marginTop: '4px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: '8px' }}>
                    {(Array.isArray(newCanal.horario_postagem) ? newCanal.horario_postagem : [newCanal.horario_postagem || '19:00']).map((horario, index) => (
                      <div key={index} style={{ position: 'relative' }}>
                        <input
                          type="time"
                          value={horario}
                          onChange={(e) => {
                            const currentHorarios = Array.isArray(newCanal.horario_postagem) ? newCanal.horario_postagem : [newCanal.horario_postagem || '19:00'];
                            const newHorarios = [...currentHorarios];
                            newHorarios[index] = e.target.value;
                            setNewCanal({...newCanal, horario_postagem: newHorarios});
                          }}
                          style={{ ...inputStyle, paddingRight: '28px', height: '40px' }}
                          onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.1)'; }}
                          onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.boxShadow = 'none'; }}
                        />
                        {((Array.isArray(newCanal.horario_postagem) ? newCanal.horario_postagem : [newCanal.horario_postagem]).length > 1) && (
                          <button
                            type="button"
                            onClick={() => {
                              const currentHorarios = Array.isArray(newCanal.horario_postagem) ? newCanal.horario_postagem : [newCanal.horario_postagem || '19:00'];
                              const newHorarios = currentHorarios.filter((_, i) => i !== index);
                              setNewCanal({...newCanal, horario_postagem: newHorarios});
                            }}
                            style={{
                              position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)',
                              width: '22px', height: '22px', borderRadius: '4px', border: 'none',
                              background: 'transparent', color: '#F87171', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                          >
                            <X size={12} />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        const currentHorarios = Array.isArray(newCanal.horario_postagem) ? newCanal.horario_postagem : [newCanal.horario_postagem || '19:00'];
                        setNewCanal({...newCanal, horario_postagem: [...currentHorarios, '19:00']});
                      }}
                      style={{
                        height: '40px', borderRadius: '8px',
                        background: 'rgba(13, 174, 255, 0.08)', border: '1px solid rgba(13, 174, 255, 0.20)',
                        color: 'var(--dp-blue-500)', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.2s',
                      }}
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div>
                  <span style={labelStyle}>{t('channels.posting_frequency')}</span>
                  <input
                    id="edit_frequencia"
                    value={newCanal.frequencia_postagem}
                    onChange={(e) => setNewCanal({...newCanal, frequencia_postagem: e.target.value})}
                    placeholder="Ex: Diário, 3x/semana..."
                    style={inputStyle}
                    onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.1)'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.boxShadow = 'none'; }}
                  />
                </div>
                <div>
                  <span style={labelStyle}>{t('channels.videos_posted')}</span>
                  <input
                    id="edit_videos_ja_postados"
                    type="number"
                    min="0"
                    value={newCanal.videos_ja_postados}
                    onChange={(e) => setNewCanal({...newCanal, videos_ja_postados: parseInt(e.target.value) || 0})}
                    placeholder="0"
                    style={inputStyle}
                    onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.1)'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.boxShadow = 'none'; }}
                  />
                </div>
              </div>
              <div>
                <span style={labelStyle}>{t('channels.notes')}</span>
                <textarea
                  id="edit_anotacoes"
                  value={newCanal.anotacoes}
                  onChange={(e) => setNewCanal({...newCanal, anotacoes: e.target.value})}
                  rows="2"
                  style={{ ...inputStyle, resize: 'vertical', lineHeight: '1.5' }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.1)'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.boxShadow = 'none'; }}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', paddingTop: '8px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditCanalDialog(false);
                    setEditingCanal(null);
                    setNewCanal({
                      nome: '',
                      idioma: 'Português',
                      email: '',
                      horario_postagem: ['19:00'],
                      frequencia_postagem: '',
                      videos_ja_postados: 0,
                      anotacoes: '',
                      custom_buttons: []
                    });
                  }}
                  style={{
                    flex: 1, padding: '10px 16px', background: 'transparent',
                    border: '1px solid var(--dp-border-subtle)', borderRadius: '10px',
                    color: 'var(--dp-text-secondary)', fontSize: '13px', fontWeight: 500,
                    fontFamily: 'var(--dp-font)', cursor: 'pointer', transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dp-bg-elevated)'; e.currentTarget.style.borderColor = 'var(--dp-border-medium)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; }}
                >
                  {t('channels.cancel')}
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    padding: '10px 16px', background: 'linear-gradient(135deg, var(--dp-blue-500), #3B82F6)',
                    border: 'none', borderRadius: '10px', color: '#fff', fontSize: '13px', fontWeight: 600,
                    fontFamily: 'var(--dp-font)', cursor: 'pointer', transition: 'all 0.3s',
                  }}
                >
                  <Save size={14} />
                  {t('channels.update_channel')}
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        
        {/* Modal de Link */}
        {showLinkModal && (
          <div
            className="dp-modal-overlay"
            style={{
              position: 'fixed', inset: 0, zIndex: 9999,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(4px)',
              padding: '16px',
            }}
            // mouseDOWN no overlay: seleção de texto que termina fora não fecha
            onMouseDown={(e) => { if (e.target === e.currentTarget) setShowLinkModal(false); }}
          >
            <div
              style={{
                background: 'var(--dp-bg-glass, rgba(28,28,28,0.85))',
                backdropFilter: 'blur(24px)',
                WebkitBackdropFilter: 'blur(24px)',
                border: '1px solid var(--dp-border-medium)',
                borderRadius: '16px',
                boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
                padding: '28px',
                width: '100%',
                maxWidth: '440px',
                maxHeight: '90vh',
                overflowY: 'auto',
                animation: 'dp-card-stagger 0.3s ease-out',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                fontSize: '18px', fontWeight: 700,
                color: 'var(--dp-text-primary)',
                fontFamily: 'var(--dp-font)', marginBottom: '20px',
                display: 'flex', alignItems: 'center', gap: '10px',
              }}>
                <div style={{
                  width: '32px', height: '32px', borderRadius: '10px',
                  background: 'rgba(13, 174, 255, 0.12)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <ExternalLink size={16} style={{ color: 'var(--dp-blue-500)' }} />
                </div>
                {linkModalData.field === 'titulo' ? t('channels.link_title') : t('channels.link_thumbnail')}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <span style={labelStyle}>{t('channels.link_placeholder')}</span>
                <input
                  type="url"
                  value={linkInputValue}
                  onChange={(e) => setLinkInputValue(e.target.value)}
                  placeholder="https://exemplo.com"
                  style={inputStyle}
                  autoFocus
                  onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.1)'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.boxShadow = 'none'; }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveLinkFromModal();
                    } else if (e.key === 'Escape') {
                      setShowLinkModal(false);
                    }
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setShowLinkModal(false)}
                  style={{
                    flex: 1, padding: '10px 16px', background: 'transparent',
                    border: '1px solid var(--dp-border-subtle)', borderRadius: '10px',
                    color: 'var(--dp-text-secondary)', fontSize: '13px', fontWeight: 500,
                    fontFamily: 'var(--dp-font)', cursor: 'pointer', transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dp-bg-elevated)'; e.currentTarget.style.borderColor = 'var(--dp-border-medium)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; }}
                >
                  {t('channels.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleSaveLinkFromModal}
                  style={{
                    flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    padding: '10px 16px', background: 'linear-gradient(135deg, var(--dp-blue-500), #3B82F6)',
                    border: 'none', borderRadius: '10px', color: '#fff', fontSize: '13px', fontWeight: 600,
                    fontFamily: 'var(--dp-font)', cursor: 'pointer', transition: 'all 0.3s',
                  }}
                >
                  <Save size={14} />
                  {t('channels.save_link')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Dialog Vincular ao YouTube (vídeo publicado → card; 2026-06) */}
        {ytLinkModal && (() => {
          const ytVideo = videos.find(v => v.id === ytLinkModal.videoId);
          const jaVinculado = !!ytVideo?.youtube_video_id && !ytLinkModal.result;
          return (
            <div
              className="dp-modal-overlay"
              style={{
                position: 'fixed', inset: 0, zIndex: 9999,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(0, 0, 0, 0.6)',
                backdropFilter: 'blur(4px)',
                padding: '16px',
              }}
              // mouseDOWN no overlay: seleção de texto que termina fora não fecha
              onMouseDown={(e) => { if (e.target === e.currentTarget && !ytLinkModal.loading) setYtLinkModal(null); }}
            >
              <div
                style={{
                  background: 'var(--dp-bg-glass, rgba(28,28,28,0.85))',
                  backdropFilter: 'blur(24px)',
                  WebkitBackdropFilter: 'blur(24px)',
                  border: '1px solid var(--dp-border-medium)',
                  borderRadius: '16px',
                  boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
                  padding: '28px',
                  width: '100%',
                  maxWidth: '460px',
                  maxHeight: '90vh',
                  overflowY: 'auto',
                  animation: 'dp-card-stagger 0.3s ease-out',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{
                  fontSize: '18px', fontWeight: 700,
                  color: 'var(--dp-text-primary)',
                  fontFamily: 'var(--dp-font)', marginBottom: '6px',
                  display: 'flex', alignItems: 'center', gap: '10px',
                }}>
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '10px',
                    background: 'rgba(255, 0, 0, 0.12)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Youtube size={16} style={{ color: '#FF0000' }} />
                  </div>
                  {t('channels.yt_link_title') || 'Vincular ao YouTube'}
                </div>

                {/* Estado 1: já vinculado (sem result novo) → mostrar vínculo + Desvincular */}
                {jaVinculado ? (
                  <>
                    <p style={{ fontSize: '13px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)', margin: '0 0 14px 0' }}>
                      {t('channels.yt_link_current') || 'Este card já está vinculado a um vídeo publicado.'}
                    </p>
                    <a
                      href={ytVideo.youtube_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '10px 14px', borderRadius: '10px', marginBottom: '18px',
                        background: 'rgba(255, 0, 0, 0.08)', border: '1px solid rgba(255, 0, 0, 0.20)',
                        color: '#EF4444', fontSize: '13px', fontWeight: 600,
                        fontFamily: 'var(--dp-font)', textDecoration: 'none',
                        overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                      }}
                    >
                      <ExternalLink size={14} style={{ flexShrink: 0 }} />
                      {ytVideo.youtube_url}
                    </a>
                    {ytLinkModal.error && (
                      <p style={{ fontSize: '12px', color: '#F87171', fontFamily: 'var(--dp-font)', margin: '0 0 12px 0' }}>{ytLinkModal.error}</p>
                    )}
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        type="button"
                        onClick={handleYtUnlink}
                        disabled={ytLinkModal.loading}
                        style={{
                          flex: 1, padding: '10px 16px', background: 'transparent',
                          border: '1px solid rgba(239, 68, 68, 0.35)', borderRadius: '10px',
                          color: '#EF4444', fontSize: '13px', fontWeight: 600,
                          fontFamily: 'var(--dp-font)', cursor: ytLinkModal.loading ? 'default' : 'pointer',
                          opacity: ytLinkModal.loading ? 0.6 : 1, transition: 'all 0.2s',
                        }}
                      >
                        {t('channels.yt_link_unlink') || 'Desvincular'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setYtLinkModal(null)}
                        style={{
                          flex: 1, padding: '10px 16px', background: 'transparent',
                          border: '1px solid var(--dp-border-subtle)', borderRadius: '10px',
                          color: 'var(--dp-text-secondary)', fontSize: '13px', fontWeight: 500,
                          fontFamily: 'var(--dp-font)', cursor: 'pointer', transition: 'all 0.2s',
                        }}
                      >
                        {t('channels.close') || 'Fechar'}
                      </button>
                    </div>
                  </>
                ) : ytLinkModal.result ? (
                  /* Estado 2: vínculo feito → preview do título + opt-in (default OFF) */
                  <>
                    <p style={{ fontSize: '13px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)', margin: '0 0 14px 0' }}>
                      {t('channels.yt_link_done_desc') || 'Vídeo vinculado e marcado como Publicado.'}
                    </p>
                    {ytLinkModal.result.aviso_canal_divergente && (
                      <div style={{
                        padding: '10px 12px', borderRadius: '10px', marginBottom: '12px',
                        background: 'rgba(251, 191, 36, 0.10)', border: '1px solid rgba(251, 191, 36, 0.25)',
                        color: '#FBBF24', fontSize: '12px', fontFamily: 'var(--dp-font)',
                        display: 'flex', alignItems: 'flex-start', gap: '8px',
                      }}>
                        <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: '1px' }} />
                        {t('channels.yt_link_other_channel') || 'Este vídeo é de outro canal do YouTube. O vínculo foi mantido mesmo assim.'}
                      </div>
                    )}
                    <div style={{ marginBottom: '14px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {t('channels.yt_link_preview_label') || 'Título no YouTube'}
                      </span>
                      <div style={{
                        marginTop: '6px', padding: '10px 12px', borderRadius: '10px',
                        background: 'rgba(0,0,0,0.25)', border: '1px solid var(--dp-border-subtle)',
                        color: 'var(--dp-text-primary)', fontSize: '13px', fontFamily: 'var(--dp-font)',
                      }}>
                        {ytLinkModal.result.titulo_youtube || '(sem título)'}
                      </div>
                    </div>
                    <label style={{
                      display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '18px',
                      fontSize: '13px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)',
                      cursor: 'pointer', userSelect: 'none',
                    }}>
                      <input
                        type="checkbox"
                        checked={ytLinkModal.updateTitle}
                        onChange={(e) => setYtLinkModal(prev => ({ ...prev, updateTitle: e.target.checked }))}
                        style={{ accentColor: '#0DAEFF', width: '15px', height: '15px', cursor: 'pointer' }}
                      />
                      {t('channels.yt_link_update_title') || 'Atualizar o título do card com o título do YouTube'}
                    </label>
                    <button
                      type="button"
                      onClick={handleYtLinkFinish}
                      style={{
                        width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                        padding: '10px 16px', background: 'linear-gradient(135deg, var(--dp-blue-500), #3B82F6)',
                        border: 'none', borderRadius: '10px', color: '#fff', fontSize: '13px', fontWeight: 600,
                        fontFamily: 'var(--dp-font)', cursor: 'pointer', transition: 'all 0.3s',
                      }}
                    >
                      {t('channels.yt_link_done') || 'Concluir'}
                    </button>
                  </>
                ) : (
                  /* Estado 3: colar URL e vincular */
                  <>
                    <p style={{ fontSize: '13px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)', margin: '0 0 14px 0' }}>
                      {t('channels.yt_link_desc') || 'Cole a URL do vídeo já publicado. O card será marcado como Publicado com a data e a thumbnail reais.'}
                    </p>
                    <input
                      type="url"
                      value={ytLinkModal.url}
                      onChange={(e) => setYtLinkModal(prev => ({ ...prev, url: e.target.value, error: '' }))}
                      placeholder="https://youtube.com/watch?v=..."
                      style={inputStyle}
                      autoFocus
                      onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.1)'; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.boxShadow = 'none'; }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleYtLink();
                        else if (e.key === 'Escape' && !ytLinkModal.loading) setYtLinkModal(null);
                      }}
                    />
                    {ytLinkModal.error && (
                      <p style={{ fontSize: '12px', color: '#F87171', fontFamily: 'var(--dp-font)', margin: '8px 0 0 0' }}>{ytLinkModal.error}</p>
                    )}
                    <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
                      <button
                        type="button"
                        onClick={() => !ytLinkModal.loading && setYtLinkModal(null)}
                        style={{
                          flex: 1, padding: '10px 16px', background: 'transparent',
                          border: '1px solid var(--dp-border-subtle)', borderRadius: '10px',
                          color: 'var(--dp-text-secondary)', fontSize: '13px', fontWeight: 500,
                          fontFamily: 'var(--dp-font)', cursor: 'pointer', transition: 'all 0.2s',
                        }}
                      >
                        {t('channels.cancel')}
                      </button>
                      <button
                        type="button"
                        onClick={handleYtLink}
                        disabled={ytLinkModal.loading || !(ytLinkModal.url || '').trim()}
                        style={{
                          flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                          padding: '10px 16px', background: 'linear-gradient(135deg, #FF0000, #CC0000)',
                          border: 'none', borderRadius: '10px', color: '#fff', fontSize: '13px', fontWeight: 600,
                          fontFamily: 'var(--dp-font)',
                          cursor: (ytLinkModal.loading || !(ytLinkModal.url || '').trim()) ? 'default' : 'pointer',
                          opacity: (ytLinkModal.loading || !(ytLinkModal.url || '').trim()) ? 0.6 : 1,
                          transition: 'all 0.3s',
                        }}
                      >
                        <Youtube size={14} />
                        {ytLinkModal.loading ? (t('channels.yt_link_loading') || 'Vinculando...') : (t('channels.yt_link_button') || 'Vincular')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })()}
      </main>

      <ScriptDrawer
        isOpen={scriptDrawerOpen}
        onClose={() => { setScriptDrawerOpen(false); setScriptDrawerVideo(null); }}
        videoId={scriptDrawerVideo?.id}
        videoTitle={scriptDrawerVideo?.titulo}
        videoStatus={scriptDrawerVideo?.status}
        canalId={scriptDrawerVideo?.canalId}
      />

      {/* Channel Prompts Modal */}
      <ChannelPromptsModal
        isOpen={showPromptsModal}
        onClose={() => setShowPromptsModal(false)}
        canalId={selectedCanal}
        canalName={selectedCanalData?.nome}
        onPromptsChanged={fetchChannelPromptCount}
      />

      {/* Channel References Modal */}
      <ChannelReferencesModal
        isOpen={showReferencesModal}
        onClose={() => setShowReferencesModal(false)}
        canalId={selectedCanal}
        canalName={selectedCanalData?.nome}
        onReferencesChanged={() => fetchChannelReferences(selectedCanal)}
      />

      <ChannelNarratorModal
        isOpen={showNarratorModal}
        onClose={() => setShowNarratorModal(false)}
        canalId={selectedCanal}
        canalName={selectedCanalData?.nome}
        onNarratorChanged={() => fetchNarratorStatus(selectedCanal)}
      />

      {/* Extras/Anotações Modal */}
      {showExtrasModal && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '16px',
            animation: 'dp-modal-overlay 0.2s ease',
          }}
          onClick={() => setShowExtrasModal(false)}
        >
          <div
            style={{
              background: 'var(--dp-bg-glass)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
              border: '1px solid var(--dp-border-medium)', borderRadius: '16px',
              boxShadow: '0 24px 64px rgba(0,0,0,0.5)', padding: '28px',
              maxWidth: '560px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column',
              overflowY: 'auto',
              animation: 'dp-modal-scale 0.25s cubic-bezier(0.4,0,0.2,1)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px', height: '40px', borderRadius: '12px',
                  background: 'rgba(251, 191, 36, 0.10)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Info size={18} style={{ color: 'var(--dp-yellow-500, #FBBF24)' }} />
                </div>
                <div>
                  <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)', margin: 0 }}>
                    {t('channels.channel_extras')}
                  </h3>
                  <p style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)', margin: '2px 0 0 0' }}>
                    {selectedCanalData?.nome}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowExtrasModal(false)}
                style={{
                  width: '32px', height: '32px', borderRadius: '8px',
                  border: '1px solid var(--dp-border-subtle)', background: 'transparent',
                  color: 'var(--dp-text-secondary)', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#F87171'; e.currentTarget.style.color = '#F87171'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
              >
                <X size={16} />
              </button>
            </div>

            {/* Textarea */}
            <textarea
              value={extrasText}
              onChange={(e) => setExtrasText(e.target.value)}
              placeholder={t('channels.notes_placeholder') || 'Escreva suas anotações sobre o canal aqui...'}
              style={{
                width: '100%', minHeight: '200px', maxHeight: '50vh', resize: 'vertical',
                padding: '14px 16px', background: 'var(--dp-bg-input)', border: '1px solid var(--dp-border-subtle)',
                borderRadius: '10px', color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)',
                fontSize: '14px', lineHeight: 1.7, outline: 'none', boxSizing: 'border-box',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => { e.target.style.borderColor = 'var(--dp-blue-500)'; }}
              onBlur={(e) => { e.target.style.borderColor = 'var(--dp-border-subtle)'; }}
            />

            {/* Footer */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={() => setShowExtrasModal(false)}
                style={{
                  padding: '9px 20px', borderRadius: '8px', background: 'transparent',
                  border: '1px solid var(--dp-border-subtle)', color: 'var(--dp-text-secondary)',
                  fontSize: '13px', fontWeight: 500, fontFamily: 'var(--dp-font)', cursor: 'pointer',
                }}
              >
                {t('ideasBoard.cancel') || 'Cancelar'}
              </button>
              <button
                onClick={handleSaveExtras}
                disabled={savingExtras}
                style={{
                  padding: '9px 20px', borderRadius: '8px',
                  background: 'linear-gradient(135deg, var(--dp-blue-500), var(--dp-blue-600, #0B8FDB))',
                  border: 'none', color: 'white', fontSize: '13px', fontWeight: 600,
                  fontFamily: 'var(--dp-font)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
                  opacity: savingExtras ? 0.7 : 1,
                }}
              >
                <Save size={14} />
                {savingExtras ? (t('references.saving') || 'Salvando...') : (t('references.save_changes') || 'Salvar')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Thumbnail Delete Confirmation Modal */}
      {thumbnailDeleteConfirm && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10000,
            padding: '16px',
          }}
          onClick={() => setThumbnailDeleteConfirm(null)}
        >
          <div
            style={{
              background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)',
              borderRadius: '12px', padding: '24px', maxWidth: '360px', width: '100%',
              maxHeight: '90vh', overflowY: 'auto',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--dp-text-primary)', marginBottom: '8px' }}>
              Remover thumbnail?
            </div>
            <div style={{ fontSize: '13px', color: 'var(--dp-text-secondary)', marginBottom: '20px', lineHeight: 1.5 }}>
              Essa ação não pode ser desfeita. A imagem será removida do vídeo.
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setThumbnailDeleteConfirm(null)}
                style={{
                  padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 500,
                  background: 'transparent', border: '1px solid var(--dp-border-subtle)',
                  color: 'var(--dp-text-secondary)', cursor: 'pointer', fontFamily: 'var(--dp-font)',
                }}
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  handleThumbnailDelete(thumbnailDeleteConfirm);
                  setThumbnailDeleteConfirm(null);
                }}
                style={{
                  padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                  background: '#EF4444', border: 'none',
                  color: 'white', cursor: 'pointer', fontFamily: 'var(--dp-font)',
                }}
              >
                Remover
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Thumbnail Preview Lightbox */}
      {thumbnailPreview && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10000, cursor: 'pointer',
            padding: '16px',
          }}
          onClick={() => setThumbnailPreview(null)}
          onKeyDown={(e) => { if (e.key === 'Escape') setThumbnailPreview(null); }}
          tabIndex={-1}
          ref={(el) => el && el.focus()}
        >
          <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setThumbnailPreview(null)}
              style={{
                position: 'absolute', top: '-40px', right: 0,
                background: 'transparent', border: 'none', color: 'white',
                fontSize: '32px', cursor: 'pointer', lineHeight: 1,
                fontFamily: 'var(--dp-font)',
              }}
            >
              ✕
            </button>
            <img
              src={thumbnailPreview}
              alt="Preview"
              style={{
                maxWidth: '90vw', maxHeight: '85vh',
                borderRadius: '8px', objectFit: 'contain',
                boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
              }}
            />
          </div>
        </div>
      )}

      {/* ===== DELETE IMPORTED VIDEO MODAL ===== */}
      {showDeleteImportedModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '16px',
        }}>
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(4px)',
          }} onClick={() => setShowDeleteImportedModal(null)} />
          <div style={{
            position: 'relative', zIndex: 1,
            background: 'var(--dp-bg-card)', borderRadius: '16px',
            border: '1px solid var(--dp-border-subtle)',
            padding: '24px', width: '100%', maxWidth: '420px',
            maxHeight: '90vh', overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <div style={{
                width: '36px', height: '36px', borderRadius: '10px',
                background: 'rgba(251,146,60,0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <AlertCircle size={18} style={{ color: '#FB923C' }} />
              </div>
              <h3 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--dp-text-primary)', margin: 0, fontFamily: 'var(--dp-font)' }}>
                Excluir vídeo importado?
              </h3>
            </div>
            <div style={{ fontSize: '13px', color: 'var(--dp-text-secondary)', lineHeight: 1.6, marginBottom: '20px' }}>
              <p style={{ margin: '0 0 8px 0' }}>Este vídeo foi importado do YouTube.</p>
              <p style={{ margin: '0 0 8px 0' }}>Ao excluir, ele será removido apenas do DarkPlanner. O vídeo continuará publicado no YouTube normalmente.</p>
              <p style={{ margin: 0, fontSize: '12px', color: 'var(--dp-text-tertiary)' }}>Se você sincronizar o canal novamente no futuro, este vídeo será importado novamente.</p>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setShowDeleteImportedModal(null)}
                style={{
                  flex: 1, padding: '10px 16px', background: 'transparent',
                  border: '1px solid var(--dp-border-subtle)', borderRadius: '10px',
                  color: 'var(--dp-text-secondary)', fontSize: '13px', fontWeight: 500,
                  fontFamily: 'var(--dp-font)', cursor: 'pointer',
                }}>
                Cancelar
              </button>
              <button onClick={async () => {
                const videoId = showDeleteImportedModal;
                setShowDeleteImportedModal(null);
                await deleteVideo(videoId);
              }}
                style={{
                  flex: 1, padding: '10px 16px',
                  background: 'linear-gradient(135deg, #EF4444, #DC2626)',
                  border: 'none', borderRadius: '10px',
                  color: '#fff', fontSize: '13px', fontWeight: 600,
                  fontFamily: 'var(--dp-font)', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                }}>
                <Trash2 size={14} />
                Excluir do Sistema
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== LINK YOUTUBE DIALOG ===== */}
      {showLinkYoutubeDialog && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '16px',
        }}>
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(4px)',
          }} onClick={() => { setShowLinkYoutubeDialog(false); resetYoutubeLinkStates(); }} />
          <div style={{
            position: 'relative', zIndex: 1,
            background: 'var(--dp-bg-card)', borderRadius: '16px',
            border: '1px solid var(--dp-border-subtle)',
            padding: '24px', width: '100%', maxWidth: '480px',
            maxHeight: '90vh', overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            {youtubeImportingLink ? (
              /* Importing state */
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <RefreshCw size={32} style={{ color: 'var(--dp-blue-500)', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
                <h3 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--dp-text-primary)', margin: '0 0 8px 0', fontFamily: 'var(--dp-font)' }}>
                  Vinculando e importando...
                </h3>
                <p style={{ fontSize: '13px', color: 'var(--dp-text-tertiary)', margin: 0 }}>
                  Importando vídeos do YouTube. Seus vídeos manuais serão renumerados.
                </p>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                  <div style={{
                    width: '36px', height: '36px', borderRadius: '10px',
                    background: 'rgba(255,0,0,0.08)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Youtube size={18} style={{ color: '#FF0000' }} />
                  </div>
                  <div>
                    <h3 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--dp-text-primary)', margin: 0, fontFamily: 'var(--dp-font)' }}>
                      Vincular YouTube
                    </h3>
                    <p style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)', margin: '2px 0 0 0' }}>
                      Vincule um canal do YouTube para importar todos os vídeos.
                    </p>
                  </div>
                </div>

                {youtubeChannelLink ? (
                  /* Channel found */
                  <div>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '12px', padding: '12px',
                      borderRadius: '10px', background: 'var(--dp-bg-elevated)',
                      border: '1px solid var(--dp-border-subtle)', marginBottom: '12px',
                    }}>
                      <img src={youtubeChannelLink.thumbnail} alt={youtubeChannelLink.name}
                        style={{ width: '44px', height: '44px', borderRadius: '10px', objectFit: 'cover' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--dp-text-primary)' }}>{youtubeChannelLink.name}</div>
                        <div style={{ fontSize: '11px', color: 'var(--dp-text-tertiary)', display: 'flex', gap: '10px', marginTop: '2px' }}>
                          <span>📺 {youtubeChannelLink.video_count} vídeos</span>
                          <span>👥 {youtubeChannelLink.subscribers >= 1000 ? `${(youtubeChannelLink.subscribers / 1000).toFixed(1)}K` : youtubeChannelLink.subscribers}</span>
                        </div>
                      </div>
                    </div>
                    <p style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)', margin: '0 0 12px 0' }}>
                      ⚠️ Os vídeos importados receberão números 1 a {youtubeChannelLink.video_count}. Seus vídeos manuais existentes serão renumerados.
                    </p>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button onClick={() => { setShowLinkYoutubeDialog(false); resetYoutubeLinkStates(); }}
                        style={{
                          flex: 1, padding: '10px 16px', background: 'transparent',
                          border: '1px solid var(--dp-border-subtle)', borderRadius: '10px',
                          color: 'var(--dp-text-secondary)', fontSize: '13px', fontWeight: 500, cursor: 'pointer',
                        }}>
                        Cancelar
                      </button>
                      <button onClick={async () => {
                        await importYoutubeChannel(youtubeChannelLink, selectedCanal);
                        setShowLinkYoutubeDialog(false);
                        resetYoutubeLinkStates();
                        fetchVideos();
                      }}
                        style={{
                          flex: 1, padding: '10px 16px',
                          background: 'linear-gradient(135deg, #FF0000, #CC0000)',
                          border: 'none', borderRadius: '10px',
                          color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                        }}>
                        <Youtube size={14} /> Vincular e Importar
                      </button>
                    </div>
                  </div>
                ) : (
                  /* URL Input */
                  <div>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                      <input
                        value={youtubeUrlLink}
                        onChange={(e) => setYoutubeUrlLink(e.target.value)}
                        placeholder="https://youtube.com/@seucanal"
                        style={{ ...inputStyle, flex: 1 }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (youtubeUrlLink.trim()) searchYoutubeChannel(youtubeUrlLink, true); } }}
                      />
                      <button type="button"
                        onClick={() => youtubeUrlLink.trim() && searchYoutubeChannel(youtubeUrlLink, true)}
                        disabled={youtubeSearchingLink || !youtubeUrlLink.trim()}
                        style={{
                          padding: '0 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
                          background: youtubeUrlLink.trim() ? 'linear-gradient(135deg, #FF0000, #CC0000)' : 'var(--dp-bg-elevated)',
                          color: youtubeUrlLink.trim() ? '#fff' : 'var(--dp-text-tertiary)',
                          border: 'none', cursor: youtubeUrlLink.trim() ? 'pointer' : 'not-allowed',
                          display: 'flex', alignItems: 'center', gap: '5px',
                        }}>
                        {youtubeSearchingLink ? <RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Search size={13} />}
                        Buscar
                      </button>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button onClick={() => { setShowLinkYoutubeDialog(false); resetYoutubeLinkStates(); }}
                        style={{
                          flex: 1, padding: '10px 16px', background: 'transparent',
                          border: '1px solid var(--dp-border-subtle)', borderRadius: '10px',
                          color: 'var(--dp-text-secondary)', fontSize: '13px', fontWeight: 500, cursor: 'pointer',
                        }}>
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
      {/* Share Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent style={{ background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-medium)', maxWidth: '520px' }}>
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--dp-text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Users size={18} />
              {t('sharing.title')}
            </DialogTitle>
            <DialogDescription style={{ color: 'var(--dp-text-secondary)' }}>
              {t('sharing.description')}
            </DialogDescription>
          </DialogHeader>

          {/* Invite Section */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Input
                placeholder={t('sharing.email_placeholder')}
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleShareInvite()}
                style={{ flex: 1, background: 'var(--dp-bg-secondary)', color: 'var(--dp-text-primary)', border: '1px solid var(--dp-border-subtle)' }}
              />
              <Button onClick={handleShareInvite} disabled={shareInviting || !shareEmail.trim()} style={{ whiteSpace: 'nowrap' }}>
                {shareInviting ? t('sharing.inviting') : t('sharing.invite_button')}
              </Button>
            </div>

            {/* Permissions checkboxes for invite */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', padding: '8px 12px', background: 'var(--dp-bg-secondary)', borderRadius: '8px', border: '1px solid var(--dp-border-subtle)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--dp-text-secondary)', cursor: 'pointer' }}>
                <input type="checkbox" checked={sharePermissions.pode_editar_config} onChange={(e) => setSharePermissions(p => ({ ...p, pode_editar_config: e.target.checked }))} />
                {t('sharing.perm_edit_config')}
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--dp-text-secondary)', cursor: 'pointer' }}>
                <input type="checkbox" checked={sharePermissions.pode_deletar_videos} onChange={(e) => setSharePermissions(p => ({ ...p, pode_deletar_videos: e.target.checked }))} />
                {t('sharing.perm_delete_videos')}
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--dp-text-secondary)', cursor: 'pointer' }}>
                <input type="checkbox" checked={sharePermissions.pode_editar_prompts} onChange={(e) => setSharePermissions(p => ({ ...p, pode_editar_prompts: e.target.checked }))} />
                {t('sharing.perm_edit_prompts')}
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--dp-text-secondary)', cursor: 'pointer' }}>
                <input type="checkbox" checked={sharePermissions.pode_editar_referencias} onChange={(e) => setSharePermissions(p => ({ ...p, pode_editar_referencias: e.target.checked }))} />
                {t('sharing.perm_edit_references')}
              </label>
            </div>
          </div>

          {/* Members List */}
          <div style={{ marginTop: '16px', borderTop: '1px solid var(--dp-border-subtle)', paddingTop: '12px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--dp-text-primary)', marginBottom: '8px', display: 'block' }}>
              {t('sharing.members_count', { count: shareMembros.length })}
            </span>
            {shareLoading ? (
              <div style={{ textAlign: 'center', padding: '16px', color: 'var(--dp-text-tertiary)', fontSize: '13px' }}>{t('sharing.loading')}</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '300px', overflowY: 'auto' }}>
                {shareMembros.map((membro) => (
                  <div key={membro.user_id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 12px', background: 'var(--dp-bg-secondary)', borderRadius: '8px',
                    border: '1px solid var(--dp-border-subtle)'
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--dp-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {membro.email}
                      </div>
                      <div style={{ fontSize: '11px', color: membro.role === 'dono' ? 'var(--dp-blue-500)' : 'var(--dp-text-tertiary)', fontWeight: 600, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {membro.role === 'dono' ? t('sharing.role_owner') : t('sharing.role_editor')}
                        {membro.role === 'editor' && membro.status && membro.status !== 'aceito' && (
                          <span style={{
                            fontSize: '10px', padding: '1px 6px', borderRadius: '4px', fontWeight: 600,
                            background: membro.status === 'pendente' ? 'rgba(245,158,11,0.12)' : 'rgba(239,68,68,0.12)',
                            color: membro.status === 'pendente' ? '#F59E0B' : '#EF4444',
                            border: `1px solid ${membro.status === 'pendente' ? 'rgba(245,158,11,0.2)' : 'rgba(239,68,68,0.2)'}`,
                            textTransform: 'none',
                          }}>
                            {membro.status === 'pendente' ? t('sharing.status_pending') : t('sharing.status_refused')}
                          </span>
                        )}
                      </div>
                    </div>
                    {membro.role === 'editor' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {/* Quick permission toggles */}
                        <div style={{ display: 'flex', gap: '4px' }}>
                          {[
                            { key: 'pode_editar_config', label: '⚙️', title: t('sharing.perm_edit_config') },
                            { key: 'pode_deletar_videos', label: '🗑️', title: t('sharing.perm_delete_videos') },
                            { key: 'pode_editar_prompts', label: '📝', title: t('sharing.perm_edit_prompts') },
                            { key: 'pode_editar_referencias', label: '📌', title: t('sharing.perm_edit_references') }
                          ].map(perm => (
                            <button
                              key={perm.key}
                              title={perm.title}
                              onClick={() => {
                                const newVal = !membro.permissions[perm.key];
                                // Update local state
                                setShareMembros(prev => prev.map(m =>
                                  m.user_id === membro.user_id
                                    ? { ...m, permissions: { ...m.permissions, [perm.key]: newVal } }
                                    : m
                                ));
                                // Update backend
                                handleUpdateMembro(membro.user_id, { [perm.key]: newVal });
                              }}
                              style={{
                                width: '26px', height: '26px', borderRadius: '4px', border: 'none',
                                background: membro.permissions[perm.key] ? 'var(--dp-blue-500)' : 'var(--dp-bg-primary)',
                                opacity: membro.permissions[perm.key] ? 1 : 0.4,
                                cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              {perm.label}
                            </button>
                          ))}
                        </div>
                        <button
                          onClick={() => handleRemoveMembro(membro.user_id, membro.email)}
                          style={{
                            background: 'transparent', border: 'none', cursor: 'pointer',
                            color: 'var(--dp-red-500, #F87171)', padding: '4px'
                          }}
                          title={t('sharing.remove_member_tooltip')}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={!!confirmState}
        icon={confirmState?.icon}
        title={confirmState?.title || ''}
        message={confirmState?.message || ''}
        confirmLabel={confirmState?.confirmLabel}
        cancelLabel={confirmState?.cancelLabel}
        danger={confirmState?.danger}
        onConfirm={confirmState?.onConfirm}
        onCancel={confirmState?.onCancel || (() => setConfirmState(null))}
      />
    </div>
  );
};

export default CanalManagerNew;
